import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import API from "./api";
import BirthLocationMapPreview from "./BirthLocationMapPreview";
import { formatCurrencyAmount, getCurrencyName, normalizeOccupationCurrencyCode } from "./currencyOptions";
import EditProfile from "./EditProfile";
import { getContactDisplayValue } from "./indiaPhone";
import { formatTime12Hour } from "./timeValue";
import WatermarkedPdfRedactionScreen from "./WatermarkedPdfRedactionScreen";
import {
  canDownloadRecommendationBiodata,
  getProfileBiodataSectionCatalog,
  generateProfileBiodataPdf,
} from "./profileBiodataPdf";
import { getProfileMediaLinkTypeLabel } from "./profileMediaLinks";
import {
  formatProfileNumberValue,
  getProfileNumberDisplay,
  isProfileNoDisplaySearchMode,
  PROFILE_NO_DISPLAY_SEARCH_KEY,
  RAW_PROFILE_NUMBER_FIELD,
} from "./profileNumber";
import { hasPermission } from "./rbac";
import { MAX_UPLOAD_FILE_SIZE_LABEL, prepareFilesForUpload } from "./uploadFileCompression";

const WATERMARK_PREVIEW_BATCH_SIZE = 10;
const NOTES_PAGE_SIZE = 12;

const REMARK_SUGGESTIONS = [
  "Interested in profile",
  "Not interested in profile",
  "Called, no response",
  "Called, will call back",
  "Meeting scheduled",
  "Meeting done, positive response",
  "Meeting done, negative response",
  "Profile on hold",
  "Awaiting family approval",
  "Biodata sent",
  "Biodata received",
  "Alliance accepted",
  "Alliance rejected",
  "Follow up required",
  "Profile shortlisted",
  "Profile rejected",
];

const FALLBACK_NOTE_META = {
  note_types: ["general", "call", "meeting", "match_share", "feedback", "follow_up", "internal"],
  outcomes: ["open", "follow_up_required", "callback", "interested", "not_interested", "shared", "resolved", "no_response"],
  follow_up_statuses: ["none", "pending", "scheduled", "completed", "cancelled"],
  defaults: {
    note_type: "general",
  },
};

const FALLBACK_RECOMMENDATION_META = {
  recommendation_statuses: ["recommended", "shared", "viewed", "call", "interested", "meeting_planned", "meeting_done", "matched", "rejected", "cancelled"],
  match_statuses: ["recommended", "shared", "viewed", "call", "interested", "meeting_planned", "meeting_done", "matched", "rejected", "cancelled"],
  outcome_sources: ["manual"],
  contact_sides: ["both"],
  defaults: {
    recommendation_status: "recommended",
    outcome_source: "manual",
    contact_side: "both",
  },
};

const NOTE_TYPE_COPY = {
  general: {
    label: "General",
    icon: "Note",
    helper: "Use for internal context that does not fit a specific CRM interaction.",
    templates: [
      "Family prefers same city match with strong education background.",
      "Important biodata details were rechecked with the family today.",
    ],
  },
  call: {
    label: "Call",
    icon: "Call",
    helper: "Capture phone conversations, callback promises, and spoken objections.",
    templates: [
      "Called family, discussed profile details and next steps.",
      "Phone call completed. Family requested time to review biodata.",
      "Called but no answer. Need another call attempt.",
    ],
  },
  meeting: {
    label: "Meeting",
    icon: "Meet",
    helper: "Log meetings, family visits, and discussion outcomes.",
    templates: [
      "Meeting completed. Families had a positive first discussion.",
      "Meeting scheduled after sharing horoscope and biodata.",
      "Meeting done. Family requested one more conversation before deciding.",
    ],
  },
  match_share: {
    label: "Match Share",
    icon: "Share",
    helper: "Track biodata sharing, match recommendations, and family responses.",
    templates: [
      "Shared biodata with the family and explained key highlights.",
      "Shared shortlisted matches for review and awaiting response.",
      "Biodata sent through WhatsApp and family acknowledged receipt.",
    ],
  },
  feedback: {
    label: "Feedback",
    icon: "Feedback",
    helper: "Capture direct feedback, objections, preferences, and conversion signals.",
    templates: [
      "Family liked education and values but wants same-state preference.",
      "Candidate feedback received. Interested, but timing is not final yet.",
      "Family declined due to location preference mismatch.",
    ],
  },
  follow_up: {
    label: "Follow Up",
    icon: "Follow-up",
    helper: "Use when the main purpose is scheduling or tracking the next action.",
    templates: [
      "Follow up with family after they review the biodata internally.",
      "Recheck interest after the weekend discussion with elders.",
      "Pending callback. Reminder set for next touchpoint.",
    ],
  },
  internal: {
    label: "Internal",
    icon: "Internal",
    helper: "Private operational notes for the admin team.",
    templates: [
      "Sensitive family concern shared internally. Handle with discretion.",
      "Keep this profile in priority queue for senior follow-up.",
    ],
  },
};

const PROFILE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "contacts", label: "Contacts" },
  { id: "career", label: "Education & Career" },
  { id: "family", label: "Family" },
  { id: "relatives", label: "Relatives" },
  { id: "addresses", label: "Addresses" },
  { id: "preferences", label: "Preferences" },
  { id: "media", label: "Media & Documents" },
  { id: "recommendations", label: "Recommendations" },
  { id: "crm", label: "CRM & Package" },
];

const FAMILY_RELATION_TYPES = new Set(["father", "mother", "brother", "sister"]);

function sanitizeWatermarkedFileNamePart(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_]+/g, "")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function buildWatermarkedBiodataFileName(profile) {
  const fullName = sanitizeWatermarkedFileNamePart(getProfileDisplayName(profile)) || "Profile";
  return `JKM_${fullName}_watermarked_Bio_Data.pdf`;
}

function buildProfileBiodataFileName(profile) {
  const fullName = sanitizeWatermarkedFileNamePart(getProfileDisplayName(profile)) || "Profile";
  return `JKM_PROFILE_${fullName}_BIO_DATA.pdf`;
}

function isPdfMedia(document) {
  const mimeType = String(document?.mime_type || "").trim().toLowerCase();
  const possiblePdfSources = [
    document?.file_url,
    document?.storage_key,
    document?.original_file_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
  const isPdf =
    mimeType === "application/pdf" ||
    possiblePdfSources.some((value) => value.endsWith(".pdf"));

  return isPdf;
}

function isImageMedia(document) {
  const mimeType = String(document?.mime_type || "").trim().toLowerCase();
  if (mimeType.startsWith("image/")) return true;

  const possibleImageSources = [
    document?.file_url,
    document?.storage_key,
    document?.original_file_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  return possibleImageSources.some((value) => value.match(/\.(jpg|jpeg|png|webp|gif|bmp|svg|tif|tiff|avif)$/i));
}

function isBiodataPdfMedia(document) {
  if (!isPdfMedia(document)) return false;

  const hints = [
    document?.document_type_name,
    document?.template_key,
    document?.original_file_name,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  return hints.some((value) => value.includes("biodata") || value.includes("bio data"));
}

function getDocumentDisplayName(document) {
  return (
    String(document?.original_file_name || "").trim() ||
    String(document?.document_type_name || "").trim() ||
    String(document?.template_key || "").trim() ||
    (document?.id ? `Document #${document.id}` : "Document")
  );
}

function buildRecommendationTargetLabel(recommendation) {
  const name = String(recommendation?.to_profile_name || "").trim();
  const profileNo = formatProfileNumberValue(recommendation?.to_profile_no, recommendation?.to_profile_gender);
  if (name && profileNo) return `${name} (#${profileNo})`;
  if (name) return name;
  if (profileNo) return `#${profileNo}`;
  return "Recommended profile";
}

function buildRecommendationNoteContextMap(recommendations = []) {
  const map = new Map();

  safeArray(recommendations).forEach((item) => {
    const recommendationId = item?.id ? String(item.id) : "";
    const targetLabel = buildRecommendationTargetLabel(item);
    const recommendationStatusLabel = formatRecommendationStatusLabel(item?.status || "recommended");

    if (item?.note_id) {
      map.set(String(item.note_id), {
        recommendationId,
        badge: "Recommendation",
        label: `${recommendationStatusLabel} profile ${targetLabel}`,
        meta: recommendationStatusLabel,
      });
    }

    safeArray(item?.outcomes).forEach((outcome) => {
      if (!outcome?.note_id) return;
      map.set(String(outcome.note_id), {
        recommendationId,
        badge: "Recommendation Update",
        label: `${formatRecommendationStatusLabel(outcome.match_status || item?.status || "recommended")} for ${targetLabel}`,
        meta: outcome?.matched_at ? formatDateTime(outcome.matched_at) : "",
      });
    });
  });

  return map;
}

function formatDocumentHistoryAction(action) {
  const normalized = String(action || "").trim().toUpperCase();
  if (normalized === "DOWNLOAD_PROFILE_DOCUMENT") return "Downloaded file";
  if (normalized === "OPEN_PROFILE_DOCUMENT") return "Opened file";
  if (normalized === "DOWNLOAD_WATERMARKED_BIODATA_PDF") return "Downloaded watermarked PDF";
  if (normalized === "VIEW_WATERMARKED_PDF_PREVIEW") return "Previewed watermarked PDF";
  return normalized ? normalized.replace(/_/g, " ").toLowerCase() : "Activity";
}

function getDocumentHistoryEntryName(entry, fallbackDocument = null) {
  const parsePayload = (value) => {
    if (value && typeof value === "object") return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch (_) {
        return {};
      }
    }
    return {};
  };
  const payload = Object.keys(parsePayload(entry?.new_data)).length
    ? parsePayload(entry?.new_data)
    : parsePayload(entry?.new_value);

  return (
    String(payload?.original_file_name || "").trim() ||
    String(payload?.document_name || "").trim() ||
    String(payload?.document_type_name || "").trim() ||
    getDocumentDisplayName(fallbackDocument)
  );
}

async function extractApiErrorMessage(error, fallbackMessage) {
  const directMessage =
    error?.response?.data?.error ||
    error?.response?.data?.detail ||
    error?.message;

  if (directMessage && typeof directMessage === "string") {
    return directMessage;
  }

  const blob = error?.response?.data;
  if (blob instanceof Blob) {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      if (parsed?.error || parsed?.detail) {
        return parsed.error || parsed.detail;
      }
    } catch (_) {
      // Ignore blob parsing issues and fall through to the fallback message.
    }
  }

  return fallbackMessage;
}

export default function ProfileDetail({
  profileId,
  initialTab = null,
  token,
  onBack,
  backLabel = "Back to Search",
  returnView = "search",
  onOpenAssignedToMe,
  onOpenAssignedByMe,
  onTabChange = null,
  user,
  onViewProfile,
}) {
  const canEdit =
    hasPermission(user, "profiles", "edit") ||
    hasPermission(user, "profiles", "edit", "duplicate_check") ||
    hasPermission(user, "profiles", "edit", "basic_details") ||
    hasPermission(user, "profiles", "edit", "community") ||
    hasPermission(user, "profiles", "edit", "address_contacts") ||
    hasPermission(user, "profiles", "edit", "contacts") ||
    hasPermission(user, "profiles", "edit", "addresses") ||
    hasPermission(user, "profiles", "edit", "education_career") ||
    hasPermission(user, "profiles", "edit", "family_relatives") ||
    hasPermission(user, "profiles", "edit", "preferences") ||
    hasPermission(user, "profiles", "edit", "persona") ||
    hasPermission(user, "profiles", "edit", "payments") ||
    hasPermission(user, "profiles", "edit", "media") ||
    hasPermission(user, "profiles", "edit", "tags") ||
    hasPermission(user, "profiles", "edit", "photos") ||
    hasPermission(user, "profiles", "edit", "documents") ||
    hasPermission(user, "profiles", "edit", "primary_photo") ||
    hasPermission(user, "profiles", "edit", "family_details");
  const canAddPhotos = hasPermission(user, "profiles", "edit", "media") || hasPermission(user, "profiles", "edit", "photos");
  const canSetPrimaryPhoto = hasPermission(user, "profiles", "edit", "media") || hasPermission(user, "profiles", "edit", "primary_photo");
  const canViewPrimaryPhoto = hasPermission(user, "profiles", "view", "primary_photo");
  const canViewPhotos = hasPermission(user, "profiles", "view", "photos");
  const canViewDocuments = hasPermission(user, "profiles", "view", "documents");
  const canViewTags = hasPermission(user, "profiles", "view", "tags");
  const canViewAlerts = hasPermission(user, "profiles", "view", "alerts");
  const canViewCompleteness = hasPermission(user, "profiles", "view", "completeness");
  const canViewOverviewTab = hasPermission(user, "profiles", "view", "overview_tab");
  const canViewContactsTab = hasPermission(user, "profiles", "view", "contacts_tab");
  const canViewCareerTab = hasPermission(user, "profiles", "view", "career_tab");
  const canViewFamilyTab = hasPermission(user, "profiles", "view", "family_tab");
  const canViewAddressesTab = hasPermission(user, "profiles", "view", "addresses_tab");
  const canViewPreferencesTab = hasPermission(user, "profiles", "view", "preferences_tab");
  const canViewMediaTab = hasPermission(user, "profiles", "view", "media_tab");
  const canViewRecommendationsProfileTab = hasPermission(user, "profiles", "view", "recommendations_tab");
  const canViewCrmTab = hasPermission(user, "profiles", "view", "crm_tab");
  const canViewPhone = false;
  const canViewEmail = false;
  const phoneVisible = false;
  const emailVisible = false;
  const viewingPhone = false;
  const viewingEmail = false;
  const primaryMobile = "";
  const emailContact = "";
  const setPhoneVisible = () => {};
  const setEmailVisible = () => {};
  const handleViewPhone = () => {};
  const handleViewEmail = () => {};
  const hiddenContactFields = [];
  const hasRestrictedContactsNotice = false;
  const canCopyPhone = hasPermission(user, "profiles", "copy", "phone");
  const canCopyEmail = hasPermission(user, "profiles", "copy", "email");
  const canDownloadProfiles = hasPermission(user, "profiles", "download");
  const canViewDocumentHistory = hasPermission(user, "audit", "view");
  const canViewNotes = hasPermission(user, "notes", "view");
  const canAddNotes = hasPermission(user, "notes", "create");
  const canGiveRecommendations = hasPermission(user, "recommendations", "give");
  const canViewRecommendationsPermission =
    hasPermission(user, "recommendations", "view") ||
    hasPermission(user, "recommendations", "view", "tab");
  const canEditRecommendations =
    hasPermission(user, "recommendations", "edit") ||
    canAddNotes;
  const canDownloadRecommendationBiodataPermission =
    hasPermission(user, "recommendations", "download") ||
    hasPermission(user, "recommendations", "download", "biodata") ||
    canDownloadProfiles;
  const canDownloadWatermarkedBiodataDocuments = canDownloadRecommendationBiodataPermission;
  const canViewRecommendations =
    canViewRecommendationsProfileTab ||
    canViewRecommendationsPermission ||
    canViewNotes;
  const canViewRecommendationsTab =
    canViewRecommendationsProfileTab ||
    canViewRecommendationsPermission ||
    (canViewCrmTab && (canViewNotes || canAddNotes));
  const showSearchReturnBar = returnView === "search";

  const [showEdit, setShowEdit] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedGalleryPhoto, setSelectedGalleryPhoto] = useState(null);
  const [selectedDocumentImage, setSelectedDocumentImage] = useState(null);

  const [notes, setNotes] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [addingRemark, setAddingRemark] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteMeta, setNoteMeta] = useState(FALLBACK_NOTE_META);
  const [noteFilters, setNoteFilters] = useState({
    note_type: "all",
    outcome: "all",
    follow_up: "all",
    follow_up_status: "all",
  });
  const [noteDraft, setNoteDraft] = useState({
    note_type: FALLBACK_NOTE_META.defaults.note_type,
    outcome: "",
    follow_up_at: "",
  });
  const [followUpSourceNote, setFollowUpSourceNote] = useState(null);
  const [resolveFollowUpSource, setResolveFollowUpSource] = useState(true);
  const [rescheduleDraft, setRescheduleDraft] = useState({ noteId: null, value: "" });
  const [noteActionBusyId, setNoteActionBusyId] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationMeta, setRecommendationMeta] = useState(FALLBACK_RECOMMENDATION_META);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationSearch, setRecommendationSearch] = useState("");
  const [recommendationSearchMode, setRecommendationSearchMode] = useState(PROFILE_NO_DISPLAY_SEARCH_KEY);
  const [recommendationResults, setRecommendationResults] = useState([]);
  const [recommendationSearchLoading, setRecommendationSearchLoading] = useState(false);
  const [selectedRecommendationTargets, setSelectedRecommendationTargets] = useState([]);
  const [recommendationNotesByTarget, setRecommendationNotesByTarget] = useState({});
  const [savingRecommendation, setSavingRecommendation] = useState(false);
  const [recommendationSearchError, setRecommendationSearchError] = useState("");
  const [recommendationSearchFeedback, setRecommendationSearchFeedback] = useState("");
  const [recommendationOutcomeDrafts, setRecommendationOutcomeDrafts] = useState({});
  const [recommendationOutcomeBusyId, setRecommendationOutcomeBusyId] = useState(null);
  const [recommendationDownloadBusyId, setRecommendationDownloadBusyId] = useState(null);
  const [biodataOptionModalOpen, setBiodataOptionModalOpen] = useState(false);
  const [biodataOptionModalLoading, setBiodataOptionModalLoading] = useState(false);
  const [biodataOptionModalError, setBiodataOptionModalError] = useState("");
  const [pendingBiodataRecommendation, setPendingBiodataRecommendation] = useState(null);
  const [pendingBiodataProfile, setPendingBiodataProfile] = useState(null);
  const [biodataOptionSelections, setBiodataOptionSelections] = useState({});
  const [watermarkedDocumentBusyId, setWatermarkedDocumentBusyId] = useState(null);
  const [watermarkedEditorOpen, setWatermarkedEditorOpen] = useState(false);
  const [pendingWatermarkedDocument, setPendingWatermarkedDocument] = useState(null);
  const [watermarkedPreviewLoading, setWatermarkedPreviewLoading] = useState(false);
  const [watermarkedPreviewLoadingMore, setWatermarkedPreviewLoadingMore] = useState(false);
  const [watermarkedPreviewError, setWatermarkedPreviewError] = useState("");
  const [watermarkedPreviewLoadMoreError, setWatermarkedPreviewLoadMoreError] = useState("");
  const [watermarkedPreviewPages, setWatermarkedPreviewPages] = useState([]);
  const [watermarkedPreviewTotalPages, setWatermarkedPreviewTotalPages] = useState(0);
  const [watermarkedPreviewHasMorePages, setWatermarkedPreviewHasMorePages] = useState(false);
  const [watermarkedPreviewNextPageStart, setWatermarkedPreviewNextPageStart] = useState(null);
  const [documentActionBusyKey, setDocumentActionBusyKey] = useState("");
  const [documentHistoryOpen, setDocumentHistoryOpen] = useState(false);
  const [documentHistoryLoading, setDocumentHistoryLoading] = useState(false);
  const [documentHistoryError, setDocumentHistoryError] = useState("");
  const [documentHistoryEntries, setDocumentHistoryEntries] = useState([]);
  const [documentHistoryMeta, setDocumentHistoryMeta] = useState(null);
  const [documentHistoryDocument, setDocumentHistoryDocument] = useState(null);
  const availableWatermarkRemovalOptions = [];
  const watermarkedDownloadSelections = {};

  const [showUpload, setShowUpload] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [preparingUploadFiles, setPreparingUploadFiles] = useState(false);
  const [uploadPreparationStatus, setUploadPreparationStatus] = useState("");
  const [settingPrimaryId, setSettingPrimaryId] = useState(null);
  const fileInputRef = useRef(null);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
  const remarkInputRef = useRef(null);

  const [lookups, setLookups] = useState({
    locations: [],
    cityTiers: [],
    castes: [],
    sakhas: [],
    educationCategories: [],
    visaStatuses: [],
    visaNames: [],
  });

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );
  const visibleTabs = useMemo(
    () =>
      PROFILE_TABS.filter((tab) => {
        if (tab.id === "overview") return canViewOverviewTab;
        if (tab.id === "contacts") return canViewContactsTab;
        if (tab.id === "career") return canViewCareerTab;
        if (tab.id === "family") return canViewFamilyTab;
        if (tab.id === "relatives") return canViewFamilyTab;
        if (tab.id === "addresses") return canViewAddressesTab;
        if (tab.id === "preferences") return canViewPreferencesTab;
        if (tab.id === "media") return canViewMediaTab && (canViewPhotos || canViewDocuments);
        if (tab.id === "recommendations") return canViewRecommendationsTab;
        if (tab.id === "crm") return canViewCrmTab;
        return false;
      }),
    [
      canViewAddressesTab,
      canViewCareerTab,
      canViewContactsTab,
      canViewCrmTab,
      canViewDocuments,
      canViewFamilyTab,
      canViewMediaTab,
      canViewOverviewTab,
      canViewPhotos,
      canViewPreferencesTab,
      canViewRecommendationsTab,
    ]
  );
  useEffect(() => {
    const requestedTab = String(initialTab || "").trim().toLowerCase();
    if (!requestedTab) return;
    if (!visibleTabs.some((tab) => tab.id === requestedTab)) return;
    setActiveTab(requestedTab);
  }, [initialTab, profileId, visibleTabs]);
  useEffect(() => {
    if (visibleTabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab(visibleTabs[0]?.id || "overview");
  }, [activeTab, visibleTabs]);
  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);

  const buildBiodataOptionList = useCallback(
    (profileData) => {
      if (!profileData) return [];

      const sectionCatalog = getProfileBiodataSectionCatalog(profileData, {
        lookups,
        includePhone: true,
        includeEmail: true,
        includeAddresses: true,
        includeRelativePhone: true,
        includeRelativeEmail: true,
      });
      const labelBySectionId = new Map(
        sectionCatalog
          .filter((section) => section?.id && section?.title)
          .map((section) => [section.id, section.title])
      );
      const photoCandidates = [
        profileData?.primary_photo_url_cache,
        profileData?.photo,
        ...safeArray(profileData?.media).map((item) => item?.file_url || item?.url || item?.src),
        ...safeArray(profileData?.files).map((item) => item?.file_url || item?.url || item?.src),
      ].filter(Boolean);
      const options = sectionCatalog.map((section) => ({
        id: section.id,
        label: labelBySectionId.get(section.id) || section.title || "Section",
      }));

      if (photoCandidates.length > 0) {
        options.splice(2, 0, { id: "profile_photo", label: "Images" });
      }

      return options;
    },
    [lookups]
  );

  const biodataOptionList = useMemo(
    () => buildBiodataOptionList(pendingBiodataProfile),
    [buildBiodataOptionList, pendingBiodataProfile]
  );

  const fetchNotes = useCallback(
    async (filters = noteFilters) => {
      if (!canViewNotes) {
        setNotes([]);
        return;
      }

      setNotesLoading(true);

      try {
        const params = {
          include_meta: 1,
          limit: 200,
        };

        if (filters.note_type && filters.note_type !== "all") params.note_type = filters.note_type;
        if (filters.outcome === "none") params.outcome = "null";
        else if (filters.outcome && filters.outcome !== "all") params.outcome = filters.outcome;
        if (filters.follow_up && filters.follow_up !== "all") params.follow_up = filters.follow_up;
        if (filters.follow_up_status && filters.follow_up_status !== "all") params.follow_up_status = filters.follow_up_status;

        const res = await API.get(`/profiles/${profileId}/notes`, {
          ...authHeaders,
          params,
        });

        const data = res.data;
        setNotes(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
        if (data?.allowed_values?.note_types?.length || data?.allowed_values?.outcomes?.length) {
        setNoteMeta((current) => ({
          note_types: data.allowed_values.note_types?.length ? data.allowed_values.note_types : current.note_types,
          outcomes: data.allowed_values.outcomes?.length ? data.allowed_values.outcomes : current.outcomes,
          follow_up_statuses: data.allowed_values.follow_up_statuses?.length ? data.allowed_values.follow_up_statuses : current.follow_up_statuses,
          defaults: current.defaults,
        }));
      }
      } catch (err) {
        console.error("Notes load failed:", err);
      } finally {
        setNotesLoading(false);
      }
    },
    [authHeaders, canViewNotes, noteFilters, profileId]
  );

  const fetchRecommendations = useCallback(async () => {
    if (!canViewRecommendations) {
      setRecommendations([]);
      return;
    }

    setRecommendationsLoading(true);

    try {
      const res = await API.get(`/profiles/${profileId}/recommendations`, {
        ...authHeaders,
        params: { include_meta: 1 },
      });
      const data = res.data || {};
      setRecommendations(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
      if (data?.allowed_values) {
        setRecommendationMeta({
          recommendation_statuses: Array.isArray(data.allowed_values.recommendation_statuses) && data.allowed_values.recommendation_statuses.length
            ? data.allowed_values.recommendation_statuses
            : FALLBACK_RECOMMENDATION_META.recommendation_statuses,
          match_statuses: Array.isArray(data.allowed_values.match_statuses) && data.allowed_values.match_statuses.length
            ? data.allowed_values.match_statuses
            : FALLBACK_RECOMMENDATION_META.match_statuses,
          outcome_sources: Array.isArray(data.allowed_values.outcome_sources) && data.allowed_values.outcome_sources.length
            ? data.allowed_values.outcome_sources
            : FALLBACK_RECOMMENDATION_META.outcome_sources,
          contact_sides: Array.isArray(data.allowed_values.contact_sides) && data.allowed_values.contact_sides.length
            ? data.allowed_values.contact_sides
            : FALLBACK_RECOMMENDATION_META.contact_sides,
          defaults: {
            recommendation_status: data?.defaults?.recommendation_status || FALLBACK_RECOMMENDATION_META.defaults.recommendation_status,
            outcome_source: data?.defaults?.outcome_source || FALLBACK_RECOMMENDATION_META.defaults.outcome_source,
            contact_side: data?.defaults?.contact_side || FALLBACK_RECOMMENDATION_META.defaults.contact_side,
          },
        });
      }
    } catch (err) {
      console.error("Recommendations load failed:", err);
    } finally {
      setRecommendationsLoading(false);
    }
  }, [authHeaders, canViewRecommendations, profileId]);

  const fetchProfile = useCallback(() => {
    setLoading(true);
    setError("");

    API.get(`/profiles/${profileId}`, authHeaders)
      .then((res) => {
        const data = res.data;
        setProfile(data);
      })
      .catch((err) => {
        console.error("Profile load failed:", err);
        setError(err.response?.data?.error || "Failed to load profile.");
      })
      .finally(() => setLoading(false));
  }, [profileId, authHeaders]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!canViewNotes && !canAddNotes) return undefined;
    let mounted = true;

    API.get("/profile-notes/meta", authHeaders)
      .then((res) => {
        if (!mounted) return;
        const data = res.data || {};
        setNoteMeta({
          note_types: Array.isArray(data.note_types) && data.note_types.length ? data.note_types : FALLBACK_NOTE_META.note_types,
          outcomes: Array.isArray(data.outcomes) && data.outcomes.length ? data.outcomes : FALLBACK_NOTE_META.outcomes,
          follow_up_statuses: Array.isArray(data.follow_up_statuses) && data.follow_up_statuses.length ? data.follow_up_statuses : FALLBACK_NOTE_META.follow_up_statuses,
          defaults: {
            note_type: data?.defaults?.note_type || FALLBACK_NOTE_META.defaults.note_type,
          },
        });
      })
      .catch(() => {
        if (!mounted) return;
        setNoteMeta(FALLBACK_NOTE_META);
      });

    return () => {
      mounted = false;
    };
  }, [authHeaders, canAddNotes, canViewNotes]);

  useEffect(() => {
    if (!canViewNotes) return;
    fetchNotes(noteFilters);
  }, [canViewNotes, fetchNotes, noteFilters]);

  useEffect(() => {
    if (!canViewRecommendations) return;
    fetchRecommendations();
  }, [canViewRecommendations, fetchRecommendations]);

  useEffect(() => {
    setNoteDraft((current) => {
      const nextType = noteMeta.defaults?.note_type || noteMeta.note_types?.[0] || FALLBACK_NOTE_META.defaults.note_type;
      if (noteMeta.note_types.includes(current.note_type)) return current;
      return {
        ...current,
        note_type: nextType,
      };
    });
  }, [noteMeta]);

  useEffect(() => {
    let mounted = true;

    Promise.allSettled([
      API.get("/locations"),
      API.get("/city-tiers"),
      API.get("/castes"),
      API.get("/sakhas"),
      API.get("/education-categories"),
      API.get("/visa-statuses"),
      API.get("/visa-names"),
    ]).then((results) => {
      if (!mounted) return;

      setLookups({
        locations: Array.isArray(results[0].value?.data) ? results[0].value.data : [],
        cityTiers: Array.isArray(results[1].value?.data) ? results[1].value.data : [],
        castes: Array.isArray(results[2].value?.data) ? results[2].value.data : [],
        sakhas: Array.isArray(results[3].value?.data) ? results[3].value.data : [],
        educationCategories: Array.isArray(results[4].value?.data) ? results[4].value.data : [],
        visaStatuses: Array.isArray(results[5].value?.data) ? results[5].value.data : [],
        visaNames: Array.isArray(results[6].value?.data) ? results[6].value.data : [],
      });
    });

    return () => {
      mounted = false;
    };
  }, []);

  const contacts = useMemo(() => safeArray(profile?.contacts), [profile]);
  const media = useMemo(() => safeArray(profile?.media || profile?.files), [profile]);
  const mediaLinks = useMemo(() => safeArray(profile?.media_links), [profile]);
  const occupations = useMemo(() => getProfileOccupations(profile), [profile]);
  const primaryOccupation = occupations.find((occupation) => occupation.is_primary) || occupations[0] || null;
  const photos = useMemo(() => media.filter((m) => !m.media_type || m.media_type === "photo"), [media]);
  const primaryPhoto = photos.find((f) => f.is_primary)?.file_url || profile?.primary_photo_url_cache || null;

  const documents = useMemo(
    () =>
      media.filter((m) => {
        const mediaType = String(m?.media_type || "").trim().toLowerCase();
        if (mediaType === "photo") return false;
        return Boolean(mediaType || m?.document_type_name || m?.template_key);
      }),
    [media]
  );
  const alerts = useMemo(() => safeArray(profile?.alerts), [profile]);
  const openAlerts = useMemo(
    () => alerts.filter((item) => normalizeAlertStatus(item?.status) === "open"),
    [alerts]
  );
  const recommendedProfileIds = useMemo(
    () => new Set(recommendations.map((item) => String(item?.to_profile_id || "")).filter(Boolean)),
    [recommendations]
  );
  const selectedRecommendationTargetIds = useMemo(
    () => new Set(selectedRecommendationTargets.map((item) => String(item?.id || "")).filter(Boolean)),
    [selectedRecommendationTargets]
  );
  const recommendationNoteContextMap = useMemo(
    () => buildRecommendationNoteContextMap(recommendations),
    [recommendations]
  );
  const showAssignmentReturnBar = returnView === "assigned-profiles";

  const completenessPct = getCompletenessPct(profile);
  const missingItems = safeArray(profile?.completeness?.missing);
  const lookupMaps = useMemo(() => buildLookupMaps(lookups), [lookups]);
  const sampradayDisplay = profile?.sampraday_name || "";
  const sampradayDetailsDisplay = profile?.sampraday_details || "";

  const dueFollowUpCount = useMemo(
    () =>
      notes.filter((note) => {
        if (!note?.follow_up_at) return false;
        const followUpDate = new Date(note.follow_up_at);
        return !Number.isNaN(followUpDate.getTime()) && followUpDate.getTime() <= Date.now();
      }).length,
    [notes]
  );
  const scheduledFollowUpCount = useMemo(
    () => notes.filter((note) => !!note?.follow_up_at).length,
    [notes]
  );
  const communicationCount = useMemo(
    () =>
      notes.filter((note) =>
        ["call", "meeting", "match_share"].includes(String(note?.note_type || "").trim().toLowerCase())
      ).length,
    [notes]
  );
  const feedbackCount = useMemo(
    () =>
      notes.filter((note) => String(note?.note_type || "").trim().toLowerCase() === "feedback").length,
    [notes]
  );

  const computedSuggestions = useMemo(() => {
    const noteTypeKey = String(noteDraft.note_type || "").trim().toLowerCase();
    const outcomeKey = String(noteDraft.outcome || "").trim().toLowerCase();
    const latestNote = notes[0];
    const suggestions = [];
    const typeTemplates = NOTE_TYPE_COPY[noteTypeKey]?.templates || [];

    suggestions.push(...typeTemplates);

    if (noteTypeKey === "call") {
      suggestions.push("Called family and introduced the profile. Next response awaited.");
      suggestions.push("Call completed. Family asked to share biodata before deciding.");
    }

    if (noteTypeKey === "meeting") {
      suggestions.push("Meeting scheduled with both families after profile shortlist.");
      suggestions.push("Meeting completed. Compatibility discussion was encouraging.");
    }

    if (noteTypeKey === "match_share") {
      suggestions.push("Shared match details and highlighted family preferences.");
      suggestions.push("Recommended this profile as a suitable shortlist option.");
    }

    if (noteTypeKey === "feedback") {
      suggestions.push("Collected direct feedback and updated future match preference.");
      suggestions.push("Feedback captured from family discussion for next recommendation round.");
    }

    if (outcomeKey === "callback") {
      suggestions.push("Family asked for a callback after discussing with elders.");
    }

    if (outcomeKey === "follow_up_required") {
      suggestions.push("Next follow-up required after family reviews biodata internally.");
    }

    if (outcomeKey === "not_interested") {
      suggestions.push("Family declined the profile after internal discussion.");
    }

    if (outcomeKey === "interested") {
      suggestions.push("Strong positive signal received. Move to next matching step.");
    }

    if (dueFollowUpCount > 0) {
      suggestions.push(`There are ${dueFollowUpCount} due follow-ups pending on this profile.`);
    }

    if (latestNote?.content) {
      suggestions.push(`Latest context: ${String(latestNote.content).slice(0, 88)}`);
    }

    return Array.from(new Set(suggestions.filter(Boolean)));
  }, [dueFollowUpCount, noteDraft.note_type, noteDraft.outcome, notes]);

  const handleRemarkChange = (event) => {
    const value = event.target.value;
    setNewRemark(value);

    const filtered = value.trim()
      ? computedSuggestions.filter((s) => s.toLowerCase().includes(value.toLowerCase()))
      : computedSuggestions.length
        ? computedSuggestions
        : REMARK_SUGGESTIONS;

    setFilteredSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  };

  const handleSuggestionClick = (suggestion) => {
    setNewRemark(suggestion);
    setShowSuggestions(false);
    remarkInputRef.current?.focus();
  };

  const handleDraftFieldChange = (field) => (event) => {
    const value = event.target.value;
    setNoteDraft((current) => ({
      ...current,
      [field]: value,
    }));

    if (field === "note_type") {
      const suggestionPool = NOTE_TYPE_COPY[String(value || "").trim().toLowerCase()]?.templates || computedSuggestions;
      setFilteredSuggestions(suggestionPool);
      setShowSuggestions(suggestionPool.length > 0);
    }
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setNoteFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetDraft = useCallback(() => {
    setNewRemark("");
    setNoteDraft({
      note_type: noteMeta.defaults?.note_type || noteMeta.note_types?.[0] || FALLBACK_NOTE_META.defaults.note_type,
      outcome: "",
      follow_up_at: "",
    });
    setFollowUpSourceNote(null);
    setResolveFollowUpSource(true);
    setRescheduleDraft({ noteId: null, value: "" });
    setFilteredSuggestions(computedSuggestions);
    setShowSuggestions(false);
  }, [computedSuggestions, noteMeta]);

  const handleStartFollowUp = useCallback((note) => {
    if (!note) return;
    const nextType = noteMeta.note_types.includes("follow_up")
      ? "follow_up"
      : noteMeta.defaults?.note_type || noteMeta.note_types?.[0] || FALLBACK_NOTE_META.defaults.note_type;

    setFollowUpSourceNote(note);
    setResolveFollowUpSource(true);
    setNoteDraft({
      note_type: nextType,
      outcome: "",
      follow_up_at: "",
    });
    setNewRemark(`Follow-up on previous note: ${note.content || ""}`.trim());
    setShowSuggestions(false);

    setTimeout(() => {
      remarkInputRef.current?.focus();
      remarkInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 40);
  }, [noteMeta, setNewRemark]);

  const handleStartReschedule = useCallback((note) => {
    if (!note?.id) return;
    setRescheduleDraft({
      noteId: note.id,
      value: toDateTimeLocalValue(note.follow_up_at),
    });
  }, []);

  const handleSaveReschedule = async (note) => {
    if (!note?.id || !rescheduleDraft.value) return;

    try {
      setNoteActionBusyId(note.id);
      await API.patch(
        `/profile-notes/${note.id}/follow-up/reschedule`,
        {
          follow_up_at: new Date(rescheduleDraft.value).toISOString(),
        },
        authHeaders
      );
      setRescheduleDraft({ noteId: null, value: "" });
      await fetchNotes(noteFilters);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to reschedule follow-up.");
    } finally {
      setNoteActionBusyId(null);
    }
  };

  const handleCompleteFollowUp = async (note) => {
    if (!note?.id) return;

    try {
      setNoteActionBusyId(note.id);
      await API.patch(`/profile-notes/${note.id}/follow-up/complete`, {}, authHeaders);
      if (followUpSourceNote?.id === note.id) {
        resetDraft();
      }
      await fetchNotes(noteFilters);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to complete follow-up.");
    } finally {
      setNoteActionBusyId(null);
    }
  };

  const handleCancelFollowUp = async (note) => {
    if (!note?.id) return;

    try {
      setNoteActionBusyId(note.id);
      await API.patch(`/profile-notes/${note.id}/follow-up/cancel`, {}, authHeaders);
      if (followUpSourceNote?.id === note.id) {
        resetDraft();
      }
      await fetchNotes(noteFilters);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to cancel follow-up.");
    } finally {
      setNoteActionBusyId(null);
    }
  };

  const handleAddRemark = async () => {
    if (!newRemark.trim()) return;

    try {
      setAddingRemark(true);

      await API.post(
        `/profiles/${profileId}/notes`,
        {
          note: newRemark.trim(),
          note_type: noteDraft.note_type,
          outcome: noteDraft.outcome || null,
          follow_up_at: noteDraft.follow_up_at ? new Date(noteDraft.follow_up_at).toISOString() : null,
          follow_up_status: deriveDraftFollowUpStatus(noteDraft),
          parent_note_id: followUpSourceNote?.id || null,
        },
        authHeaders
      );

      if (followUpSourceNote?.id && resolveFollowUpSource) {
        await API.patch(
          `/profile-notes/${followUpSourceNote.id}/follow-up/complete`,
          {},
          authHeaders
        );
      }

      resetDraft();
      if (canViewNotes) {
        await fetchNotes(noteFilters);
      } else {
        fetchProfile();
      }
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add note.");
    } finally {
      setAddingRemark(false);
    }
  };

  useEffect(() => {
    setFilteredSuggestions(computedSuggestions.length ? computedSuggestions : REMARK_SUGGESTIONS);
  }, [computedSuggestions]);

  useEffect(() => {
    if (activeTab !== "recommendations" || !canGiveRecommendations) return undefined;

    const query = String(recommendationSearch || "").trim();
    const minLength = isProfileNoDisplaySearchMode(recommendationSearchMode) ? 1 : 2;
    if (query.length < minLength) {
      setRecommendationResults([]);
      setRecommendationSearchLoading(false);
      setRecommendationSearchError("");
      setRecommendationSearchFeedback("");
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setRecommendationSearchLoading(true);
      setRecommendationSearchError("");
      try {
        const res = await API.get("/profiles/search", {
          ...authHeaders,
          params: {
            q: query,
            limit: 8,
            search_by: recommendationSearchMode,
            source: "profile-recommendations",
          },
        });
        if (cancelled) return;
        const items = Array.isArray(res.data?.items) ? res.data.items : Array.isArray(res.data) ? res.data : [];
        const selfMatches = items.some((item) => String(item?.id || "") === String(profileId));
        const alreadyRecommendedMatches = items.some((item) => recommendedProfileIds.has(String(item?.id || "")));
        const alreadySelectedMatches = items.some((item) => selectedRecommendationTargetIds.has(String(item?.id || "")));
        const filteredItems = items.filter((item) => {
          const itemId = String(item?.id || "");
          return (
            itemId &&
            itemId !== String(profileId) &&
            !recommendedProfileIds.has(itemId) &&
            !selectedRecommendationTargetIds.has(itemId)
          );
        });

        setRecommendationResults(filteredItems);

        if (filteredItems.length > 0) {
          setRecommendationSearchFeedback("");
        } else if (selfMatches && alreadyRecommendedMatches) {
          setRecommendationSearchFeedback("Matching profiles were filtered out because the current profile cannot recommend itself and some results are already recommended.");
        } else if (selfMatches) {
          setRecommendationSearchFeedback("This search matches the current profile, which cannot be recommended to itself.");
        } else if (alreadyRecommendedMatches) {
          setRecommendationSearchFeedback("Matching profiles are already recommended from this profile.");
        } else if (alreadySelectedMatches) {
          setRecommendationSearchFeedback("Matching profiles are already selected for this batch.");
        } else {
          setRecommendationSearchFeedback("No matching profiles available for recommendation.");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Recommendation search failed:", err);
        setRecommendationResults([]);
        setRecommendationSearchError(err?.response?.data?.error || "Could not search profiles right now.");
        setRecommendationSearchFeedback("");
      } finally {
        if (!cancelled) setRecommendationSearchLoading(false);
      }
    }, 260);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeTab, authHeaders, canGiveRecommendations, profileId, recommendationSearch, recommendationSearchMode, recommendedProfileIds, selectedRecommendationTargetIds]);

  const handleSelectRecommendationTarget = useCallback((target) => {
    if (!target) {
      setSelectedRecommendationTargets([]);
      setRecommendationResults([]);
      setRecommendationNotesByTarget({});
      return;
    }

    setSelectedRecommendationTargets((current) => {
      const targetId = String(target?.id || "");
      if (!targetId || current.some((item) => String(item?.id || "") === targetId)) return current;
      return [...current, target];
    });
    setRecommendationSearch("");
    setRecommendationResults([]);
    setRecommendationSearchFeedback("");
  }, []);

  const handleRemoveRecommendationTarget = useCallback((targetId) => {
    const normalizedTargetId = String(targetId || "");
    const nextTargets = selectedRecommendationTargets.filter((item) => String(item?.id || "") !== normalizedTargetId);
    setSelectedRecommendationTargets(nextTargets);
    setRecommendationNotesByTarget((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, normalizedTargetId)) return current;
      const next = { ...current };
      delete next[normalizedTargetId];
      return next;
    });
  }, [selectedRecommendationTargets]);

  const handleRecommendationTargetNoteChange = useCallback((targetId, value) => {
    const normalizedTargetId = String(targetId || "");
    if (!normalizedTargetId) return;
    setRecommendationNotesByTarget((current) => ({
      ...current,
      [normalizedTargetId]: value,
    }));
  }, []);

  const handleCreateRecommendation = async () => {
    const targets = selectedRecommendationTargets
      .map((item) => {
        const targetId = String(item?.id || "").trim();
        if (!targetId) return null;
        return {
          to_profile_id: targetId,
          note: String(recommendationNotesByTarget[targetId] || "").trim() || null,
        };
      })
      .filter(Boolean);
    if (!canGiveRecommendations || targets.length === 0) return;

    try {
      setSavingRecommendation(true);
      const res = await API.post(
        `/profiles/${profileId}/recommendations`,
        {
          recommendations: targets,
        },
        authHeaders
      );
      const createdCount = Number(res.data?.created_count || 0);
      const skippedCount = Number(res.data?.skipped_count || 0);
      setRecommendationSearch("");
      setRecommendationResults([]);
      setSelectedRecommendationTargets([]);
      setRecommendationNotesByTarget({});
      await Promise.all([
        fetchRecommendations(),
        canViewNotes ? fetchNotes(noteFilters) : Promise.resolve(),
      ]);
      if (!createdCount && skippedCount) {
        alert("Selected profiles were already recommended from this profile.");
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to create recommendation.");
    } finally {
      setSavingRecommendation(false);
    }
  };

  const handleRecommendationOutcomeDraftChange = useCallback((recommendationId, field, value) => {
    setRecommendationOutcomeDrafts((current) => ({
      ...current,
      [recommendationId]: {
        status: current[recommendationId]?.status || "",
        notes: current[recommendationId]?.notes || "",
        contactSide:
          current[recommendationId]?.contactSide ||
          recommendationMeta.defaults?.contact_side ||
          "both",
        [field]: value,
      },
    }));
  }, [recommendationMeta.defaults?.contact_side]);

  const handleSaveRecommendationOutcome = async (recommendation) => {
    const recommendationId = recommendation?.id;
    const draft = recommendationOutcomeDrafts[recommendationId] || {};
    if (!canEditRecommendations || !recommendationId || !draft.status) return;

    try {
      setRecommendationOutcomeBusyId(recommendationId);
      await API.post(
        `/recommendations/${recommendationId}/outcomes`,
        {
          match_status: draft.status,
          outcome_notes: String(draft.notes || "").trim() || null,
          outcome_source: recommendationMeta.defaults?.outcome_source || "manual",
          contact_side: draft.contactSide || recommendationMeta.defaults?.contact_side || "both",
        },
        authHeaders
      );
      setRecommendationOutcomeDrafts((current) => {
        const next = { ...current };
        delete next[recommendationId];
        return next;
      });
      await Promise.all([
        fetchRecommendations(),
        canViewNotes ? fetchNotes(noteFilters) : Promise.resolve(),
      ]);
    } catch (err) {
      alert(err?.response?.data?.error || "Failed to save recommendation outcome.");
    } finally {
      setRecommendationOutcomeBusyId(null);
    }
  };

  const closeBiodataOptionModal = useCallback((force = false) => {
    if (!force && recommendationDownloadBusyId === pendingBiodataRecommendation?.id) return;
    setBiodataOptionModalOpen(false);
    setBiodataOptionModalLoading(false);
    setBiodataOptionModalError("");
    setPendingBiodataRecommendation(null);
    setPendingBiodataProfile(null);
    setBiodataOptionSelections({});
  }, [pendingBiodataRecommendation?.id, recommendationDownloadBusyId]);

  const handleBiodataOptionSelectionChange = useCallback((optionId, checked) => {
    setBiodataOptionSelections((current) => ({
      ...current,
      [optionId]: checked,
    }));
  }, []);

  const handleDownloadRecommendationBiodata = async (recommendation) => {
    const recommendationId = recommendation?.id;
    const targetId = recommendation?.to_profile_id;
    if (!canDownloadRecommendationBiodataPermission || !recommendationId || !targetId) return;

    try {
      setBiodataOptionModalOpen(true);
      setBiodataOptionModalLoading(true);
      setBiodataOptionModalError("");
      setPendingBiodataRecommendation(recommendation);
      setPendingBiodataProfile(null);
      setBiodataOptionSelections({});

      const res = await API.get(`/profiles/${targetId}`, {
        ...authHeaders,
        params: {
          recommendation_id: recommendationId,
          source_profile_id: profileId,
        },
      });
      const nextProfile = res.data;
      const nextOptions = buildBiodataOptionList(nextProfile);
      if (!nextOptions.length) {
        setBiodataOptionModalError("No biodata sections are available on this profile.");
      }
      setPendingBiodataProfile(nextProfile);
      setBiodataOptionSelections(
        nextOptions.reduce((acc, option) => {
          acc[option.id] = true;
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Biodata PDF generation failed:", err);
      setBiodataOptionModalError(
        err?.response?.data?.error || err?.message || "Failed to prepare biodata options."
      );
    } finally {
      setBiodataOptionModalLoading(false);
    }
  };

  const handleConfirmRecommendationBiodataDownload = async () => {
    const recommendationId = pendingBiodataRecommendation?.id;
    const targetId = pendingBiodataRecommendation?.to_profile_id;
    if (!canDownloadRecommendationBiodataPermission || !recommendationId || !targetId || !pendingBiodataProfile) return;

    const selectedSectionIds = biodataOptionList
      .filter((option) => option.id !== "profile_photo" && biodataOptionSelections[option.id] === true)
      .map((option) => option.id);

    if (!selectedSectionIds.length) {
      setBiodataOptionModalError("Select at least one biodata section to include.");
      return;
    }

    try {
      setRecommendationDownloadBusyId(recommendationId);
      setBiodataOptionModalError("");
      await API.post(
        `/profiles/${targetId}/biodata-download`,
        {
          recommendation_id: recommendationId,
          source_profile_id: profileId,
        },
        authHeaders
      );
      await generateProfileBiodataPdf(pendingBiodataProfile, {
        lookups,
        includePhone: true,
        includeEmail: true,
        includeAddresses: true,
        includeRelativePhone: true,
        includeRelativeEmail: true,
        includePhoto: biodataOptionSelections.profile_photo === true,
        includedSectionIds: selectedSectionIds,
        fileName: buildProfileBiodataFileName(pendingBiodataProfile),
        accessContext: {
          recommendationId,
          sourceProfileId: profileId,
        },
      });
      closeBiodataOptionModal(true);
    } catch (err) {
      console.error("Biodata PDF generation failed:", err);
      setBiodataOptionModalError(
        err?.response?.data?.error || err?.message || "Failed to download biodata PDF."
      );
    } finally {
      setRecommendationDownloadBusyId(null);
    }
  };

  const loadWatermarkedPreviewPages = useCallback(async (
    docItem,
    { startPage = 1, append = false } = {}
  ) => {
    if (!docItem?.id || !profileId) return [];

    if (append) {
      setWatermarkedPreviewLoadingMore(true);
      setWatermarkedPreviewLoadMoreError("");
    } else {
      setWatermarkedPreviewLoading(true);
      setWatermarkedPreviewError("");
      setWatermarkedPreviewLoadMoreError("");
    }

    try {
      const res = await API.get(
        `/profiles/${profileId}/documents/${docItem.id}/watermarked-preview`,
        {
          ...authHeaders,
          params: {
            startPage,
            pageCount: WATERMARK_PREVIEW_BATCH_SIZE,
          },
        }
      );
      const nextPages = Array.isArray(res.data?.pages) ? res.data.pages : [];

      setWatermarkedPreviewTotalPages(Number(res.data?.page_count || nextPages.length || 0));
      setWatermarkedPreviewHasMorePages(Boolean(res.data?.has_more_pages));
      setWatermarkedPreviewNextPageStart(
        Number(res.data?.next_page_start || 0) > 0 ? Number(res.data.next_page_start) : null
      );
      setWatermarkedPreviewPages((current) => {
        if (!append) return nextPages;

        const merged = new Map();
        [...current, ...nextPages].forEach((page) => {
          if (page?.pageNumber) merged.set(page.pageNumber, page);
        });
        return Array.from(merged.values()).sort((left, right) => left.pageNumber - right.pageNumber);
      });

      return nextPages;
    } catch (err) {
      if (append) {
        setWatermarkedPreviewLoadMoreError(
          await extractApiErrorMessage(err, "Failed to load more preview pages.")
        );
      } else {
        throw err;
      }
      return [];
    } finally {
      if (append) {
        setWatermarkedPreviewLoadingMore(false);
      } else {
        setWatermarkedPreviewLoading(false);
      }
    }
  }, [authHeaders, profileId]);

  const openWatermarkedDownloadOptions = async (docItem) => {
    if (!canDownloadWatermarkedBiodataDocuments || !docItem?.id || !isBiodataPdfMedia(docItem)) return;
    setPendingWatermarkedDocument(docItem);
    setWatermarkedPreviewPages([]);
    setWatermarkedPreviewError("");
    setWatermarkedPreviewLoadMoreError("");
    setWatermarkedPreviewTotalPages(0);
    setWatermarkedPreviewHasMorePages(false);
    setWatermarkedPreviewNextPageStart(null);
    setWatermarkedPreviewLoading(true);
    setWatermarkedEditorOpen(true);

    try {
      await loadWatermarkedPreviewPages(docItem, { startPage: 1, append: false });
    } catch (err) {
      console.error("Watermarked preview load failed:", err);
      setWatermarkedPreviewError(
        await extractApiErrorMessage(err, "Failed to load the biodata preview.")
      );
    } finally {
      setWatermarkedPreviewLoading(false);
    }
  };

  const closeWatermarkedDownloadOptions = () => {
    if (watermarkedDocumentBusyId) return;
    setWatermarkedEditorOpen(false);
    setPendingWatermarkedDocument(null);
    setWatermarkedPreviewPages([]);
    setWatermarkedPreviewError("");
    setWatermarkedPreviewLoadMoreError("");
    setWatermarkedPreviewLoading(false);
    setWatermarkedPreviewLoadingMore(false);
    setWatermarkedPreviewTotalPages(0);
    setWatermarkedPreviewHasMorePages(false);
    setWatermarkedPreviewNextPageStart(null);
  };
  const handleWatermarkedSelectionChange = () => {};

  const handleDownloadWatermarkedDocument = async (
    redactions,
    docItem = pendingWatermarkedDocument
  ) => {
    const documentId = docItem?.id;
    if (!canDownloadWatermarkedBiodataDocuments || !documentId || !isBiodataPdfMedia(docItem) || !profile) return;

    try {
      setWatermarkedDocumentBusyId(documentId);
      const res = await API.post(
        `/profiles/${profileId}/documents/${documentId}/watermarked-download`,
        {
          redactions: Array.isArray(redactions) ? redactions : [],
        },
        {
          ...authHeaders,
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = buildWatermarkedBiodataFileName(profile);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(downloadUrl);
      closeWatermarkedDownloadOptions();
    } catch (err) {
      console.error("Watermarked PDF download failed:", err);
      const errorMessage = await extractApiErrorMessage(
        err,
        "Failed to download the watermarked PDF."
      );
      alert(errorMessage);
    } finally {
      setWatermarkedDocumentBusyId(null);
    }
  };

  const loadDocumentHistory = useCallback(
    async (docItem) => {
      if (!docItem?.id) return null;

      const res = await API.get(
        `/profiles/${profileId}/documents/${docItem.id}/history`,
        {
          ...authHeaders,
          params: {
            include_meta: 1,
          },
        }
      );

      setDocumentHistoryEntries(Array.isArray(res.data?.items) ? res.data.items : []);
      setDocumentHistoryMeta(res.data?.document || null);
      return res.data || null;
    },
    [authHeaders, profileId]
  );

  const closeDocumentHistory = useCallback(() => {
    setDocumentHistoryOpen(false);
    setDocumentHistoryLoading(false);
    setDocumentHistoryError("");
    setDocumentHistoryEntries([]);
    setDocumentHistoryMeta(null);
    setDocumentHistoryDocument(null);
  }, []);

  const openDocumentHistory = useCallback(
    async (docItem) => {
      if (!canViewDocumentHistory || !docItem?.id) return;

      setDocumentHistoryDocument(docItem);
      setDocumentHistoryOpen(true);
      setDocumentHistoryLoading(true);
      setDocumentHistoryError("");
      setDocumentHistoryEntries([]);
      setDocumentHistoryMeta(null);

      try {
        await loadDocumentHistory(docItem);
      } catch (err) {
        console.error("Document history load failed:", err);
        setDocumentHistoryError(
          await extractApiErrorMessage(err, "Failed to load document history.")
        );
      } finally {
        setDocumentHistoryLoading(false);
      }
    },
    [canViewDocumentHistory, loadDocumentHistory]
  );

  const handleDocumentFileAction = useCallback(
    async (docItem, disposition) => {
      if (!docItem?.id) return;

      const documentIsImage = isImageMedia(docItem);
      const normalizedDisposition = disposition === "attachment" ? "attachment" : "inline";
      const busyKey = `${normalizedDisposition}:${docItem.id}`;
      const previewWindow =
        normalizedDisposition === "inline" && !documentIsImage
          ? window.open("", "_blank", "noopener,noreferrer")
          : null;

      try {
        setDocumentActionBusyKey(busyKey);
        const res = await API.get(
          `/profiles/${profileId}/documents/${docItem.id}/file`,
          {
            ...authHeaders,
            params: {
              disposition: normalizedDisposition,
            },
            responseType: "blob",
          }
        );

        const blob = new Blob([res.data], {
          type: res.headers?.["content-type"] || docItem.mime_type || "application/octet-stream",
        });
        const objectUrl = window.URL.createObjectURL(blob);

        if (normalizedDisposition === "inline") {
          if (documentIsImage) {
            setSelectedDocumentImage((current) => {
              if (current?.preview_url) {
                window.URL.revokeObjectURL(current.preview_url);
              }
              return {
                ...docItem,
                preview_url: objectUrl,
              };
            });
          } else {
            if (previewWindow && !previewWindow.closed) {
              previewWindow.location = objectUrl;
            } else {
              window.open(objectUrl, "_blank", "noopener,noreferrer");
            }
            window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
          }
        } else {
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = docItem.original_file_name || getDocumentDisplayName(docItem);
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(objectUrl);
        }

        if (documentHistoryOpen && documentHistoryDocument?.id === docItem.id) {
          loadDocumentHistory(docItem).catch(() => {});
        }
      } catch (err) {
        if (previewWindow && !previewWindow.closed) {
          previewWindow.close();
        }
        console.error("Document action failed:", err);
        alert(
          await extractApiErrorMessage(
            err,
            normalizedDisposition === "attachment"
              ? "Failed to download the document."
              : "Failed to open the document."
          )
        );
      } finally {
        setDocumentActionBusyKey("");
      }
    },
    [authHeaders, documentHistoryDocument, documentHistoryOpen, loadDocumentHistory, profileId]
  );

const handleFileSelect = async (event) => {
  const input = event.target;
  const selected = Array.from(input.files || []);
  setUploadStatus("");
  setUploadPreparationStatus("");

  if (!selected.length) {
    input.value = "";
    return;
  }

  const validSizeFiles = await prepareFilesForUpload(selected, {
    allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    allowedTypeLabel: "JPEG, PNG and WEBP",
    fileKindLabel: "photo",
    onStatusChange: ({ active, message }) => {
      setPreparingUploadFiles(active);
      setUploadPreparationStatus(active ? message : "");
    },
  });

  const remaining = Math.max(0, 10 - imageFiles.length);

  if (remaining <= 0) {
    alert("Maximum 10 photos are allowed in one upload.");
    input.value = "";
    return;
  }

  const entries = validSizeFiles.slice(0, remaining).map((file) => ({
    file,
    previewUrl: URL.createObjectURL(file),
    name: file.name,
    size: file.size,
    type: file.type,
  }));

  if (!entries.length) {
    input.value = "";
    return;
  }

  setImageFiles((prev) => [...prev, ...entries].slice(0, 10));
  setPrimaryIndex((current) =>
    imageFiles.length === 0 && current === 0 ? 0 : current
  );

  setPreparingUploadFiles(false);
  setUploadPreparationStatus("");
  input.value = "";
};

  const removeImage = (idx) => {
    setImageFiles((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });

    setPrimaryIndex((pi) => {
      if (pi === idx) return 0;
      if (pi > idx) return pi - 1;
      return pi;
    });
  };

  const closeUpload = () => {
    if (preparingUploadFiles) return;
    imageFiles.forEach((file) => URL.revokeObjectURL(file.previewUrl));
    setImageFiles([]);
    setPrimaryIndex(0);
    setUploadStatus("");
    setUploadPreparationStatus("");
    setShowUpload(false);
  };

const handleUpload = async () => {
  if (!canAddPhotos) return;

  const uploadItems = imageFiles.filter(
    (item) =>
      item &&
      item.file &&
      typeof item.file === "object" &&
      item.file.size > 0 &&
      ["image/jpeg", "image/png", "image/webp"].includes(item.file.type)
  );

  if (!uploadItems.length) {
    setUploadStatus("Please select at least one valid photo.");
    return;
  }

  setUploading(true);
  setUploadStatus(
    `Uploading ${uploadItems.length} photo${uploadItems.length > 1 ? "s" : ""}...`
  );

  try {
    const formData = new FormData();

    uploadItems.forEach((item) => {
      formData.append("images", item.file, item.file.name || "profile-photo");
    });

    const safePrimaryIndex =
      Number.isInteger(primaryIndex) &&
      primaryIndex >= 0 &&
      primaryIndex < uploadItems.length
        ? primaryIndex
        : 0;

    formData.append("primary_image_index", String(safePrimaryIndex));
    formData.append("primaryImageIndex", String(safePrimaryIndex));

    // Use native fetch for this multipart request.
    // Do not set Content-Type; the browser must set multipart boundary automatically.
    const baseURL = API.defaults?.baseURL || "/api";

    const response = await fetch(`${baseURL}/profiles/${profileId}/images`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.error || "Image upload failed");
    }

    setUploadStatus("Done. Reloading...");

    setTimeout(() => {
      closeUpload();
      fetchProfile();
    }, 700);
  } catch (err) {
    setUploadStatus("");
    alert("Upload failed: " + (err.message || "Image upload failed"));
  } finally {
    setUploading(false);
  }
};

  const handleSetPrimary = async (fileId) => {
    if (!canSetPrimaryPhoto || !fileId || settingPrimaryId === fileId) return;

    setSettingPrimaryId(fileId);

    try {
      await API.patch(`/profiles/${profileId}/images/${fileId}/primary`, {}, authHeaders);
      fetchProfile();
    } catch (err) {
      alert("Failed to set primary image: " + (err?.response?.data?.error || err.message));
    } finally {
      setSettingPrimaryId(null);
    }
  };

  useEffect(() => {
    if (!selectedGalleryPhoto && !selectedDocumentImage) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setSelectedGalleryPhoto(null);
        setSelectedDocumentImage(null);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedDocumentImage, selectedGalleryPhoto]);

  useEffect(() => {
    return () => {
      if (selectedDocumentImage?.preview_url) {
        window.URL.revokeObjectURL(selectedDocumentImage.preview_url);
      }
    };
  }, [selectedDocumentImage]);

  useEffect(() => {
    const hasOpenOverlay =
      selectedDocumentImage ||
      showUpload ||
      biodataOptionModalOpen ||
      documentHistoryOpen;
    if (!hasOpenOverlay) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [biodataOptionModalOpen, documentHistoryOpen, selectedDocumentImage, showUpload]);

  if (showEdit) {
    return (
      <EditProfile
        profileId={profileId}
        token={token}
        user={user}
        onBack={() => setShowEdit(false)}
        onViewProfile={onViewProfile}
        onSaved={() => {
          fetchProfile();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div style={S.centerWrap}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={S.spinner} />
        <p style={S.loadingText}>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.centerWrap}>
        <p style={S.errorText}>{error}</p>
        <button style={S.backBtn} onClick={onBack}>{`< ${backLabel}`}</button>
      </div>
    );
  }

  if (!profile) return null;

  if (watermarkedEditorOpen) {
    return (
      <div style={S.watermarkPage}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
        <div style={S.watermarkWorkspaceWrap}>
          <WatermarkedPdfRedactionScreen
            open
            pages={watermarkedPreviewPages}
            loading={watermarkedPreviewLoading}
            loadingMorePages={watermarkedPreviewLoadingMore}
            error={watermarkedPreviewError}
            loadMoreError={watermarkedPreviewLoadMoreError}
            busy={watermarkedDocumentBusyId === pendingWatermarkedDocument?.id}
            totalPageCount={watermarkedPreviewTotalPages}
            hasMorePages={watermarkedPreviewHasMorePages}
            fileName={profile ? buildWatermarkedBiodataFileName(profile) : "Watermarked_Bio_Data.pdf"}
            onClose={closeWatermarkedDownloadOptions}
            onLoadMorePages={() =>
              loadWatermarkedPreviewPages(pendingWatermarkedDocument, {
                startPage: watermarkedPreviewNextPageStart || watermarkedPreviewPages.length + 1,
                append: true,
              })
            }
            onConfirm={(redactions) =>
              handleDownloadWatermarkedDocument(redactions, pendingWatermarkedDocument)
            }
          />
        </div>
      </div>
    );
  }

  const fullName = [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" ");
  const age = getAge(profile.birth_date || profile.dob);
  const initials = `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase();
  const avatarColor = pickColor(profile.first_name || "A");

  const locationDisplay =
    profile.location_display_cache ||
    formatProfileLocation(profile, lookupMaps) ||
    [profile.city, profile.state, profile.country].filter(Boolean).join(", ");

  const birthPlaceDisplay =
    formatProfileBirthPlace(profile, lookupMaps) ||
    profile.birth_place ||
    [profile.birth_city, profile.birth_state, profile.birth_country].filter(Boolean).join(", ");

  const contactCounts = countContactTypes(contacts);

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
        .sug-item:hover { background: #fff2ef !important; color: #D73C2E !important; }
        .profile-tab:focus, .profile-tab:active { outline: none !important; box-shadow: none !important; }
        .profile-tab::-moz-focus-inner { border: 0; }
        @media (max-width: 980px) {
          .profile-return-bar {
            align-items: flex-start !important;
            flex-direction: column !important;
            padding-right: 22px !important;
          }
          .profile-return-back {
            position: static !important;
          }
          .profile-hero { grid-template-columns: 1fr !important; }
          .profile-actions { align-items: flex-start !important; flex-direction: row !important; flex-wrap: wrap !important; }
          .profile-signal-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .profile-grid-2, .profile-card-grid, .recommendation-preview-grid { grid-template-columns: 1fr !important; }
          .note-stat-grid, .note-composer-grid, .recommendation-search-list { grid-template-columns: 1fr 1fr !important; }
        }
        @media (max-width: 620px) {
          .profile-signal-grid { grid-template-columns: 1fr !important; }
          .profile-tab-content { padding: 20px !important; }
          .note-stat-grid, .note-composer-grid, .recommendation-search-list, .recommendation-preview-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {showUpload && canAddPhotos && (
        <UploadOverlay
          profileNo={getProfileNumberDisplay(profile)}
          imageFiles={imageFiles}
          primaryIndex={primaryIndex}
          uploading={uploading}
          preparingUploadFiles={preparingUploadFiles}
          uploadStatus={uploadStatus}
          uploadPreparationStatus={uploadPreparationStatus}
          fileInputRef={fileInputRef}
          setPrimaryIndex={setPrimaryIndex}
          handleFileSelect={handleFileSelect}
          removeImage={removeImage}
          closeUpload={closeUpload}
          handleUpload={handleUpload}
        />
      )}

      {showAssignmentReturnBar && (
        <div style={S.topBar}>
          <div style={S.assignmentReturnBar} className="profile-return-bar">
            <div>
              <p style={S.assignmentReturnEyebrow}>Assignment Workspace</p>
              <div style={S.assignmentReturnTabs}>
                <button
                  type="button"
                  style={{ ...S.assignmentReturnTab, ...S.assignmentReturnTabActive }}
                  onClick={typeof onOpenAssignedToMe === "function" ? onOpenAssignedToMe : onBack}
                >
                  Assigned To Me
                </button>
                <button
                  type="button"
                  style={S.assignmentReturnTab}
                  onClick={typeof onOpenAssignedByMe === "function" ? onOpenAssignedByMe : undefined}
                >
                  Assigned By Me
                </button>
              </div>
            </div>

            <button type="button" style={S.backLink} className="profile-return-back" onClick={onBack}>
              {`< ${backLabel}`}
            </button>
          </div>
        </div>
      )}

      {showSearchReturnBar && !showAssignmentReturnBar && (
        <div style={S.topBar}>
          <button type="button" style={S.searchBackButton} onClick={onBack}>
            {`< ${backLabel}`}
          </button>
        </div>
      )}

      <div style={{ ...S.topBar, ...((showAssignmentReturnBar || showSearchReturnBar) ? S.topBarHidden : {}) }}>
        <button style={S.backLink} onClick={onBack}>{`< ${backLabel}`}</button>
      </div>

      <div style={S.wrap}>
        <div style={S.hero} className="profile-hero">
          <div style={S.photoWrap}>
            <ProfilePhoto
              src={canViewPrimaryPhoto ? primaryPhoto : null}
              initials={initials}
              avatarColor={avatarColor}
            />
          </div>

          <div style={S.heroInfo}>
            <div style={S.heroTopLine}>
              <span style={S.eyebrow}>Profile #{getProfileNumberDisplay(profile, "—")}</span>
              <span style={S.statusChip}>{profile.status || "active"}</span>
            </div>

            <h1 style={S.heroName}>{fullName || "Unnamed Profile"}</h1>

            <div style={S.pillRow}>
              {age && <Pill>{age} yrs</Pill>}
              {profile.gender && <Pill>{profile.gender}</Pill>}
              {locationDisplay && <Pill>📍 {locationDisplay}</Pill>}
              {profile.marital_status && <Pill>{profile.marital_status}</Pill>}
              {profile.is_verified && <Pill>Verified</Pill>}
              {profile.referral_source_name && <Pill>Referral: {profile.referral_source_name}</Pill>}
              {(profile.height_display || profile.height_cm) && (
                <Pill>{profile.height_display || `${profile.height_cm} cm`}</Pill>
              )}
              {profile.persona_tier_cache && <Pill>Tier: {profile.persona_tier_cache}</Pill>}
            </div>

            {profile.about_me && <p style={S.heroAbout}>{profile.about_me}</p>}

            {canViewTags && safeArray(profile.tags).length > 0 && (
              <div style={S.tagRow}>
                {profile.tags.map((tag) => (
                  <span key={tag.id || tag.name || tag} style={S.tag}>
                    {tag.name || tag}
                  </span>
                ))}
              </div>
            )}

            <div style={S.actionRow}>
              {canViewPhone && phoneVisible ? (
                <span style={S.phoneRevealed}>
                  <span>📞</span>
                  {primaryMobile || "No phone available"}
                  <button style={S.phoneHideBtn} onClick={() => setPhoneVisible(false)}>Hide</button>
                </span>
              ) : canViewPhone ? (
                <button style={S.phoneBtn} onClick={handleViewPhone} disabled={viewingPhone}>
                  {viewingPhone ? "Loading..." : "View Phone Number"}
                </button>
              ) : null}

              {canViewEmail && emailContact ? (
                emailVisible ? (
                  <span style={S.emailPill}>
                    ✉ {emailContact}
                    <button style={S.phoneHideBtn} onClick={() => setEmailVisible(false)}>Hide</button>
                  </span>
                ) : (
                  <button style={S.phoneBtn} onClick={handleViewEmail} disabled={viewingEmail}>
                    {viewingEmail ? "Loading..." : "View Email"}
                  </button>
                )
              ) : null}
            </div>

            {hasRestrictedContactsNotice && (
              <div style={S.policyNotice}>
                <strong style={S.policyNoticeTitle}>Contact visibility is restricted.</strong>
                <div style={S.policyNoticeBody}>
                  {hiddenContactFields.length > 0 && (
                    <span>Hidden by policy: {hiddenContactFields.join(", ")}.</span>
                  )}
                  {!canViewPhone && !hiddenContactFields.includes("phone") && (
                    <span>Your role cannot reveal phone numbers.</span>
                  )}
                  {!canViewEmail && !hiddenContactFields.includes("email") && (
                    <span>Your role cannot reveal email addresses.</span>
                  )}
                </div>
              </div>
            )}

            {canViewAlerts && openAlerts.length > 0 && (
              <div style={S.heroAlertStack}>
                {openAlerts.slice(0, 2).map((item, index) => (
                  <div
                    key={item.id || `${item.profile_id || profileId}-alert-${index}`}
                    style={{
                      ...S.heroAlertBanner,
                      ...(normalizeAlertSeverity(item.severity) === "high" ? S.heroAlertBannerHigh : {}),
                    }}
                  >
                    <div style={S.heroAlertTitle}>{item.message || labelize(item.alert_type) || "Open alert"}</div>
                    <div style={S.heroAlertMeta}>
                      {[
                        labelize(item.alert_type || "alert"),
                        item.severity ? `${labelize(item.severity)} severity` : "",
                        item.created_at ? `Created ${formatDateTime(item.created_at)}` : "",
                      ]
                        .filter(Boolean)
                        .join(" | ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.heroActions} className="profile-actions">
            {canEdit && (
              <button style={S.editBtn} onClick={() => setShowEdit(true)}>
                ✎ Edit Profile
              </button>
            )}

            {canAddPhotos && (
              <button style={S.addPhotoBtn} onClick={() => setShowUpload(true)}>
                ＋ Add Photos
              </button>
            )}
          </div>
        </div>

        {canViewCompleteness && (
          <CompletenessPanel
            pct={completenessPct}
            missingItems={missingItems}
          />
        )}

        <div style={S.profileSignalGrid} className="profile-signal-grid">
          <SignalCard
            label="Education"
            title={profile.education_summary || highestEducationText(profile.educations) || "Not added"}
            detail={educationDetail(profile.educations)}
            accent="blue"
          />
          <SignalCard
            label="Career"
            title={primaryOccupation?.occupation_details || primaryOccupation?.occupation_type || "Not added"}
            detail={[
              primaryOccupation?.company_name,
              primaryOccupation?.work_profile,
              formatOccupationAmount(primaryOccupation),
            ].filter(Boolean).join(" · ")}
            accent="green"
          />
          <SignalCard
            label="Community"
            title={[profile.caste || profile.caste_name_cache, profile.sakha || profile.sakha_name_cache].filter(Boolean).join(" · ") || "Not added"}
            detail={sampradayDetailsDisplay || sampradayDisplay || "Sampraday not added"}
            accent="orange"
          />
          <SignalCard
            label="Contact Coverage"
            title={`${contacts.length} contact${contacts.length !== 1 ? "s" : ""}`}
            detail={[
              contactCounts.mobile ? `${contactCounts.mobile} mobile` : "",
              contactCounts.email ? `${contactCounts.email} email` : "",
              contactCounts.whatsapp ? `${contactCounts.whatsapp} WhatsApp` : "",
            ].filter(Boolean).join(" · ") || "No contact details"}
            accent="purple"
          />
        </div>

        <div style={S.detailBox}>
          <div style={S.tabBar}>
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                className="profile-tab"
                style={{ ...S.tab, ...(activeTab === tab.id ? S.tabActive : {}) }}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={S.tabContent} className="profile-tab-content" key={activeTab}>
            {visibleTabs.length === 0 && (
              <Empty>No profile detail tabs are available for your role.</Empty>
            )}
            {activeTab === "overview" && (
              <>
                <div style={S.grid2} className="profile-grid-2">
                  <InfoItem label="Gender" value={profile.gender ? labelize(profile.gender) : ""} />
                  <InfoItem label="Date of Birth" value={formatDate(profile.birth_date || profile.dob)} />
                  <InfoItem label="Birth Time" value={toTime(profile.birth_time)} />
                  <InfoItem label="Birth Place" value={birthPlaceDisplay} />
                  <InfoItem label="Current Location" value={locationDisplay} />
                  <InfoItem label="Birth Coordinates" value={formatCoordinatePair(profile.birth_latitude, profile.birth_longitude)} />
                  <InfoItem label="Height" value={profile.height_display || (profile.height_cm ? `${profile.height_cm} cm` : "")} />
                  <InfoItem label="Complexion" value={profile.complexion} />
                  <InfoItem label="Blood Group" value={profile.blood_group} />
                  <InfoItem label="Manglik" value={profile.manglik} />
                  <InfoItem label="Marital Status" value={profile.marital_status_description || profile.marital_status} />
                  <InfoItem label="Has Children" value={profile.has_children ? "Yes" : (showChildrenFields(profile.marital_status) ? "No" : "")} />
                  <InfoItem label="Marital Field Details" value={profile.marital_field_details || profile.children_description} />
                </div>
                <SectionHead>Sakha Info</SectionHead>
                <div style={S.grid2} className="profile-grid-2">
                  <InfoItem label="Caste" value={profile.caste || profile.caste_name_cache} />
                  <InfoItem label="Sakha" value={profile.sakha || profile.sakha_name_cache} />
                  <InfoItem label="Sampraday" value={sampradayDisplay} />
                  <InfoItem label="Sampraday Details" value={sampradayDetailsDisplay} />
                  <InfoItem label="Dadisa Sakha" value={profile.dadisa_sakha_name} />
                  <InfoItem label="Nanisa Sakha" value={profile.nanisa_sakha_name} />
                  <InfoItem label="Mamasa Sakha" value={profile.mamasa_sakha_name} />
                </div>
                <SectionHead>Verification Status</SectionHead>
                <div style={S.grid2} className="profile-grid-2">
                  <InfoItem label="Referral Source" value={profile.referral_source_name} />
                  <InfoItem label="Referral Name" value={profile.referral_name} />
                  <InfoItem label="Verified At" value={formatDateTime(profile.verified_at)} />
                  <InfoItem label="Verified By" value={profile.verified_by_name || profile.verified_by_email || (profile.verified_by ? `User #${profile.verified_by}` : "")} />
                </div>
                <BirthLocationMapPreview
                  latitude={profile.birth_latitude}
                  longitude={profile.birth_longitude}
                />
              </>
            )}

            {activeTab === "contacts" && (
              <div>
                <div style={S.policyHint}>
                  Reveals and copy actions for phone and email are recorded in the audit trail.
                </div>
                <SectionHead>Contact Details</SectionHead>
                {contacts.length > 0 ? (
                  <div style={S.cardGrid} className="profile-card-grid">
                    {contacts.map((contact, index) => (
                      <ContactCard
                        key={contact.id || index}
                        contact={contact}
                        profileId={profileId}
                        authHeaders={authHeaders}
                        canCopyPhone={canCopyPhone}
                        canCopyEmail={canCopyEmail}
                      />
                    ))}
                  </div>
                ) : (
                  <Empty>No contact details added.</Empty>
                )}

                {false && <SectionHead>Identifiers</SectionHead>}
                {false ? (
                  <div style={S.cardGrid} className="profile-card-grid">
                    {profile.identifiers.map((identifier, index) => (
                      <SubCard key={identifier.id || index}>
                        <div style={S.subCardTitle}>{labelize(identifier.identifier_type)}</div>
                        <div style={S.subCardSub}>
                          {[identifier.identifier_value, identifier.source, identifier.is_active === false ? "Inactive" : "Active"]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </SubCard>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {activeTab === "career" && (
              <div>
                <SectionHead>Education</SectionHead>
                {safeArray(profile.educations).length > 0 ? (
                  profile.educations.map((edu, index) => (
                    <SubCard key={edu.id || index}>
                      <div style={S.subCardTitle}>
                        {edu.degree_name || edu.education_description || "Education"}
                        {edu.is_highest_degree && <span style={S.highestBadge}>Highest</span>}
                        {String(edu.education_status || "").toLowerCase() === "pursuing" && <span style={S.highestBadge}>Pursuing</span>}
                      </div>
                      <div style={S.subCardSub}>
                        {[edu.education_category, edu.institution_name, edu.education_location, edu.passing_year]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </SubCard>
                  ))
                ) : (
                  <Empty>No education details added.</Empty>
                )}

                <div style={{ height: 24 }} />

                <SectionHead>Career</SectionHead>
                {occupations.length > 0 ? (
                  occupations.map((occupation, index) => (
                    <SubCard key={occupation.id || index}>
                      <div style={S.subCardTitle}>
                        {occupation.occupation_details || occupation.occupation_type || `Occupation ${index + 1}`}
                        {occupation.is_primary && <span style={S.highestBadge}>Primary</span>}
                      </div>
                      <div style={S.subCardSub}>
                        {[
                          occupation.occupation_type,
                          occupation.company_name,
                          occupation.work_profile,
                          formatOccupationAmount(occupation),
                        ].filter(Boolean).join(" · ")}
                      </div>
                    </SubCard>
                  ))
                ) : (
                  <Empty>No career details added.</Empty>
                )}
              </div>
            )}

            {activeTab === "family" && (
              <div>
                <SectionHead>Family</SectionHead>
                {familyMembers(profile.relatives).length > 0 ? (
                  familyMembers(profile.relatives).map((relative, index) => (
                    <RelativeCard key={relative.id || index} relative={relative} />
                  ))
                ) : (
                  <Empty>No family details added.</Empty>
                )}
              </div>
            )}

            {activeTab === "relatives" && (
              <div>
                <SectionHead>Relatives</SectionHead>
                {sideRelatives(profile.relatives).length > 0 ? (
                  sideRelatives(profile.relatives).map((relative, index) => (
                    <RelativeCard key={relative.id || index} relative={relative} showFamilySide />
                  ))
                ) : (
                  <Empty>No relatives added.</Empty>
                )}
              </div>
            )}

            {activeTab === "addresses" && (
              <div>
                <SectionHead>Addresses</SectionHead>
                {safeArray(profile.addresses).length > 0 ? (
                  <div style={S.cardGrid} className="profile-card-grid">
                    {profile.addresses.map((address, index) => (
                      <SubCard key={address.id || index}>
                        {(() => {
                          const visaInfo = getAddressVisaInfo(address, lookupMaps);
                          return (
                            <>
                        <div style={S.subCardTitle}>{labelize(address.address_type || "Address")}</div>
                        <div style={S.subCardSub}>
                          {formatAddress(address, profile, lookupMaps)}
                        </div>
                        {visaInfo?.visaStatus && (
                          <div style={S.metaLine}>
                            Visa Status: {visaInfo.visaStatus}
                          </div>
                        )}
                        {visaInfo?.visaName && (
                          <div style={S.metaLine}>
                            Visa Name: {visaInfo.visaName}
                          </div>
                        )}
                            </>
                          );
                        })()}
                      </SubCard>
                    ))}
                  </div>
                ) : (
                  <Empty>No address records added.</Empty>
                )}
              </div>
            )}

            {activeTab === "preferences" && (
              <div>
                {profile.preferences ? (
                  <div style={S.grid2} className="profile-grid-2">
                    <InfoItem
                      label="Preferred Age Difference"
                      value={
                        formatPreferredAgeDifference(profile.preferences) ||
                        (
                          profile.preferences.preferred_age_min && profile.preferences.preferred_age_max
                            ? `${profile.preferences.preferred_age_min} – ${profile.preferences.preferred_age_max} years`
                            : ""
                        )
                      }
                    />
                    <InfoItem
                      label="Preferred Occupation Types"
                      value={safeArray(profile.preferences.preferred_occupation_types).join(", ")}
                    />
                    <InfoItem
                      label="Non Preferred Occupation Types"
                      value={safeArray(profile.preferences.non_preferred_occupation_types).join(", ")}
                    />
                    <InfoItem
                      label="Preferred Cities"
                      value={formatPreferenceCityIds(profile.preferences.preferred_city_ids, lookupMaps.locations)}
                    />
                    <InfoItem
                      label="Non Preferred Cities"
                      value={formatPreferenceCityIds(profile.preferences.non_preferred_city_ids, lookupMaps.locations)}
                    />
                    <InfoItem
                      label="Preferred Castes"
                      value={formatPreferenceIds(profile.preferences.preferred_caste_ids, lookupMaps.castes)}
                    />
                    <InfoItem
                      label="Non Preferred Sakhas"
                      value={formatPreferenceIds(profile.preferences.non_preferred_sakha_ids, lookupMaps.sakhas)}
                    />
                    <InfoItem
                      label="Preferred Education Categories"
                      value={formatPreferenceIds(profile.preferences.preferred_education_category_ids, lookupMaps.educationCategories)}
                    />
                    <InfoItem
                      label="Non Preferred Education Categories"
                      value={formatPreferenceIds(profile.preferences.non_preferred_education_category_ids, lookupMaps.educationCategories)}
                    />
                    <InfoItem label="Preferred Degrees" value={safeArray(profile.preferences.preferred_degrees).join(", ")} />
                    <InfoItem label="Non Preferred Degrees" value={safeArray(profile.preferences.non_preferred_degrees).join(", ")} />
                    <InfoItem label="Notes" value={profile.preferences.notes} />
                  </div>
                ) : (
                  <Empty>No partner preferences specified.</Empty>
                )}
              </div>
            )}

            {activeTab === "media" && (
              <div>
                {canViewPhotos && (
                  <>
                    <SectionHead>Photos</SectionHead>
                    {photos.length > 0 ? (
                      <div style={S.galleryGridInTab}>
                        {photos.map((file, index) => (
                          <GalleryItem
                            key={file.id || index}
                            file={file}
                            onOpen={setSelectedGalleryPhoto}
                            onSetPrimary={handleSetPrimary}
                            isSettingPrimary={settingPrimaryId === file.id}
                            canSetPrimary={canSetPrimaryPhoto}
                          />
                        ))}
                      </div>
                    ) : (
                      <Empty>No photos uploaded.</Empty>
                    )}
                  </>
                )}

                {canViewPhotos && canViewDocuments && <div style={{ height: 24 }} />}

                {canViewDocuments && (
                  <>
                    <SectionHead>Media Links</SectionHead>
                    {mediaLinks.length > 0 ? (
                      <div style={S.cardGrid} className="profile-card-grid">
                        {mediaLinks.map((link, index) => (
                          <SubCard key={link.id || `${link.link_type}-${index}`}>
                            <div style={S.subCardTitle}>{getProfileMediaLinkTypeLabel(link.link_type)}</div>
                            <div style={{ ...S.subCardSub, wordBreak: "break-word" }}>{link.link_url}</div>
                            <div style={S.documentActionRow}>
                              <a
                                href={link.link_url}
                                target="_blank"
                                rel="noreferrer"
                                style={S.inlineLinkButton}
                              >
                                Open Link
                              </a>
                            </div>
                          </SubCard>
                        ))}
                      </div>
                    ) : (
                      <Empty>No media links added.</Empty>
                    )}

                    <div style={{ height: 24 }} />

                    <SectionHead>Documents & Generated Media</SectionHead>
                {documents.length > 0 ? (
                  <div style={S.cardGrid} className="profile-card-grid">
                    {documents.map((doc, index) => (
                      <SubCard key={doc.id || index}>
                        <div style={S.documentCardHeader}>
                          <div style={S.documentCardTitleWrap}>
                            <div style={S.subCardTitle}>
                              {doc.document_type_name || doc.template_key || labelize(doc.media_type)}
                              {doc.is_primary && <span style={S.highestBadge}>Primary</span>}
                            </div>
                          </div>
                          {canViewDocumentHistory && (
                            <button
                              type="button"
                              style={S.documentHistoryButton}
                              onClick={() => openDocumentHistory(doc)}
                            >
                              History
                            </button>
                          )}
                        </div>
                        <div style={S.subCardSub}>
                          {[
                            doc.original_file_name,
                            doc.document_type_name,
                            doc.version_no ? `Version ${doc.version_no}` : "",
                            doc.mime_type,
                            formatFileSize(doc.file_size_bytes),
                            doc.generated_at ? `Generated ${formatDateTime(doc.generated_at)}` : "",
                            doc.is_watermarked ? "Watermarked" : "",
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {(doc.id || canDownloadWatermarkedBiodataDocuments) && (
                          <div style={S.documentActionRow}>
                            <button
                              type="button"
                              style={{
                                ...S.inlineLinkButton,
                                ...(documentActionBusyKey === `inline:${doc.id}` ? S.inlineLinkButtonDisabled : {}),
                              }}
                              onClick={() => handleDocumentFileAction(doc, "inline")}
                              disabled={documentActionBusyKey === `inline:${doc.id}`}
                            >
                              {documentActionBusyKey === `inline:${doc.id}` ? "Opening..." : "Open file"}
                            </button>
                            <button
                              type="button"
                              style={{
                                ...S.inlineLinkButton,
                                ...(documentActionBusyKey === `attachment:${doc.id}` ? S.inlineLinkButtonDisabled : {}),
                              }}
                              onClick={() => handleDocumentFileAction(doc, "attachment")}
                              disabled={documentActionBusyKey === `attachment:${doc.id}`}
                            >
                              {documentActionBusyKey === `attachment:${doc.id}`
                                ? "Preparing..."
                                : isPdfMedia(doc)
                                  ? "Download PDF"
                                  : "Download file"}
                            </button>
                            {canDownloadWatermarkedBiodataDocuments && isBiodataPdfMedia(doc) && (
                              <button
                                type="button"
                                style={{
                                  ...S.inlineLinkButton,
                                  ...(watermarkedDocumentBusyId === doc.id ? S.inlineLinkButtonDisabled : {}),
                                }}
                                onClick={() => openWatermarkedDownloadOptions(doc)}
                                disabled={watermarkedDocumentBusyId === doc.id}
                              >
                                {watermarkedDocumentBusyId === doc.id ? "Preparing..." : "Download Watermarked PDF"}
                              </button>
                            )}
                          </div>
                        )}
                      </SubCard>
                    ))}
                  </div>
                ) : (
                  <Empty>No documents or generated media added.</Empty>
                )}
                  </>
                )}
              </div>
            )}

            {activeTab === "recommendations" && (
              <div>
                {canViewRecommendationsTab && (
                  <ProfileRecommendationsSection
                    profile={profile}
                    recommendations={recommendations}
                    recommendationsLoading={recommendationsLoading}
                    recommendationMeta={recommendationMeta}
                    recommendationSearch={recommendationSearch}
                    recommendationSearchMode={recommendationSearchMode}
                    setRecommendationSearch={setRecommendationSearch}
                    setRecommendationSearchMode={setRecommendationSearchMode}
                    recommendationResults={recommendationResults}
                    recommendationSearchLoading={recommendationSearchLoading}
                    recommendationSearchError={recommendationSearchError}
                    recommendationSearchFeedback={recommendationSearchFeedback}
                    selectedRecommendationTargets={selectedRecommendationTargets}
                    recommendationNotesByTarget={recommendationNotesByTarget}
                    savingRecommendation={savingRecommendation}
                    canGiveRecommendations={canGiveRecommendations}
                    canViewRecommendations={canViewRecommendations}
                    canEditRecommendations={canEditRecommendations}
                    canDownloadRecommendationBiodataPermission={canDownloadRecommendationBiodataPermission}
                    recommendationOutcomeDrafts={recommendationOutcomeDrafts}
                    recommendationOutcomeBusyId={recommendationOutcomeBusyId}
                    recommendationDownloadBusyId={recommendationDownloadBusyId}
                    onSelectTarget={handleSelectRecommendationTarget}
                    onRemoveTarget={handleRemoveRecommendationTarget}
                    onTargetNoteChange={handleRecommendationTargetNoteChange}
                    onCreateRecommendation={handleCreateRecommendation}
                    onOutcomeDraftChange={handleRecommendationOutcomeDraftChange}
                    onSaveOutcome={handleSaveRecommendationOutcome}
                    onDownloadBiodata={handleDownloadRecommendationBiodata}
                  />
                )}
              </div>
            )}

            {activeTab === "crm" && (
              <div>
                {canViewAlerts && (
                  <>
                    <SectionHead>Profile Alerts</SectionHead>
                    {alerts.length > 0 ? (
                      alerts.map((alertItem, index) => (
                        <AlertCard key={alertItem.id || index} alert={alertItem} />
                      ))
                    ) : (
                      <Empty>No alerts for this profile.</Empty>
                    )}

                    <div style={{ height: 24 }} />
                  </>
                )}

                <SectionHead>Persona</SectionHead>
                {safeArray(profile.persona).length > 0 ? (
                  profile.persona.map((persona, index) => (
                    <SubCard key={persona.id || index}>
                      <div style={S.subCardTitle}>{labelize(persona.tier)} Package</div>
                      <div style={S.subCardSub}>
                        {[
                          persona.status,
                          persona.completeness_pct != null ? `${persona.completeness_pct}% complete` : "",
                          persona.payment_amount != null ? `₹${persona.payment_amount}` : "",
                          persona.valid_until ? `Valid until ${formatDate(persona.valid_until)}` : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </SubCard>
                  ))
                ) : (
                  <Empty>No persona package assigned.</Empty>
                )}

                <div style={{ height: 24 }} />

                <SectionHead>Payment History</SectionHead>
                {safeArray(profile.payments).length > 0 ? (
                  profile.payments.map((payment, index) => (
                    <SubCard key={payment.id || index}>
                      <div style={S.subCardTitle}>
                        ₹{payment.amount} · {labelize(payment.payment_status)}
                      </div>
                      <div style={S.subCardSub}>
                        {[payment.payment_method, payment.transaction_ref, formatDate(payment.paid_at)].filter(Boolean).join(" · ")}
                      </div>
                    </SubCard>
                  ))
                ) : (
                  <Empty>No payments added.</Empty>
                )}
              </div>
            )}
          </div>
        </div>

        {(canViewNotes || canAddNotes) && (
          <StructuredNotesSection
            notes={notes}
            recommendationNoteContextMap={recommendationNoteContextMap}
            newRemark={newRemark}
            setNewRemark={setNewRemark}
            addingRemark={addingRemark}
            notesLoading={notesLoading}
            canViewNotes={canViewNotes}
            canAddNotes={canAddNotes}
            noteMeta={noteMeta}
            noteDraft={noteDraft}
            noteFilters={noteFilters}
            followUpSourceNote={followUpSourceNote}
            resolveFollowUpSource={resolveFollowUpSource}
            rescheduleDraft={rescheduleDraft}
            noteActionBusyId={noteActionBusyId}
            dueFollowUpCount={dueFollowUpCount}
            scheduledFollowUpCount={scheduledFollowUpCount}
            communicationCount={communicationCount}
            feedbackCount={feedbackCount}
            handleRemarkChange={handleRemarkChange}
            handleSuggestionClick={handleSuggestionClick}
            handleAddRemark={handleAddRemark}
            handleStartFollowUp={handleStartFollowUp}
            handleStartReschedule={handleStartReschedule}
            handleSaveReschedule={handleSaveReschedule}
            handleCompleteFollowUp={handleCompleteFollowUp}
            handleCancelFollowUp={handleCancelFollowUp}
            handleDraftFieldChange={handleDraftFieldChange}
            handleFilterChange={handleFilterChange}
            setResolveFollowUpSource={setResolveFollowUpSource}
            setRescheduleDraft={setRescheduleDraft}
            resetDraft={resetDraft}
            showSuggestions={showSuggestions}
            setShowSuggestions={setShowSuggestions}
            filteredSuggestions={filteredSuggestions}
            hoveredSuggestion={hoveredSuggestion}
            setHoveredSuggestion={setHoveredSuggestion}
            remarkInputRef={remarkInputRef}
          />
        )}

        {documentHistoryOpen && typeof document !== "undefined" && createPortal(
          <div
            style={S.biodataModalBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeDocumentHistory();
              }
            }}
          >
            <div style={S.documentHistoryModalCard}>
              <div style={S.biodataModalHeader}>
                <div>
                  <p style={S.overlayEyebrow}>Document History</p>
                  <h2 style={S.overlayTitle}>
                    {getDocumentDisplayName(documentHistoryMeta || documentHistoryDocument)}
                  </h2>
                  <p style={S.watermarkModalText}>
                    Download and open activity for this file, including who accessed it and when.
                  </p>
                </div>
                <button type="button" style={S.overlayClose} onClick={closeDocumentHistory}>
                  X
                </button>
              </div>

              <div style={S.documentHistoryBody}>
                {documentHistoryLoading ? (
                  <div style={S.watermarkEmptyState}>Loading history...</div>
                ) : documentHistoryError ? (
                  <div style={S.biodataOptionError}>{documentHistoryError}</div>
                ) : documentHistoryEntries.length > 0 ? (
                  <div style={S.documentHistoryList}>
                    {documentHistoryEntries.map((entry) => (
                      <div key={entry.id || `${entry.action}-${entry.created_at}`} style={S.documentHistoryItem}>
                        <div style={S.documentHistoryItemTop}>
                          <strong style={S.documentHistoryActor}>
                            {entry.user_name || entry.user_email || "System"}
                          </strong>
                          <span style={S.documentHistoryActionBadge}>
                            {formatDocumentHistoryAction(entry.action)}
                          </span>
                        </div>
                        <div style={S.documentHistoryItemMeta}>
                          {formatDateTime(entry.created_at)}
                        </div>
                        <div style={S.documentHistoryItemMeta}>
                          {getDocumentHistoryEntryName(entry, documentHistoryMeta || documentHistoryDocument)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={S.watermarkEmptyState}>
                    No document activity has been recorded yet.
                  </div>
                )}
              </div>

              <div style={S.watermarkModalFooter}>
                <div style={S.watermarkActionRow}>
                  <button type="button" style={S.overlayCancel} onClick={closeDocumentHistory}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {biodataOptionModalOpen && typeof document !== "undefined" && createPortal(
          <div
            style={S.biodataModalBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeBiodataOptionModal();
              }
            }}
          >
            <div style={S.biodataModalCard}>
              <div style={S.biodataModalHeader}>
                <div>
                  <p style={S.overlayEyebrow}>Biodata PDF</p>
                  <h2 style={S.overlayTitle}>Choose What To Include</h2>
                  <p style={S.watermarkModalText}>
                    Choose the sections and images to include. We&apos;ll generate the PDF using only the checked items.
                  </p>
                </div>
                <button type="button" style={S.overlayClose} onClick={closeBiodataOptionModal}>
                  x
                </button>
              </div>

              <div style={S.biodataOptionList}>
                {biodataOptionModalLoading ? (
                  <div style={S.watermarkEmptyState}>Preparing biodata options...</div>
                ) : biodataOptionList.length > 0 ? (
                  biodataOptionList.map((option) => (
                    <label key={option.id} style={S.watermarkOptionRow}>
                      <input
                        type="checkbox"
                        checked={biodataOptionSelections[option.id] === true}
                        onChange={(event) =>
                          handleBiodataOptionSelectionChange(option.id, event.target.checked)
                        }
                      />
                      <span>{option.label}</span>
                    </label>
                  ))
                ) : (
                  <div style={S.watermarkEmptyState}>
                    No biodata sections are available on this profile.
                  </div>
                )}
                {biodataOptionModalError && (
                  <div style={S.biodataOptionError}>{biodataOptionModalError}</div>
                )}
              </div>

              <div style={S.watermarkModalFooter}>
                <div style={S.watermarkFileHint}>
                  {pendingBiodataProfile ? buildProfileBiodataFileName(pendingBiodataProfile) : "JKM_PROFILE_BIO_DATA.pdf"}
                </div>
                <div style={S.watermarkActionRow}>
                  <button type="button" style={S.overlayCancel} onClick={closeBiodataOptionModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...S.overlaySubmit,
                      ...(recommendationDownloadBusyId === pendingBiodataRecommendation?.id || biodataOptionModalLoading
                        ? S.overlaySubmitDisabled
                        : {}),
                    }}
                    onClick={handleConfirmRecommendationBiodataDownload}
                    disabled={
                      recommendationDownloadBusyId === pendingBiodataRecommendation?.id ||
                      biodataOptionModalLoading ||
                      biodataOptionList.length === 0
                    }
                  >
                    {recommendationDownloadBusyId === pendingBiodataRecommendation?.id
                      ? "Preparing..."
                      : "Download PDF"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {false && (
          <div
            style={S.watermarkModalBackdrop}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                closeWatermarkedDownloadOptions();
              }
            }}
          >
            <div style={S.watermarkModalCard}>
              <div style={S.watermarkModalHeader}>
                <div>
                  <p style={S.overlayEyebrow}>Watermarked Biodata</p>
                  <h2 style={S.overlayTitle}>Choose What To Remove</h2>
                  <p style={S.watermarkModalText}>
                    Select the sections to hide before we add the watermark and download the PDF.
                  </p>
                </div>
                <button type="button" style={S.overlayClose} onClick={closeWatermarkedDownloadOptions}>
                  ×
                </button>
              </div>

              <div style={S.watermarkOptionList}>
                {availableWatermarkRemovalOptions.length > 0 ? (
                  availableWatermarkRemovalOptions.map((option) => (
                    <label key={option.id} style={S.watermarkOptionRow}>
                      <input
                        type="checkbox"
                        checked={watermarkedDownloadSelections[option.id] === true}
                        onChange={(event) => handleWatermarkedSelectionChange(option.id, event.target.checked)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))
                ) : (
                  <div style={S.watermarkEmptyState}>
                    No removable biodata sections were found for this profile. The PDF will be downloaded with watermark only.
                  </div>
                )}
              </div>

              <div style={S.watermarkModalFooter}>
                <div style={S.watermarkFileHint}>
                  {buildWatermarkedBiodataFileName(profile)}
                </div>
                <div style={S.watermarkActionRow}>
                  <button type="button" style={S.overlayCancel} onClick={closeWatermarkedDownloadOptions}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{
                      ...S.overlaySubmit,
                      ...(watermarkedDocumentBusyId === pendingWatermarkedDocument?.id ? S.overlaySubmitDisabled : {}),
                    }}
                    onClick={() => handleDownloadWatermarkedDocument(pendingWatermarkedDocument)}
                    disabled={watermarkedDocumentBusyId === pendingWatermarkedDocument?.id}
                  >
                    {watermarkedDocumentBusyId === pendingWatermarkedDocument?.id ? "Preparing..." : "Download PDF"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {canAddPhotos && (
          <>
            {canViewPhotos && photos.length > 0 ? (
              <div style={S.gallery}>
                <div style={S.galleryHeader}>
                  <p style={S.galleryTitle}>Photos ({photos.length})</p>
                  <button style={S.galleryAddBtn} onClick={() => setShowUpload(true)}>＋ Add More</button>
                </div>
                <div style={S.galleryHelp}>
                  {canSetPrimaryPhoto ? "Click any photo to view it. You can set primary from the preview." : "Photo uploads are allowed for your role."}
                </div>
                <div style={S.galleryGrid}>
                  {photos.map((file, index) => (
                    <GalleryItem
                      key={file.id || index}
                      file={file}
                      onOpen={setSelectedGalleryPhoto}
                      onSetPrimary={handleSetPrimary}
                      isSettingPrimary={settingPrimaryId === file.id}
                      canSetPrimary={canSetPrimaryPhoto}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div style={S.noPhotoNudge}>
                <span style={S.noPhotoIcon}>◈</span>
                <p style={S.noPhotoText}>
                  {canViewPhotos ? "No photos uploaded yet" : "You can add photos, but photo viewing is restricted for your role."}
                </p>
                <button style={S.noPhotoBtn} onClick={() => setShowUpload(true)}>Upload Photos</button>
              </div>
            )}
          </>
        )}

        {selectedGalleryPhoto && (
          <PhotoPreviewModal
            file={selectedGalleryPhoto}
            canSetPrimary={canSetPrimaryPhoto}
            isSettingPrimary={settingPrimaryId === selectedGalleryPhoto.id}
            onClose={() => setSelectedGalleryPhoto(null)}
            onSetPrimary={async (fileId) => {
              await handleSetPrimary(fileId);
              setSelectedGalleryPhoto((current) => (
                current && current.id === fileId
                  ? { ...current, is_primary: true }
                  : current
              ));
            }}
          />
        )}

        {selectedDocumentImage && (
          <PhotoPreviewModal
            file={{ ...selectedDocumentImage, file_url: selectedDocumentImage.preview_url || selectedDocumentImage.file_url }}
            canSetPrimary={false}
            isSettingPrimary={false}
            eyebrow="Document Preview"
            title={getDocumentDisplayName(selectedDocumentImage)}
            onClose={() => {
              if (selectedDocumentImage.preview_url) {
                window.URL.revokeObjectURL(selectedDocumentImage.preview_url);
              }
              setSelectedDocumentImage(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function UploadOverlay({
  profileNo,
  imageFiles,
  primaryIndex,
  uploading,
  preparingUploadFiles,
  uploadStatus,
  uploadPreparationStatus,
  fileInputRef,
  setPrimaryIndex,
  handleFileSelect,
  removeImage,
  closeUpload,
  handleUpload,
}) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div style={S.overlayBackdrop} onClick={(e) => { if (e.target === e.currentTarget) closeUpload(); }}>
      <div style={S.overlayCard}>
        <div style={S.overlayHeader}>
          <div>
            <p style={S.overlayEyebrow}>{profileNo ? `Profile #${profileNo}` : "Photo Upload"}</p>
            <h2 style={S.overlayTitle}>Add Photos</h2>
          </div>
          <button style={S.overlayClose} onClick={closeUpload} disabled={uploading || preparingUploadFiles}>x</button>
        </div>

        <div
          style={{
            ...S.dropZone,
            ...(preparingUploadFiles ? S.processingDropZone : {}),
          }}
          onClick={() => {
            if (!preparingUploadFiles) fileInputRef.current?.click();
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            style={{ display: "none" }}
            onChange={handleFileSelect}
            disabled={preparingUploadFiles}
          />
          <span style={S.dropIcon}>＋</span>
          <span style={S.dropText}>{preparingUploadFiles ? "Preparing photos..." : "Click to select photos"}</span>
          <span style={S.dropSub}>JPEG · PNG · WEBP · Max {MAX_UPLOAD_FILE_SIZE_LABEL} after compression · {imageFiles.length}/10 selected</span>
          {preparingUploadFiles ? (
            <span style={S.processingInlineNote}>
              <span style={S.processingSpinner} />
              {uploadPreparationStatus || "Compressing selected photos..."}
            </span>
          ) : null}
        </div>

        {imageFiles.length > 0 && (
          <>
            <p style={S.overlayHint}>Click a photo to set it as primary.</p>
            <div style={S.previewGrid}>
              {imageFiles.map((img, index) => (
                <div
                  key={index}
                  style={{
                    ...S.previewCard,
                    ...(index === primaryIndex ? S.previewCardPrimary : {}),
                  }}
                  onClick={() => setPrimaryIndex(index)}
                >
                  <img src={img.previewUrl} alt={img.name} style={S.previewImg} />
                  {index === primaryIndex && <div style={S.primaryBadge}>★ Primary</div>}
                  <button
                    type="button"
                    style={S.previewRemove}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(index);
                    }}
                  >
                    x
                  </button>
                  <div style={S.previewName}>{img.name}</div>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={S.overlayFooter}>
          {preparingUploadFiles ? (
            <span style={S.uploadStatus}>
              <span style={S.processingSpinner} />
              {uploadPreparationStatus || "Preparing files..."}
            </span>
          ) : uploadStatus ? (
            <span style={S.uploadStatus}>{uploadStatus}</span>
          ) : (
            <span style={S.uploadHint}>
              {imageFiles.length === 0
                ? "No photos selected yet"
                : `${imageFiles.length} photo${imageFiles.length > 1 ? "s" : ""} ready`}
            </span>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button style={S.overlayCancel} onClick={closeUpload} disabled={uploading || preparingUploadFiles}>Cancel</button>
            <button
              style={{
                ...S.overlaySubmit,
                ...(imageFiles.length === 0 || uploading || preparingUploadFiles ? S.overlaySubmitDisabled : {}),
              }}
              onClick={handleUpload}
              disabled={imageFiles.length === 0 || uploading || preparingUploadFiles}
            >
              {uploading ? "Uploading..." : imageFiles.length === 1 ? "Upload 1 Photo" : `Upload ${imageFiles.length || ""} Photos`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// eslint-disable-next-line no-unused-vars
function NotesSection({
  notes,
  newRemark,
  addingRemark,
  canViewNotes,
  canAddNotes,
  handleRemarkChange,
  handleSuggestionClick,
  handleAddRemark,
  showSuggestions,
  setShowSuggestions,
  filteredSuggestions,
  hoveredSuggestion,
  setHoveredSuggestion,
  remarkInputRef,
}) {
  const visibleNotes = canViewNotes ? notes : [];

  return (
    <div style={S.remarkBox}>
      <div style={S.remarkHeader}>
        <div>
          <p style={S.remarkEyebrow}>Internal</p>
          <h3 style={S.remarkTitle}>Admin Notes</h3>
        </div>
        <span style={S.remarkCount}>{visibleNotes.length} note{visibleNotes.length !== 1 ? "s" : ""}</span>
      </div>

      {canAddNotes && <div style={S.remarkInputWrap}>
        <div style={{ position: "relative" }}>
          <textarea
            ref={remarkInputRef}
            placeholder="Write a note or pick from suggestions..."
            value={newRemark}
            onChange={handleRemarkChange}
            onFocus={() => {
              setShowSuggestions(true);
            }}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            style={S.remarkInput}
          />

          {showSuggestions && (
            <div style={S.suggestionBox}>
              <div style={S.suggestionHeader}>Quick suggestions</div>
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion}
                  className="sug-item"
                  style={{
                    ...S.suggestionItem,
                    ...(hoveredSuggestion === index ? S.suggestionItemHovered : {}),
                  }}
                  onMouseEnter={() => setHoveredSuggestion(index)}
                  onMouseLeave={() => setHoveredSuggestion(null)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <span style={S.suggestionDot}>›</span> {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.remarkActions}>
          <span style={S.remarkCharCount}>{newRemark.length > 0 ? `${newRemark.length} chars` : ""}</span>
          <button
            onClick={handleAddRemark}
            style={{ ...S.remarkBtn, ...(addingRemark || !newRemark.trim() ? S.remarkBtnDisabled : {}) }}
            disabled={addingRemark || !newRemark.trim()}
          >
            {addingRemark ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>}

      <div style={S.remarkList}>
        {!canViewNotes ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Note</span>
            <p style={S.remarkEmptyText}>Saved notes are hidden for your role.</p>
          </div>
        ) : visibleNotes.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>📝</span>
            <p style={S.remarkEmptyText}>No notes yet. Add the first one above.</p>
          </div>
        ) : (
          visibleNotes.map((note, index) => (
            <div key={note.id || index} style={{ ...S.remarkItem, ...(index === 0 ? S.remarkItemFirst : {}) }}>
              <div style={S.remarkItemLeft}>
                <div style={S.remarkAvatar}>
                  {(note.created_by_email || note.created_by_name || "A")[0].toUpperCase()}
                </div>
              </div>
              <div style={S.remarkItemRight}>
                <div style={S.remarkItemTop}>
                  <span style={S.remarkAuthor}>
                    {note.created_by_email || note.created_by_name || "Admin"}
                  </span>
                  <span style={S.remarkTime}>{formatRelativeTime(note.created_at)}</span>
                </div>
                <p style={S.remarkText}>{note.content || note.note}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function StructuredNotesSection({
  notes,
  recommendationNoteContextMap,
  newRemark,
  setNewRemark,
  addingRemark,
  notesLoading,
  canViewNotes,
  canAddNotes,
  noteMeta,
  noteDraft,
  noteFilters,
  followUpSourceNote,
  resolveFollowUpSource,
  rescheduleDraft,
  noteActionBusyId,
  dueFollowUpCount,
  scheduledFollowUpCount,
  communicationCount,
  feedbackCount,
  handleRemarkChange,
  handleSuggestionClick,
  handleAddRemark,
  handleStartFollowUp,
  handleStartReschedule,
  handleSaveReschedule,
  handleCompleteFollowUp,
  handleCancelFollowUp,
  handleDraftFieldChange,
  handleFilterChange,
  setResolveFollowUpSource,
  setRescheduleDraft,
  resetDraft,
  showSuggestions,
  setShowSuggestions,
  filteredSuggestions,
  hoveredSuggestion,
  setHoveredSuggestion,
  remarkInputRef,
}) {
  const visibleNotes = canViewNotes ? notes : [];
  const noteRecommendationMap =
    recommendationNoteContextMap instanceof Map ? recommendationNoteContextMap : new Map();
  const [currentNotesPage, setCurrentNotesPage] = useState(1);
  const totalNotePages = Math.max(1, Math.ceil(visibleNotes.length / NOTES_PAGE_SIZE));
  const paginatedNotes = visibleNotes.slice(
    (currentNotesPage - 1) * NOTES_PAGE_SIZE,
    currentNotesPage * NOTES_PAGE_SIZE
  );
  const pageButtonStart = Math.max(1, currentNotesPage - 2);
  const pageButtonEnd = Math.min(totalNotePages, pageButtonStart + 4);
  const notePageButtons = [];

  for (let page = Math.max(1, pageButtonEnd - 4); page <= pageButtonEnd; page += 1) {
    notePageButtons.push(page);
  }

  useEffect(() => {
    setCurrentNotesPage(1);
  }, [
    visibleNotes.length,
    noteFilters.note_type,
    noteFilters.outcome,
    noteFilters.follow_up,
    noteFilters.follow_up_status,
  ]);

  useEffect(() => {
    if (currentNotesPage > totalNotePages) {
      setCurrentNotesPage(totalNotePages);
    }
  }, [currentNotesPage, totalNotePages]);

  return (
    <div style={S.remarkBox}>
      <div style={S.remarkHeader}>
        <div>
          <p style={S.remarkEyebrow}>Internal</p>
          <h3 style={S.remarkTitle}>Admin Notes</h3>
          <p style={S.remarkSubhead}>Structured CRM logging for calls, meetings, match sharing, and feedback.</p>
        </div>
        <span style={S.remarkCount}>{visibleNotes.length} note{visibleNotes.length !== 1 ? "s" : ""}</span>
      </div>

      <div style={S.noteStatGrid} className="note-stat-grid">
        <MetricCard label="Logged" value={visibleNotes.length} hint="Total timeline rows" />
        <MetricCard label="Communication" value={communicationCount} hint="Calls, meetings, shares" />
        <MetricCard label="Due Follow-ups" value={dueFollowUpCount} hint="Needs attention now" accent="alert" />
        <MetricCard label="Feedback" value={feedbackCount} hint={`${scheduledFollowUpCount} scheduled follow-ups`} />
      </div>

      {canAddNotes && (
        <div style={S.remarkInputWrap}>
          {followUpSourceNote && (
            <div style={S.followUpContextCard}>
              <div style={{ minWidth: 0 }}>
                <div style={S.followUpContextTitle}>Following up on an existing note</div>
                <div style={S.followUpContextMeta}>
                  {[followUpSourceNote.created_by_email || followUpSourceNote.created_by_name || "Admin", formatDateTime(followUpSourceNote.created_at)]
                    .filter(Boolean)
                    .join(" | ")}
                </div>
                <div style={S.followUpContextBody}>{followUpSourceNote.content || followUpSourceNote.note}</div>
              </div>
              <div style={S.followUpContextActions}>
                <label style={S.followUpResolveLabel}>
                  <input
                    type="checkbox"
                    checked={resolveFollowUpSource}
                    onChange={(event) => setResolveFollowUpSource(event.target.checked)}
                  />
                  Mark previous follow-up complete
                </label>
                <button
                  type="button"
                  style={S.noteGhostBtn}
                  onClick={() => {
                    setNewRemark("");
                    resetDraft();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={S.noteComposerGrid} className="note-composer-grid">
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Interaction Type</label>
              <select style={S.noteSelect} value={noteDraft.note_type} onChange={handleDraftFieldChange("note_type")}>
                {noteMeta.note_types.map((item) => (
                  <option key={item} value={item}>{labelize(item)}</option>
                ))}
              </select>
            </div>
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Result</label>
              <select style={S.noteSelect} value={noteDraft.outcome} onChange={handleDraftFieldChange("outcome")}>
                <option value="">No outcome yet</option>
                {noteMeta.outcomes.filter((item) => item !== "follow_up_required").map((item) => (
                  <option key={item} value={item}>{labelize(item)}</option>
                ))}
              </select>
            </div>
            <div style={S.noteField}>
              <label style={S.noteFieldLabel}>Next Follow-up</label>
              <input
                type="datetime-local"
                style={S.noteInput}
                value={noteDraft.follow_up_at}
                onChange={handleDraftFieldChange("follow_up_at")}
              />
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <textarea
              ref={remarkInputRef}
              placeholder="Write a structured CRM note or pick a suggestion..."
              value={newRemark}
              onChange={handleRemarkChange}
              onFocus={() => {
                setShowSuggestions(filteredSuggestions.length > 0);
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              style={S.remarkInput}
            />

            {showSuggestions && filteredSuggestions.length > 0 && (
              <div style={S.suggestionBox}>
                <div style={S.suggestionHeader}>Suggested note starters</div>
                {filteredSuggestions.slice(0, 10).map((suggestion, index) => (
                  <div
                    key={suggestion}
                    className="sug-item"
                    style={{
                      ...S.suggestionItem,
                      ...(hoveredSuggestion === index ? S.suggestionItemHovered : {}),
                    }}
                    onMouseEnter={() => setHoveredSuggestion(index)}
                    onMouseLeave={() => setHoveredSuggestion(null)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <span style={S.suggestionDot}></span> {suggestion}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.remarkActions}>
            <span style={S.remarkCharCount}>{newRemark.length > 0 ? `${newRemark.length} chars` : ""}</span>
            <div style={S.noteActionRow}>
              <button
                type="button"
                style={S.noteGhostBtn}
                onClick={() => {
                  setNewRemark("");
                  resetDraft();
                }}
              >
                Reset
              </button>
              <button
                onClick={handleAddRemark}
                style={{ ...S.remarkBtn, ...(addingRemark || !newRemark.trim() ? S.remarkBtnDisabled : {}) }}
                disabled={addingRemark || !newRemark.trim()}
              >
                {addingRemark ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      {canViewNotes && (
        <div style={S.noteFiltersBar}>
          <select style={S.noteFilterSelect} value={noteFilters.note_type} onChange={handleFilterChange("note_type")}>
            <option value="all">All types</option>
            {noteMeta.note_types.map((item) => (
              <option key={item} value={item}>{labelize(item)}</option>
            ))}
          </select>
          <select style={S.noteFilterSelect} value={noteFilters.outcome} onChange={handleFilterChange("outcome")}>
            <option value="all">All outcomes</option>
            <option value="none">No outcome</option>
            {noteMeta.outcomes.map((item) => (
              <option key={item} value={item}>{labelize(item)}</option>
            ))}
          </select>
          <select style={S.noteFilterSelect} value={noteFilters.follow_up} onChange={handleFilterChange("follow_up")}>
            <option value="all">All follow-ups</option>
            <option value="scheduled">Scheduled only</option>
            <option value="due">Due now</option>
          </select>
          <select style={S.noteFilterSelect} value={noteFilters.follow_up_status} onChange={handleFilterChange("follow_up_status")}>
            <option value="all">All statuses</option>
            {noteMeta.follow_up_statuses.map((item) => (
              <option key={item} value={item}>{labelize(item)}</option>
            ))}
          </select>
        </div>
      )}

      <div style={S.remarkList}>
        {!canViewNotes ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Note</span>
            <p style={S.remarkEmptyText}>Saved notes are hidden for your role.</p>
          </div>
        ) : notesLoading ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>...</span>
            <p style={S.remarkEmptyText}>Refreshing CRM timeline...</p>
          </div>
        ) : visibleNotes.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Note</span>
            <p style={S.remarkEmptyText}>No notes yet. Add the first one above.</p>
          </div>
        ) : (
          paginatedNotes.map((note, index) => {
            const recommendationContext = noteRecommendationMap.get(String(note.id || ""));
            return (
              <div key={note.id || index} style={{ ...S.remarkItem, ...(index === 0 ? S.remarkItemFirst : {}) }}>
                <div style={S.remarkItemLeft}>
                  <div style={S.remarkAvatar}>
                    {(note.created_by_email || note.created_by_name || "A")[0].toUpperCase()}
                  </div>
                </div>
                <div style={S.remarkItemRight}>
                  <div style={S.remarkItemTop}>
                    <div>
                      <span style={S.remarkAuthor}>
                        {note.created_by_email || note.created_by_name || "Admin"}
                      </span>
                      <div style={S.noteMetaRow}>
                        <span style={S.noteTypeBadge}>{labelize(note.note_type || "general")}</span>
                        {note.outcome && note.outcome !== "follow_up_required" && <span style={S.noteOutcomeBadge}>{labelize(note.outcome)}</span>}
                        {String(note.follow_up_status_effective || "").trim().toLowerCase() !== "none" && (
                          <span
                            style={{
                              ...S.noteStatusBadge,
                              ...(String(note.follow_up_status_effective || "").trim().toLowerCase() === "completed" ? S.noteStatusBadgeCompleted : {}),
                              ...(String(note.follow_up_status_effective || "").trim().toLowerCase() === "cancelled" ? S.noteStatusBadgeCancelled : {}),
                            }}
                          >
                            {labelize(note.follow_up_status_effective)}
                          </span>
                        )}
                        {note.follow_up_at && String(note.follow_up_status_effective || "").trim().toLowerCase() !== "completed" && String(note.follow_up_status_effective || "").trim().toLowerCase() !== "cancelled" && (
                          <span
                            style={{
                              ...S.noteFollowUpBadge,
                              ...(isFollowUpDue(note.follow_up_at) ? S.noteFollowUpBadgeDue : {}),
                            }}
                          >
                            {isFollowUpDue(note.follow_up_at) ? "Due " : "Follow-up "}
                            {formatDateTime(note.follow_up_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <span style={S.remarkTime}>{formatRelativeTime(note.created_at)}</span>
                  </div>
                  {recommendationContext && (
                    <div style={S.noteRecommendationContext}>
                      <span style={S.noteRecommendationBadge}>{recommendationContext.badge}</span>
                      <span style={S.noteRecommendationText}>{recommendationContext.label}</span>
                      {recommendationContext.meta && (
                        <span style={S.noteRecommendationMeta}>{recommendationContext.meta}</span>
                      )}
                    </div>
                  )}
                  <p style={S.remarkText}>{note.content || note.note}</p>
                  {canAddNotes && noteNeedsFollowUp(note) && (
                    <div style={S.noteCardActions}>
                      {rescheduleDraft.noteId === note.id ? (
                        <div style={S.noteInlineActionGroup}>
                          <input
                            type="datetime-local"
                            style={S.noteInlineInput}
                            value={rescheduleDraft.value}
                            onChange={(event) =>
                              setRescheduleDraft((current) => ({ ...current, value: event.target.value }))
                            }
                          />
                          <button
                            type="button"
                            style={S.noteFollowUpBtn}
                            onClick={() => handleSaveReschedule(note)}
                            disabled={!rescheduleDraft.value || noteActionBusyId === note.id}
                          >
                            {noteActionBusyId === note.id ? "Saving..." : "Save"}
                          </button>
                          <button
                            type="button"
                            style={S.noteGhostBtn}
                            onClick={() => setRescheduleDraft({ noteId: null, value: "" })}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={S.noteInlineActionGroup}>
                          <button
                            type="button"
                            style={S.noteFollowUpBtn}
                            onClick={() => handleStartFollowUp(note)}
                          >
                            Follow Up
                          </button>
                          <button
                            type="button"
                            style={S.noteSecondaryBtn}
                            onClick={() => handleStartReschedule(note)}
                          >
                            Reschedule
                          </button>
                          <button
                            type="button"
                            style={S.noteSecondaryBtn}
                            onClick={() => handleCompleteFollowUp(note)}
                            disabled={noteActionBusyId === note.id}
                          >
                            {noteActionBusyId === note.id ? "Working..." : "Complete"}
                          </button>
                          <button
                            type="button"
                            style={S.noteSecondaryBtn}
                            onClick={() => handleCancelFollowUp(note)}
                            disabled={noteActionBusyId === note.id}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {canViewNotes && visibleNotes.length > NOTES_PAGE_SIZE && (
        <div style={S.notePagination}>
          <div style={S.notePaginationInfo}>
            Showing {(currentNotesPage - 1) * NOTES_PAGE_SIZE + 1}
            {" "}to {Math.min(currentNotesPage * NOTES_PAGE_SIZE, visibleNotes.length)}
            {" "}of {visibleNotes.length} notes
          </div>
          <div style={S.notePaginationControls}>
            <button
              type="button"
              style={{
                ...S.notePaginationButton,
                ...(currentNotesPage === 1 ? S.notePaginationButtonDisabled : {}),
              }}
              onClick={() => setCurrentNotesPage((current) => Math.max(1, current - 1))}
              disabled={currentNotesPage === 1}
            >
              Prev
            </button>
            {notePageButtons.map((page) => (
              <button
                key={page}
                type="button"
                style={{
                  ...S.notePaginationButton,
                  ...(page === currentNotesPage ? S.notePaginationButtonActive : {}),
                }}
                onClick={() => setCurrentNotesPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              style={{
                ...S.notePaginationButton,
                ...(currentNotesPage === totalNotePages ? S.notePaginationButtonDisabled : {}),
              }}
              onClick={() => setCurrentNotesPage((current) => Math.min(totalNotePages, current + 1))}
              disabled={currentNotesPage === totalNotePages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileRecommendationsSection({
  profile,
  recommendations,
  recommendationsLoading,
  recommendationMeta,
  recommendationSearch,
  recommendationSearchMode,
  setRecommendationSearch,
  setRecommendationSearchMode,
  recommendationResults,
  recommendationSearchLoading,
  recommendationSearchError,
  recommendationSearchFeedback,
  selectedRecommendationTargets,
  recommendationNotesByTarget,
  savingRecommendation,
  canGiveRecommendations,
  canViewRecommendations,
  canEditRecommendations,
  canDownloadRecommendationBiodataPermission,
  recommendationOutcomeDrafts,
  recommendationOutcomeBusyId,
  recommendationDownloadBusyId,
  onSelectTarget,
  onRemoveTarget,
  onTargetNoteChange,
  onCreateRecommendation,
  onOutcomeDraftChange,
  onSaveOutcome,
  onDownloadBiodata,
}) {
  const visibleRecommendations = canViewRecommendations ? recommendations : [];
  const selectedTargets = Array.isArray(selectedRecommendationTargets) ? selectedRecommendationTargets : [];
  const selectedTargetCount = selectedTargets.length;
  const matchedCount = visibleRecommendations.filter((item) => String(item?.status || "").trim().toLowerCase() === "matched").length;
  const callCount = visibleRecommendations.reduce((total, item) => {
    const historyCalls = Array.isArray(item?.outcomes)
      ? item.outcomes.filter(
          (outcome) => String(outcome?.match_status || "").trim().toLowerCase() === "call"
        ).length
      : 0;
    if (historyCalls > 0) return total + historyCalls;
    return total + (String(item?.status || "").trim().toLowerCase() === "call" ? 1 : 0);
  }, 0);
  const meetingCount = visibleRecommendations.filter((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return status === "meeting_planned" || status === "meeting_done";
  }).length;
  const activeCount = visibleRecommendations.filter((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    return !["matched", "rejected", "cancelled"].includes(status);
  }).length;

  return (
    <div style={S.remarkBox}>
      <div style={S.remarkHeader}>
        <div>
          <p style={S.remarkEyebrow}>CRM</p>
          <h3 style={S.remarkTitle}>Recommendations</h3>
          <p style={S.remarkSubhead}>Recommend one or more profiles from this record, track status changes, and keep every recommendation tied to a CRM note.</p>
        </div>
        <span style={S.remarkCount}>{visibleRecommendations.length} recommendation{visibleRecommendations.length !== 1 ? "s" : ""}</span>
      </div>

      <div style={S.noteStatGrid} className="note-stat-grid recommendation-stat-grid">
        <MetricCard label="Recommended" value={visibleRecommendations.length} hint="Manual recommendations created" />
        <MetricCard label="Calls" value={callCount} hint="Call actions logged" />
        <MetricCard label="Active" value={activeCount} hint="Still in progress" />
        <MetricCard label="Meetings" value={meetingCount} hint="Planned or completed" />
        <MetricCard label="Matched" value={matchedCount} hint="Successful conversions" accent="alert" />
      </div>

      {canGiveRecommendations && (
        <div style={S.remarkInputWrap}>
          <div style={S.recommendationSearchModeRow}>
            <label style={S.recommendationRadioLabel}>
              <input
                type="radio"
                name="recommendation-search-mode"
                value={PROFILE_NO_DISPLAY_SEARCH_KEY}
                checked={isProfileNoDisplaySearchMode(recommendationSearchMode)}
                onChange={(event) => setRecommendationSearchMode(event.target.value)}
              />
              <span>Profile Number</span>
            </label>
            <label style={S.recommendationRadioLabel}>
              <input
                type="radio"
                name="recommendation-search-mode"
                value="name"
                checked={recommendationSearchMode === "name"}
                onChange={(event) => setRecommendationSearchMode(event.target.value)}
              />
              <span>Name</span>
            </label>
          </div>

          <div style={S.noteField}>
            <label style={S.noteFieldLabel}>Recommended Profile</label>
            <input
              type="text"
              style={S.noteInput}
              value={recommendationSearch}
              onChange={(event) => setRecommendationSearch(event.target.value)}
              placeholder={
                canGiveRecommendations
                  ? isProfileNoDisplaySearchMode(recommendationSearchMode)
                    ? "Enter profile number e.g. M123"
                    : "Enter profile name..."
                  : "Recommendation give permission is required"
              }
              disabled={!canGiveRecommendations}
            />
          </div>

          {canGiveRecommendations && (
            <div style={S.recommendationSearchPanel}>
              {recommendationSearchLoading ? (
                <div style={S.recommendationSearchHint}>Searching profiles...</div>
              ) : recommendationSearch.trim().length < (isProfileNoDisplaySearchMode(recommendationSearchMode) ? 1 : 2) ? (
                <div style={S.recommendationSearchHint}>
                  {isProfileNoDisplaySearchMode(recommendationSearchMode)
                    ? "Type at least 1 character to search by profile number."
                    : "Type at least 2 characters to search by name."}
                </div>
              ) : recommendationSearchError ? (
                <div style={S.recommendationSearchError}>{recommendationSearchError}</div>
              ) : recommendationResults.length === 0 ? (
                <div style={S.recommendationSearchHint}>
                  {recommendationSearchFeedback || "No matching profiles available for recommendation."}
                </div>
              ) : (
                <div style={S.recommendationSearchList} className="recommendation-search-list">
                  {recommendationResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      style={S.recommendationSearchItem}
                      onClick={() => onSelectTarget(item)}
                    >
                      <div style={S.recommendationSearchItemInner}>
                        <div style={S.recommendationSearchPhotoWrap}>
                          <ProfilePhoto
                            src={item.photo || item.primary_photo_url_cache || null}
                            initials={getProfileInitials(item)}
                            avatarColor={pickColor(item?.first_name || getProfileNumberDisplay(item) || "R")}
                          />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={S.recommendationSearchName}>{getProfileDisplayName(item) || `Profile #${getProfileNumberDisplay(item) || item.id}`}</div>
                          <div style={S.recommendationSearchMeta}>{buildRecommendationResultMeta(item)}</div>
                        </div>
                        <span style={S.recommendationAddPill}>Add</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {selectedTargetCount > 0 && (
            <div style={S.recommendationSelectedWrap}>
              <div style={S.recommendationSelectedHeader}>
                <div>
                  <div style={S.recommendationSelectedTitle}>Added profiles ({selectedTargetCount})</div>
                  <div style={S.recommendationSelectedHint}>Add notes if needed, or remove a profile before creating.</div>
                </div>
                <button type="button" style={S.noteGhostBtn} onClick={() => onSelectTarget(null)}>
                  Clear all
                </button>
              </div>
              <div style={S.recommendationSelectedGrid}>
                {selectedTargets.map((target) => (
                  <div key={target.id} style={S.recommendationSelectedCard}>
                    <div style={S.recommendationSelectedMain}>
                      <div style={S.recommendationTargetPhotoWrap}>
                        <ProfilePhoto
                          src={target?.photo || target?.primary_photo_url_cache || null}
                          initials={getProfileInitials(target)}
                          avatarColor={pickColor(target?.first_name || getProfileNumberDisplay(target) || "R")}
                        />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={S.recommendationTargetTitle}>{getProfileDisplayName(target) || "Selected profile"}</div>
                        <div style={S.recommendationTargetMeta}>{buildRecommendationResultMeta(target)}</div>
                      </div>
                    </div>
                    <div style={S.recommendationSelectedControls}>
                      <button
                        type="button"
                        style={S.recommendationSelectedRemove}
                        onClick={() => onRemoveTarget(target.id)}
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      style={S.recommendationSelectedNote}
                      value={recommendationNotesByTarget?.[String(target.id)] || ""}
                      onChange={(event) => onTargetNoteChange(target.id, event.target.value)}
                      placeholder="Optional note for this recommendation"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={S.remarkActions}>
            <span style={S.remarkCharCount}>
              {selectedTargetCount ? `Notes are optional for each selected profile.` : "Pick a profile to start"}
            </span>
            <button
              type="button"
              onClick={onCreateRecommendation}
              style={{ ...S.remarkBtn, ...((savingRecommendation || selectedTargetCount === 0) ? S.remarkBtnDisabled : {}) }}
              disabled={savingRecommendation || selectedTargetCount === 0}
            >
              {savingRecommendation
                ? "Saving..."
                : selectedTargetCount > 1
                  ? `Create ${selectedTargetCount} Recommendations`
                  : "Create Recommendation"}
            </button>
          </div>
        </div>
      )}

      <div style={S.remarkList}>
        {!canViewRecommendations ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Link</span>
            <p style={S.remarkEmptyText}>Recommendations are hidden for your role.</p>
          </div>
        ) : recommendationsLoading ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>...</span>
            <p style={S.remarkEmptyText}>Loading recommendations...</p>
          </div>
        ) : visibleRecommendations.length === 0 ? (
          <div style={S.remarkEmpty}>
            <span style={S.remarkEmptyIcon}>Recommend</span>
            <p style={S.remarkEmptyText}>No profile recommendations recorded yet.</p>
          </div>
        ) : (
          visibleRecommendations.map((item, index) => {
            const draft = recommendationOutcomeDrafts[item.id] || { status: "", notes: "", contactSide: recommendationMeta.defaults?.contact_side || "both" };
            const busy = recommendationOutcomeBusyId === item.id;
            const downloadBusy = recommendationDownloadBusyId === item.id;
            const canDownloadBiodata =
              canDownloadRecommendationBiodataPermission &&
              canDownloadRecommendationBiodata(item) &&
              item?.to_profile_id;
            const contactSideOptions = buildRecommendationContactSideOptions(
              profile,
              {
                id: item.to_profile_id,
                [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no,
                full_name: item.to_profile_name,
              }
            );
            return (
              <div key={item.id || index} style={{ ...S.remarkItem, ...(index === 0 ? S.remarkItemFirst : {}) }}>
                <div style={S.remarkItemLeft}>
                  <div style={S.remarkAvatar}>
                    <ProfilePhoto
                      src={item.to_profile_photo || null}
                      initials={getProfileInitials({
                        full_name: item.to_profile_name,
                        [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no || item.to_profile_id,
                      })}
                      avatarColor={pickColor(item.to_profile_name || formatProfileNumberValue(item.to_profile_no, item.to_profile_gender) || "R")}
                    />
                  </div>
                </div>
                <div style={S.remarkItemRight}>
                  <div style={S.remarkItemTop}>
                    <div>
                      <span style={S.remarkAuthor}>
                        {item.to_profile_name || `Profile #${formatProfileNumberValue(item.to_profile_no, item.to_profile_gender) || item.to_profile_id}`}
                      </span>
                      <div style={S.noteMetaRow}>
                        {item.to_profile_no && <span style={S.noteTypeBadge}>#{formatProfileNumberValue(item.to_profile_no, item.to_profile_gender)}</span>}
                        <span style={S.noteOutcomeBadge}>{formatRecommendationStatusLabel(item.status || "recommended")}</span>
                        {item.note_type && <span style={S.noteStatusBadge}>{labelize(item.note_type)}</span>}
                      </div>
                    </div>
                    <span style={S.remarkTime}>{formatRelativeTime(item.created_at)}</span>
                  </div>
                  <p style={S.remarkText}>{item.note_content || "Recommendation linked without note preview."}</p>
                  <div style={S.recommendationCardMeta}>
                    {[item.recommended_by_email || "Admin", item.to_profile_location, item.to_profile_gender ? labelize(item.to_profile_gender) : ""]
                      .filter(Boolean)
                      .join(" | ")}
                  </div>

                  {Array.isArray(item.outcomes) && item.outcomes.length > 0 && (
                    <div style={S.recommendationTimeline}>
                      {item.outcomes.map((outcome) => (
                        <div key={outcome.id} style={S.recommendationTimelineItem}>
                          <div style={S.recommendationTimelineTop}>
                            <div style={S.noteMetaRow}>
                              <span style={S.noteOutcomeBadge}>{formatRecommendationStatusLabel(outcome.match_status)}</span>
                              {outcome.contact_side ? (
                                <span style={S.noteStatusBadge}>
                                  {getRecommendationContactSideLabel(
                                    outcome.contact_side,
                                    profile,
                                    {
                                      id: item.to_profile_id,
                                      [RAW_PROFILE_NUMBER_FIELD]: item.to_profile_no,
                                      full_name: item.to_profile_name,
                                    }
                                  )}
                                </span>
                              ) : null}
                            </div>
                            <span style={S.remarkTime}>{formatDateTime(outcome.matched_at || outcome.created_at)}</span>
                          </div>
                          {(outcome.outcome_notes || outcome.note_content) && (
                            <div style={S.recommendationTimelineBody}>{outcome.outcome_notes || outcome.note_content}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {canDownloadBiodata && (
                    <div style={S.recommendationDownloadRow}>
                      <span style={S.remarkCharCount}>Biodata is ready for download on this recommendation entry.</span>
                      <button
                        type="button"
                        style={{ ...S.noteFollowUpBtn, ...(downloadBusy ? S.remarkBtnDisabled : {}) }}
                        onClick={() => onDownloadBiodata(item)}
                        disabled={downloadBusy}
                      >
                        {downloadBusy ? "Preparing PDF..." : "Download Biodata PDF"}
                      </button>
                    </div>
                  )}

                  {canEditRecommendations && (
                    <div style={S.recommendationOutcomeComposer}>
                      <div style={S.noteComposerGrid} className="note-composer-grid recommendation-outcome-grid">
                        <div style={S.noteField}>
                          <label style={S.noteFieldLabel}>Update Status</label>
                          <select
                            style={S.noteSelect}
                            value={draft.status}
                            onChange={(event) => onOutcomeDraftChange(item.id, "status", event.target.value)}
                          >
                            <option value="">Choose next stage</option>
                            {recommendationMeta.match_statuses.map((status) => (
                              <option key={status} value={status}>{formatRecommendationStatusLabel(status)}</option>
                            ))}
                          </select>
                        </div>
                        <div style={S.noteField}>
                          <label style={S.noteFieldLabel}>Applies To</label>
                          <select
                            style={S.noteSelect}
                            value={draft.contactSide || recommendationMeta.defaults?.contact_side || "both"}
                            onChange={(event) => onOutcomeDraftChange(item.id, "contactSide", event.target.value)}
                          >
                            {contactSideOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ ...S.noteField, gridColumn: "span 3" }}>
                          <label style={S.noteFieldLabel}>Outcome Note</label>
                          <input
                            type="text"
                            style={S.noteInput}
                            value={draft.notes}
                            onChange={(event) => onOutcomeDraftChange(item.id, "notes", event.target.value)}
                            placeholder="What happened after recommending this profile?"
                          />
                        </div>
                      </div>
                      <div style={S.recommendationOutcomeActions}>
                        <span style={S.remarkCharCount}>Current status: {formatRecommendationStatusLabel(item.status || "recommended")}</span>
                        <button
                          type="button"
                          style={{ ...S.noteFollowUpBtn, ...(busy || !draft.status ? S.remarkBtnDisabled : {}) }}
                          onClick={() => onSaveOutcome(item)}
                          disabled={busy || !draft.status}
                        >
                          {busy ? "Saving..." : "Save Outcome"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, hint, accent = "default" }) {
  return (
    <div
      style={{
        ...S.noteMetricCard,
        ...(accent === "alert" ? S.noteMetricCardAlert : {}),
      }}
    >
      <span style={S.noteMetricLabel}>{label}</span>
      <strong style={S.noteMetricValue}>{value}</strong>
      <span style={S.noteMetricHint}>{hint}</span>
    </div>
  );
}


function ContactCard({ contact, profileId, authHeaders, canCopyPhone = false, canCopyEmail = false }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const type = String(contact.contact_type || contact.type || "contact").toLowerCase();
  const isEmail = type === "email";
  const displayValue = getContactDisplayValue(contact);
  const canCopy = isEmail
    ? canCopyEmail
    : canCopyPhone && ["mobile", "phone", "whatsapp", "parent_mobile", "landline"].includes(type);

  const copyValue = async () => {
    if (!displayValue) return;

    try {
      await API.post(
        `/profiles/${profileId}/contact-access`,
        {
          kind: isEmail ? "email" : "phone",
          action: "copy",
          contact_type: type,
        },
        authHeaders
      );
    } catch (err) {
      alert(err?.response?.data?.error || "Copy is restricted for this contact.");
      return;
    }

    const ok = await copyToClipboard(displayValue);

    if (ok) {
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 1300);
    } else {
      setCopyFailed(true);
      setTimeout(() => setCopyFailed(false), 2200);
    }
  };

  return (
    <SubCard>
      <div style={S.contactCardTop}>
        <div style={{ minWidth: 0 }}>
          <div style={S.subCardTitle}>
            {labelize(contact.contact_type || contact.type || "Contact")}
            {contact.is_primary && <span style={S.highestBadge}>Primary</span>}
            {contact.is_verified && <span style={S.verifiedBadge}>Verified</span>}
          </div>
          <div style={S.subCardSub}>
            {[displayValue, contact.contact_person_name, labelize(contact.contact_person_relation)]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {copyFailed && (
            <div style={S.copyHint}>
              Copy blocked by browser. Select and copy manually: {displayValue}
            </div>
          )}
        </div>

        {canCopy && displayValue && (
          <button style={S.copyBtn} onClick={copyValue} type="button">
            {copied ? "Copied" : isEmail ? "Copy Email" : "Copy Phone"}
          </button>
        )}
      </div>
    </SubCard>
  );
}

function RelativeCard({ relative, showFamilySide = false }) {
  const occupationRows = getRelativeOccupations(relative);
  const primaryOccupation = occupationRows.find((occupation) => occupation.is_primary) || occupationRows[0] || null;

  return (
    <SubCard>
      <div style={S.relativeTop}>
        <div>
          <div style={S.subCardTitle}>
            {relative.full_name ||
              [relative.first_name, relative.middle_name, relative.last_name].filter(Boolean).join(" ") ||
              "Unnamed"}
          </div>
          <div style={S.subCardSub}>
            {[
              labelize(relative.relation_type),
              showFamilySide ? getRelativeFamilySideLabel(relative.family_side) : "",
              relative.sakha_name || "",
              primaryOccupation?.occupation_type || "",
              primaryOccupation?.occupation_details || "",
              primaryOccupation?.company_name || "",
              primaryOccupation?.work_profile || "",
              shouldShowRelativeMaritalStatus(relative) && relative.marital_status ? labelize(relative.marital_status) : "",
              shouldShowRelativeMaritalStatus(relative) && isRelativeMarriedStatus(relative.marital_status) && relative.partner_name ? `${getRelativePartnerNameLabel(relative)}: ${relative.partner_name}` : "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {(relative.phone || relative.email) && (
            <div style={S.relativeContact}>
              {[relative.phone, relative.email].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        <div style={S.relativeBadgeColumn}>
          {shouldShowRelativeMaritalStatus(relative) && relative.is_future_prospect && isRelativeUnmarriedStatus(relative.marital_status) && <span style={S.futureBadge}>Future Prospect</span>}
          {relative.is_deceased && <span style={S.dangerBadge}>Deceased</span>}
        </div>
      </div>

      {occupationRows.length > 1 && (
        <div style={S.relativeEduList}>
          {occupationRows.map((occupation, occupationIndex) => (
            <span key={occupation.id || occupationIndex} style={S.relativeEduChip}>
              {formatRelativeOccupationSummary(occupation)}
            </span>
          ))}
        </div>
      )}

      {safeArray(relative.education).length > 0 && (
        <div style={S.relativeEduList}>
          {relative.education.map((edu, eduIndex) => (
            <span key={edu.id || eduIndex} style={S.relativeEduChip}>
              {[edu.degree_name, edu.education_description].filter(Boolean).join(" · ")}
            </span>
          ))}
        </div>
      )}
    </SubCard>
  );
}

function GalleryItem({ file, onOpen, isSettingPrimary, canSetPrimary = false }) {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    if (isSettingPrimary) return;
    onOpen?.(file);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...S.galleryItem,
        ...(file.is_primary ? S.galleryItemPrimary : {}),
        ...S.galleryItemClickable,
        ...(isSettingPrimary ? S.galleryItemLoading : {}),
        ...(hovered && !isSettingPrimary
          ? { transform: "scale(1.03)", boxShadow: "0 12px 28px rgba(37,41,53,0.14)" }
          : {}),
      }}
      title={file.is_primary ? "View primary photo" : canSetPrimary ? "View photo and actions" : "View photo"}
      disabled={isSettingPrimary}
    >
      {!imgError ? (
        <img src={file.file_url} alt="" style={S.galleryImg} onError={() => setImgError(true)} />
      ) : (
        <div style={S.galleryImgFallback}>?</div>
      )}
      {file.is_primary && <span style={S.galleryBadge}>★ Primary</span>}
      {!file.is_primary && !isSettingPrimary && <span style={S.gallerySetBadge}>{canSetPrimary ? "View Photo" : "Preview"}</span>}
      {isSettingPrimary && <span style={S.galleryUpdatingBadge}>Updating...</span>}
    </button>
  );
}

function PhotoPreviewModal({
  file,
  canSetPrimary,
  isSettingPrimary,
  onClose,
  onSetPrimary,
  eyebrow = "Photo Preview",
  title = null,
  footerText = "",
}) {
  const [imgError, setImgError] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    setImgError(false);
    setZoomLevel(1);
  }, [file?.id]);

  if (typeof document === "undefined") return null;

  const zoomOutDisabled = zoomLevel <= 1;
  const zoomInDisabled = zoomLevel >= 3;

  return createPortal(
    <div
      style={S.photoPreviewBackdrop}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div style={S.photoPreviewModal}>
        <div style={S.photoPreviewHeader}>
          <div>
            <p style={S.overlayEyebrow}>{eyebrow}</p>
            <h2 style={S.photoPreviewTitle}>{title || (file.is_primary ? "Primary Photo" : "Profile Photo")}</h2>
          </div>
          <div style={S.photoPreviewToolbar}>
            <button
              type="button"
              style={{
                ...S.photoZoomButton,
                ...(zoomOutDisabled ? S.photoZoomButtonDisabled : {}),
              }}
              onClick={() => setZoomLevel((current) => Math.max(1, Number((current - 0.25).toFixed(2))))}
              disabled={zoomOutDisabled}
            >
              -
            </button>
            <button
              type="button"
              style={S.photoZoomResetButton}
              onClick={() => setZoomLevel(1)}
            >
              Reset
            </button>
            <button
              type="button"
              style={{
                ...S.photoZoomButton,
                ...(zoomInDisabled ? S.photoZoomButtonDisabled : {}),
              }}
              onClick={() => setZoomLevel((current) => Math.min(3, Number((current + 0.25).toFixed(2))))}
              disabled={zoomInDisabled}
            >
              +
            </button>
            <button type="button" style={S.photoPreviewCloseButton} onClick={onClose}>x</button>
          </div>
        </div>

        <div style={S.photoPreviewBody}>
          {!imgError ? (
            <div style={S.photoPreviewViewport}>
              <img
                src={file.file_url}
                alt="Profile"
                style={{
                  ...S.photoPreviewImage,
                  transform: `scale(${zoomLevel})`,
                }}
                onError={() => setImgError(true)}
              />
            </div>
          ) : (
            <div style={S.photoPreviewFallback}>Photo preview unavailable.</div>
          )}
        </div>

        <div style={S.photoPreviewFooter}>
          <div style={S.photoPreviewMeta}>
            {footerText || (
              file.is_primary
                ? `This photo is currently primary. Zoom ${Math.round(zoomLevel * 100)}%.`
                : canSetPrimary
                  ? `You can make this the primary profile photo. Zoom ${Math.round(zoomLevel * 100)}%.`
                  : `Zoom ${Math.round(zoomLevel * 100)}%.`
            )}
          </div>
          <div style={S.photoPreviewActions}>
            <button type="button" style={S.overlayCancel} onClick={onClose}>Close</button>
            {canSetPrimary && !file.is_primary && (
              <button
                type="button"
                style={{
                  ...S.overlaySubmit,
                  ...(isSettingPrimary ? S.overlaySubmitDisabled : {}),
                }}
                onClick={() => onSetPrimary?.(file.id)}
                disabled={isSettingPrimary}
              >
                {isSettingPrimary ? "Setting..." : "Set as Primary"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ProfilePhoto({ src, initials, avatarColor }) {
  const [imgError, setImgError] = useState(false);
  const showImage = src && !imgError;

  return showImage ? (
    <img src={src} alt="" style={S.photo} onError={() => setImgError(true)} />
  ) : (
    <div style={{ ...S.avatar, background: avatarColor }}>
      <span style={S.avatarInitials}>{initials || "?"}</span>
    </div>
  );
}

function Pill({ children }) {
  return <span style={S.pill}>{children}</span>;
}

function SignalCard({ label, title, detail, accent }) {
  return (
    <div style={{ ...S.signalCard, ...(S[`signal_${accent}`] || {}) }}>
      <span style={S.signalLabel}>{label}</span>
      <b style={S.signalTitle}>{title || "Not added"}</b>
      {detail && <p style={S.signalDetail}>{detail}</p>}
    </div>
  );
}

function CompletenessPanel({ pct, missingItems }) {
  const normalizedPct = Math.max(0, Math.min(Number(pct) || 0, 100));
  const status =
    normalizedPct >= 90
      ? "Excellent"
      : normalizedPct >= 75
        ? "Strong"
        : normalizedPct >= 55
          ? "Needs attention"
          : "Incomplete";

  return (
    <div style={S.completenessPanel}>
      <div style={S.completenessPanelTop}>
        <div>
          <p style={S.panelEyebrow}>Profile Strength</p>
          <h3 style={S.panelTitle}>{normalizedPct}% complete · {status}</h3>
          <p style={S.panelSub}>
            This score is based on biodata completeness only. Payment/package status is shown separately.
          </p>
        </div>
      </div>

      <div style={S.progressTrack}>
        <div style={{ ...S.progressFill, width: `${normalizedPct}%` }} />
      </div>

      {missingItems.length > 0 ? (
        <div style={S.missingGrid}>
          {missingItems.slice(0, 4).map((item, index) => (
            <div key={index} style={S.missingItem}>
              <span style={S.missingNumber}>{index + 1}</span>
              <div>
                <b>{item.section || "Missing detail"}</b>
                <p>{item.message || "Add the required detail to improve profile quality."}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p style={S.completeText}>Profile looks complete and ready for matching conversations.</p>
      )}
    </div>
  );
}

function SectionHead({ children }) {
  return <p style={S.sectionHead}>{children}</p>;
}

function SubCard({ children }) {
  return <div style={S.subCard}>{children}</div>;
}

function Empty({ children }) {
  return <p style={S.emptyText}>{children}</p>;
}

function AlertCard({ alert }) {
  const status = normalizeAlertStatus(alert?.status);
  const severity = normalizeAlertSeverity(alert?.severity);
  const notePreview = summarizeText(alert?.note_content, 144);

  return (
    <SubCard>
      <div style={S.alertCardTop}>
        <div style={{ minWidth: 0 }}>
          <div style={S.subCardTitle}>{alert?.message || labelize(alert?.alert_type) || "Alert"}</div>
          <div style={S.subCardSub}>
            {[
              labelize(alert?.alert_type || "alert"),
              severity ? `${labelize(severity)} severity` : "",
              labelize(status || "open"),
            ]
              .filter(Boolean)
              .join(" | ")}
          </div>
          <div style={S.metaLine}>
            {[
              alert?.created_at ? `Created ${formatDateTime(alert.created_at)}` : "",
              alert?.resolved_at ? `Resolved ${formatDateTime(alert.resolved_at)}` : "",
              alert?.follow_up_at ? `Due ${formatDateTime(alert.follow_up_at)}` : "",
              alert?.related_entity_type ? `Linked to ${labelize(alert.related_entity_type)}` : "",
            ]
              .filter(Boolean)
              .join(" | ")}
          </div>
          {notePreview ? <div style={S.metaLine}>{notePreview}</div> : null}
        </div>

        <div style={S.alertBadgeColumn}>
          <span
            style={{
              ...S.alertStatusBadge,
              ...(status === "resolved" ? S.alertStatusBadgeResolved : S.alertStatusBadgeOpen),
            }}
          >
            {labelize(status || "open")}
          </span>
          {severity && (
            <span
              style={{
                ...S.alertSeverityBadge,
                ...(severity === "high" ? S.alertSeverityBadgeHigh : {}),
              }}
            >
              {labelize(severity)}
            </span>
          )}
        </div>
      </div>
    </SubCard>
  );
}

function InfoItem({ label, value }) {
  if (!value && value !== 0) return null;

  return (
    <div style={S.infoItem}>
      <span style={S.infoLabel}>{label}</span>
      <span style={S.infoValue}>{value}</span>
    </div>
  );
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeAlertStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized || "open";
}

function normalizeAlertSeverity(value) {
  return String(value || "").trim().toLowerCase();
}

function isServiceOccupationTypeLabel(value) {
  return /\bservice\b/i.test(String(value || "").trim());
}

function occupationHasContent(occupation) {
  return Boolean(
    occupation?.occupation_type_id ||
    occupation?.occupation_type ||
    occupation?.company_name ||
    occupation?.occupation_details ||
    occupation?.work_profile ||
    occupation?.annual_income_amount ||
    occupation?.annual_turnover_amount
  );
}

function getProfileOccupations(profile) {
  const occupationRows = safeArray(profile?.occupations);
  if (occupationRows.length) return occupationRows;

  const fallbackOccupation = {
    occupation_type: profile?.occupation_type || "",
    company_name: profile?.company_name || "",
    occupation_details: profile?.current_occupation_details || "",
    work_profile: profile?.work_profile || "",
    annual_income_amount: profile?.annual_income_amount ?? null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      profile?.annual_income_currency_code,
      profile?.annual_income_amount
    ),
    annual_turnover_amount: profile?.annual_turnover_amount ?? null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      profile?.annual_turnover_currency_code,
      profile?.annual_turnover_amount
    ),
    is_primary: true,
  };

  return [fallbackOccupation].filter((occupation) =>
    [
      occupation.occupation_type,
      occupation.company_name,
      occupation.occupation_details,
      occupation.work_profile,
      occupation.annual_income_amount,
      occupation.annual_turnover_amount,
    ].some((value) => value !== null && value !== undefined && String(value).trim() !== "")
  );
}

function formatOccupationAmount(occupation) {
  if (!occupation) return "";
  const amount = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? occupation.annual_income_amount
    : occupation.annual_turnover_amount;
  if (amount === null || amount === undefined || amount === "") return "";

  const label = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? "Annual income"
    : "Annual turnover";
  const currencyCode = isServiceOccupationTypeLabel(occupation.occupation_type)
    ? normalizeOccupationCurrencyCode(occupation.annual_income_currency_code, amount)
    : normalizeOccupationCurrencyCode(occupation.annual_turnover_currency_code, amount);
  const formattedAmount = formatCurrencyAmount(amount, currencyCode || "INR");
  const currencyName = getCurrencyName(currencyCode || "INR");
  return `${label}: ${formattedAmount}${currencyName ? ` (${currencyName})` : ""}`;
}

function getRelativeOccupations(relative) {
  const occupationRows = safeArray(relative?.occupations).filter(occupationHasContent);
  if (occupationRows.length) return occupationRows;

  const fallbackOccupation = {
    occupation_type: relative?.occupation_type || "",
    company_name: relative?.company_name || "",
    occupation_details: relative?.occupation_details || "",
    work_profile: relative?.work_profile || "",
    annual_income_amount: relative?.annual_income_amount ?? null,
    annual_income_currency_code: normalizeOccupationCurrencyCode(
      relative?.annual_income_currency_code,
      relative?.annual_income_amount
    ),
    annual_turnover_amount: relative?.annual_turnover_amount ?? null,
    annual_turnover_currency_code: normalizeOccupationCurrencyCode(
      relative?.annual_turnover_currency_code,
      relative?.annual_turnover_amount
    ),
    is_primary: true,
  };

  return occupationHasContent(fallbackOccupation) ? [fallbackOccupation] : [];
}

function formatRelativeOccupationSummary(occupation) {
  return [
    occupation?.is_primary ? "Primary" : "",
    occupation?.occupation_type || "",
    occupation?.occupation_details || "",
    occupation?.company_name || "",
    occupation?.work_profile || "",
    formatOccupationAmount(occupation),
  ]
    .filter(Boolean)
    .join(" · ");
}

function getAge(dateValue) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((new Date() - date) / (365.25 * 86400000));
}

function formatDate(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function toTime(value) {
  return formatTime12Hour(value);
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "—";

  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (Number.isNaN(diff)) return "—";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function labelize(value) {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatRecommendationStatusLabel(value) {
  return labelize(value);
}

function isRelativeUnmarriedStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "unmarried" || normalized === "single";
}

function isRelativeMarriedStatus(value) {
  return String(value || "").trim().toLowerCase() === "married";
}

function shouldShowRelativeMaritalStatus(relative) {
  const normalizedRelationType = normalizeRelationType(relative?.relation_type);
  return normalizedRelationType === "sister" || normalizedRelationType === "brother" || normalizedRelationType === "sibling";
}

function getRelativePartnerNameLabel(relative) {
  const normalizedRelationType = normalizeRelationType(relative?.relation_type);
  if (normalizedRelationType === "sister") return "Husband Name";
  if (normalizedRelationType === "sibling") {
    const normalizedGender = String(relative?.gender || "").trim().toLowerCase();
    if (normalizedGender === "female") return "Husband Name";
  }
  return "Spouse Name";
}

function getRelativeFamilySideLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "none" || normalized === "direct") return "Direct";
  return labelize(value);
}

function getProfileDisplayName(profile) {
  if (!profile || typeof profile !== "object") return "";
  const parts = [
    profile.full_name,
    [profile.first_name, profile.middle_name, profile.last_name].filter(Boolean).join(" "),
  ].filter(Boolean);
  return parts[0] || "";
}

function buildProfileReferenceLabel(profile) {
  if (!profile || typeof profile !== "object") return "Unknown profile";
  const name = getProfileDisplayName(profile);
  const profileNo = getProfileNumberDisplay(profile) || profile.id || profile.profile_id;
  if (name && profileNo) return `${name} (#${profileNo})`;
  if (name) return name;
  if (profileNo) return `Profile #${profileNo}`;
  return "Unknown profile";
}

function buildRecommendationContactSideOptions(sourceProfile, targetProfile) {
  const options = [];
  const sourceProfileId = sourceProfile?.id || sourceProfile?.profile_id;
  const targetProfileId = targetProfile?.id || targetProfile?.profile_id;

  if (sourceProfileId) {
    options.push({
      value: String(sourceProfileId),
      label: buildProfileReferenceLabel(sourceProfile),
    });
  }

  if (targetProfileId) {
    options.push({
      value: String(targetProfileId),
      label: buildProfileReferenceLabel(targetProfile),
    });
  }

  options.push({
    value: "both",
    label: `${buildProfileReferenceLabel(sourceProfile)} + ${buildProfileReferenceLabel(targetProfile)}`,
  });

  return options;
}

function getRecommendationContactSideLabel(side, sourceProfile, targetProfile) {
  const rawSide = String(side || "").trim();
  const normalized = rawSide.toLowerCase();
  const sourceProfileId = String(sourceProfile?.id || sourceProfile?.profile_id || "").trim();
  const targetProfileId = String(targetProfile?.id || targetProfile?.profile_id || "").trim();
  if (!normalized) return "";
  if (normalized === "profile_1" || (sourceProfileId && rawSide === sourceProfileId)) {
    return buildProfileReferenceLabel(sourceProfile);
  }
  if (normalized === "profile_2" || (targetProfileId && rawSide === targetProfileId)) {
    return buildProfileReferenceLabel(targetProfile);
  }
  if (normalized === "both") {
    return `${buildProfileReferenceLabel(sourceProfile)} + ${buildProfileReferenceLabel(targetProfile)}`;
  }
  if (normalized === "internal") return "Admin Note (Internal)";
  return labelize(side);
}

function getProfileInitials(profile) {
  if (!profile || typeof profile !== "object") return "?";
  const first = String(profile.first_name || profile.full_name || profile.profile_no_display || profile[RAW_PROFILE_NUMBER_FIELD] || "").trim();
  const last = String(profile.last_name || "").trim();
  const initials = `${first[0] || ""}${last[0] || ""}`.trim().toUpperCase();
  return initials || "?";
}

function buildRecommendationResultMeta(profile) {
  if (!profile || typeof profile !== "object") return "";
  return [
    getProfileNumberDisplay(profile) ? `#${getProfileNumberDisplay(profile)}` : "",
    profile.gender ? labelize(profile.gender) : "",
    profile.location_display_cache || profile.location_display || [profile.city, profile.state, profile.country].filter(Boolean).join(", "),
  ]
    .filter(Boolean)
    .join(" | ");
}

function showChildrenFields(maritalStatus) {
  const normalized = String(maritalStatus || "").trim().toLowerCase();
  if (!normalized) return false;
  return normalized !== "single" && normalized !== "never married";
}

function formatCoordinate(value) {
  if (value === undefined || value === null || value === "") return "";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return parsed.toFixed(6);
}

function formatCoordinatePair(latitude, longitude) {
  const lat = formatCoordinate(latitude);
  const lng = formatCoordinate(longitude);
  if (!lat || !lng) return "";
  return `${lat}, ${lng}`;
}

function normalizeRelationType(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function pickColor(str) {
  const palette = ["#D73C2E", "#B9343A", "#C7433F", "#A93338", "#D1483A", "#252935"];
  let hash = 0;
  for (const ch of str) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function getCompletenessPct(profile) {
  return profile?.completeness?.completeness_pct ?? profile?.completeness_pct_cache ?? 0;
}

function formatDateTime(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function summarizeText(value, maxLength = 120) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

function toDateTimeLocalValue(dateValue) {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function isFollowUpDue(dateValue) {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() <= Date.now();
}

function deriveDraftFollowUpStatus(noteDraft) {
  if (!noteDraft) return "none";
  if (noteDraft.follow_up_at) return "scheduled";

  const outcome = String(noteDraft.outcome || "").trim().toLowerCase();
  if (["follow_up_required", "callback"].includes(outcome)) return "pending";

  return "none";
}

function noteNeedsFollowUp(note) {
  const status = String(note?.follow_up_status_effective || "").trim().toLowerCase();
  if (["completed", "cancelled", "none"].includes(status)) return false;
  if (status) return true;

  const outcome = String(note?.outcome || "").trim().toLowerCase();
  if (outcome === "follow_up_required" || outcome === "callback") return true;
  return Boolean(note?.follow_up_at);
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function countContactTypes(contacts) {
  return contacts.reduce(
    (acc, c) => {
      const type = String(c.contact_type || "").toLowerCase();
      if (type.includes("mobile") || type.includes("phone")) acc.mobile += 1;
      if (type.includes("email")) acc.email += 1;
      if (type.includes("whatsapp")) acc.whatsapp += 1;
      return acc;
    },
    { mobile: 0, email: 0, whatsapp: 0 }
  );
}

function highestEducationText(educations) {
  const edu = safeArray(educations).find((e) => e.is_highest_degree) || safeArray(educations)[0];
  if (!edu) return "";
  return edu.degree_name || edu.education_description || "";
}

function educationDetail(educations) {
  const edu = safeArray(educations).find((e) => e.is_highest_degree) || safeArray(educations)[0];
  if (!edu) return "";
  return [edu.institution_name, edu.education_location, edu.passing_year].filter(Boolean).join(" · ");
}

function buildLookupMaps(lookups) {
  return {
    locations: safeArray(lookups.locations),
    cityTiers: safeArray(lookups.cityTiers),
    castes: safeArray(lookups.castes),
    sakhas: safeArray(lookups.sakhas),
    educationCategories: safeArray(lookups.educationCategories),
    visaStatuses: safeArray(lookups.visaStatuses),
    visaNames: safeArray(lookups.visaNames),
  };
}

function getCountryName(row) {
  return String(row?.country || row?.country_name || row?.countryName || "India").trim();
}

function getCountryId(row) {
  return row?.country_id || row?.countryId || row?.countryID || null;
}

function lookupName(items, id, type = "default") {
  if (!id) return "";
  const found = safeArray(items).find((item) => Number(item.id) === Number(id));
  if (!found) return "";

  if (type === "city") {
    return [
      found.city || found.name,
      found.district || found.district_name,
      found.state || found.state_name,
      getCountryName(found),
    ]
      .filter(Boolean)
      .join(", ");
  }

  return found.name || found.display_label || "";
}

function lookupLocationDetails(items, cityId) {
  if (!cityId) return {};
  const found = safeArray(items).find((item) => Number(item.id) === Number(cityId));
  if (!found) return {};

  return {
    city_id: found.id || null,
    city: found.city || found.name || "",
    tehsil_id: found.tehsil_id || found.tehsilId || found.tehsilID || null,
    tehsil: found.tehsil || found.tehsil_name || found.tehsilName || "",
    district_id: found.district_id || found.districtId || found.districtID || null,
    district: found.district || found.district_name || found.districtName || "",
    state_id: found.state_id || found.stateId || found.stateID || null,
    state: found.state || found.state_name || found.stateName || "",
    country_id: getCountryId(found),
    country: getCountryName(found),
  };
}

function lookupHierarchyName(items, id, idKeys, nameKeys) {
  if (!id) return "";
  const found = safeArray(items).find((item) =>
    idKeys.some((key) => Number(item?.[key]) === Number(id))
  );
  if (!found) return "";
  return nameKeys.map((key) => found?.[key]).find(Boolean) || "";
}

function formatLocationParts(parts) {
  const out = [];
  parts.forEach((value) => pushUniqueAddressPart(out, value));
  return out.join(", ");
}

function formatProfileLocation(profile, lookupMaps) {
  if (!profile) return "";

  const lookup = lookupLocationDetails(lookupMaps.locations, profile.city_id || profile.current_location_id);

  return formatLocationParts([
    profile.city || lookup.city,
    profile.district || lookup.district,
    profile.state || lookup.state,
    profile.country || lookup.country,
  ]);
}

function formatProfileBirthPlace(profile, lookupMaps) {
  if (!profile) return "";

  const lookup = lookupLocationDetails(lookupMaps.locations, profile.birth_place_city_id || profile.birth_place_id);

  return formatLocationParts([
    profile.birth_city || profile.birth_place_city || lookup.city,
    profile.birth_district || lookup.district,
    profile.birth_state || lookup.state,
    profile.birth_country || lookup.country,
  ]);
}

function formatPreferenceIds(ids, items, type = "default") {
  const names = safeArray(ids)
    .map((id) => lookupName(items, id, type))
    .filter(Boolean);

  return names.length ? names.join(", ") : "";
}

function formatPreferenceCityIds(ids, items) {
  const names = safeArray(ids)
    .map((id) => {
      const location = safeArray(items).find((item) => Number(item.id) === Number(id));
      if (!location) return "";
      return [
        location.city || location.name,
        location.state || location.state_name,
        getCountryName(location),
      ]
        .filter(Boolean)
        .join(", ");
    })
    .filter(Boolean);

  return names.length ? names.join(", ") : "";
}

function formatAgeGapMonths(value) {
  const totalMonths = Number(value);
  if (!Number.isFinite(totalMonths)) return "";
  if (totalMonths <= 0) return "0 months";
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  const yearText = years ? `${years} year${years === 1 ? "" : "s"}` : "";
  const monthText = remainingMonths ? `${formatPreferredAgeNumber(remainingMonths)} month${remainingMonths === 1 ? "" : "s"}` : "";
  return [yearText, monthText].filter(Boolean).join(" ");
}

function formatPreferredAgeNumber(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  return Number.isInteger(numericValue) ? String(numericValue) : String(Number(numericValue.toFixed(2)));
}

function getStoredPreferredAgeUnit(preferences, side) {
  const unitValue =
    side === "min"
      ? preferences?.preferred_age_min_unit
      : preferences?.preferred_age_max_unit;
  const normalized = String(unitValue || "").trim().toLowerCase();
  return normalized === "years" || normalized === "months" ? normalized : "";
}

function formatPreferredAgeValue(value, unit = "") {
  const totalMonths = Number(value);
  if (!Number.isFinite(totalMonths)) return "";
  if (unit === "years") {
    const yearValue = totalMonths / 12;
    const normalizedValue = formatPreferredAgeNumber(yearValue);
    return normalizedValue ? `${normalizedValue} year${Number(yearValue) === 1 ? "" : "s"}` : "";
  }
  if (unit === "months") {
    const normalizedValue = formatPreferredAgeNumber(totalMonths);
    return normalizedValue ? `${normalizedValue} month${Number(totalMonths) === 1 ? "" : "s"}` : "";
  }
  return formatAgeGapMonths(totalMonths);
}

function formatPreferredAgeDifference(preferences) {
  const minRaw = preferences?.preferred_age_min ?? preferences?.min_age;
  const maxRaw = preferences?.preferred_age_max ?? preferences?.max_age;
  const minMonths = Number(minRaw);
  const maxMonths = Number(maxRaw);
  const minUnit = getStoredPreferredAgeUnit(preferences, "min");
  const maxUnit = getStoredPreferredAgeUnit(preferences, "max");
  const hasMin = Number.isFinite(minMonths) && minRaw !== "" && minRaw !== null && minRaw !== undefined;
  const hasMax = Number.isFinite(maxMonths) && maxRaw !== "" && maxRaw !== null && maxRaw !== undefined;

  if (hasMin || hasMax) {
    const fromLabel = `- ${formatPreferredAgeValue(hasMin ? minMonths : 0, minUnit)}`;
    const toLabel = `+ ${formatPreferredAgeValue(hasMax ? maxMonths : 0, maxUnit)}`;
    return `${fromLabel}, ${toLabel}`;
  }

  const legacyGap = preferences?.preferred_age_gap_months;
  const gapMonths = Number(legacyGap);
  if (Number.isFinite(gapMonths) && legacyGap !== "" && legacyGap !== null && legacyGap !== undefined) {
    return `- ${formatAgeGapMonths(gapMonths)}, + ${formatAgeGapMonths(gapMonths)}`;
  }

  return "";
}

function cleanAddressLine(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const normalized = text.toLowerCase();

  // Hide demo/test helper text from address display.
  if (
    normalized.includes("same city as source") ||
    normalized.includes("same district as source") ||
    normalized.includes("same state as source")
  ) {
    return "";
  }

  if (normalized.includes("intentional test native house")) {
    return text.replace(/intentional test native house/ig, "Native house").trim();
  }

  return text;
}

function pushUniqueAddressPart(parts, value) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return;

  const key = text.toLowerCase();
  const alreadyExists = parts.some((part) => {
    const partKey = String(part).toLowerCase();
    return partKey === key || partKey.includes(key) || key.includes(partKey);
  });

  if (!alreadyExists) parts.push(text);
}

function formatAddress(address, profile, lookupMaps) {
  const lookup = lookupLocationDetails(lookupMaps.locations, address.city_id);
  const tehsilName =
    address.tehsil_name ||
    address.tehsil ||
    lookupHierarchyName(lookupMaps.locations, address.tehsil_id, ["tehsil_id", "tehsilId", "tehsilID"], ["tehsil", "tehsil_name", "tehsilName"]) ||
    (Number(address.tehsil_id) === Number(profile.tehsil_id) ? profile.tehsil : "");

  const districtName =
    address.district_name ||
    address.district ||
    lookup.district ||
    lookupHierarchyName(lookupMaps.locations, address.district_id, ["district_id", "districtId", "districtID"], ["district", "district_name", "districtName"]) ||
    (Number(address.district_id) === Number(profile.district_id) ? profile.district : "");

  const stateName =
    address.state_name ||
    address.state ||
    lookup.state ||
    lookupHierarchyName(lookupMaps.locations, address.state_id, ["state_id", "stateId", "stateID"], ["state", "state_name", "stateName"]) ||
    (Number(address.state_id) === Number(profile.state_id) ? profile.state : "");

  const countryName =
    address.country_name ||
    address.country ||
    lookup.country ||
    lookupHierarchyName(lookupMaps.locations, address.country_id, ["country_id", "countryId", "countryID"], ["country", "country_name", "countryName"]) ||
    profile.country ||
    "India";

  const villageName =
    address.village_name ||
    address.village ||
    (Number(address.village_id) === Number(profile.village_id) ? profile.village : "");

  const parts = [];

  pushUniqueAddressPart(parts, cleanAddressLine(address.address_line1));
  pushUniqueAddressPart(parts, cleanAddressLine(address.address_line2));
  pushUniqueAddressPart(parts, villageName);
  pushUniqueAddressPart(parts, tehsilName);
  pushUniqueAddressPart(parts, districtName);
  pushUniqueAddressPart(parts, stateName);
  pushUniqueAddressPart(parts, countryName);
  pushUniqueAddressPart(parts, address.pincode);

  return parts.join(" · ");
}

function getAddressVisaInfo(address, lookupMaps) {
  const visaStatus = lookupName(lookupMaps.visaStatuses, address?.visa_status_id);
  const visaName = lookupName(lookupMaps.visaNames, address?.visa_name_id);
  if (!visaStatus && !visaName) return null;
  return { visaStatus, visaName };
}

async function copyToClipboard(value) {
  const text = String(value || "");
  if (!text) return false;

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // fallback below
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    return false;
  }
}

function familyMembers(relatives) {
  return safeArray(relatives).filter((relative) =>
    FAMILY_RELATION_TYPES.has(normalizeRelationType(relative.relation_type))
  );
}

function sideRelatives(relatives) {
  return safeArray(relatives).filter(
    (relative) => !FAMILY_RELATION_TYPES.has(normalizeRelationType(relative.relation_type))
  );
}

const C = {
  bg: "#fff7f5",
  card: "#ffffff",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.18)",
  borderBold: "rgba(185,52,58,0.22)",
  accent: "#D73C2E",
  accentDark: "#B9343A",
  accentLight: "#fff2ef",
  blue: "#B9343A",
  blueLight: "#fff7f5",
  text: "#252935",
  muted: "#6b6f7d",
  inputBg: "#fff7f5",
  soft: "#fff7f5",
  green: "#B9343A",
  purple: "#D73C2E",
  amber: "#252935",
  successBg: "#f4fbf6",
  successText: "#1f7a46",
  neutralBg: "#f7f8fb",
  shadow: "rgba(37,41,53,0.07)",
  shadowSoft: "rgba(37,41,53,0.06)",
  shadowLight: "rgba(37,41,53,0.05)",
  shadowStrong: "rgba(37,41,53,0.14)",
};

const S = {
  page: {
    minHeight: "calc(100vh - 60px)",
    background: `linear-gradient(135deg, #ffffff 0%, ${C.bg} 48%, ${C.soft} 100%)`,
    padding: "28px 18px 60px",
    fontFamily: "var(--jm-font-body)",
  },
  watermarkPage: {
    minHeight: "calc(100dvh - 112px)",
    height: "calc(100dvh - 112px)",
    background: `linear-gradient(135deg, #ffffff 0%, ${C.bg} 48%, ${C.soft} 100%)`,
    width: "100vw",
    marginLeft: "calc(50% - 50vw)",
    padding: "0",
    fontFamily: "var(--jm-font-body)",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  centerWrap: {
    minHeight: "62vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },

  spinner: {
    width: 38,
    height: 38,
    border: `3px solid ${C.border}`,
    borderTopColor: C.accent,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  },

  loadingText: { color: C.muted, fontSize: 14, fontWeight: 800 },
  errorText: { color: C.accent, fontSize: 14, fontWeight: 800 },

  backBtn: {
    background: C.accent,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  topBar: { maxWidth: 1180, margin: "0 auto 18px", position: "relative" },
  topBarHidden: {
    display: "none",
  },
  assignmentReturnBar: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    gap: 18,
    padding: "18px 22px",
    paddingRight: 180,
    borderRadius: 24,
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    boxShadow: `0 18px 40px ${C.shadowLight}`,
  },
  assignmentReturnEyebrow: {
    margin: "0 0 10px",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: "0.24em",
    textTransform: "uppercase",
    color: C.accent,
  },
  assignmentReturnTabs: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: 6,
    borderRadius: 999,
    background: "#fff3ef",
    border: `1px solid ${C.border}`,
  },
  assignmentReturnTab: {
    border: "none",
    background: "transparent",
    color: C.muted,
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  assignmentReturnTabActive: {
    background: C.accent,
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(215,60,46,0.18)",
  },
  backLink: {
    position: "absolute",
    top: 20,
    right: 22,
    background: "none",
    border: "none",
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    padding: 0,
  },
  searchBackButton: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    color: C.accent,
    borderRadius: 999,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
    boxShadow: `0 12px 30px ${C.shadowLight}`,
  },

  wrap: {
    maxWidth: 1180,
    margin: "0 auto",
    animation: "fadeUp 0.35s ease both",
  },
  watermarkWorkspaceWrap: {
    width: "100%",
    margin: 0,
    minHeight: "calc(100dvh - 112px)",
    height: "calc(100dvh - 112px)",
    animation: "fadeUp 0.35s ease both",
    overflow: "hidden",
  },

  hero: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 32,
    padding: 28,
    display: "grid",
    gridTemplateColumns: "180px 1fr auto",
    gap: 28,
    alignItems: "flex-start",
    marginBottom: 18,
    boxShadow: `0 24px 70px ${C.shadow}`,
  },

  photoWrap: { flexShrink: 0 },
  photoShell: { position: "relative", width: 190, height: 190 },

  photo: {
    width: "100%",
    height: "100%",
    borderRadius: "inherit",
    objectFit: "cover",
    border: `3px solid ${C.border}`,
    display: "block",
    boxSizing: "border-box",
  },

  avatar: {
    width: "100%",
    height: "100%",
    borderRadius: "inherit",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `3px solid ${C.border}`,
    boxSizing: "border-box",
  },

  avatarInitials: {
    fontSize: 58,
    color: "rgba(255,255,255,0.92)",
    fontWeight: 950,
    letterSpacing: -2,
    userSelect: "none",
  },

  completenessBadge: {
    position: "absolute",
    right: -10,
    bottom: -10,
    width: 74,
    height: 74,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: "#fff",
    border: `7px solid ${C.accent}`,
    boxShadow: `0 16px 34px ${C.borderBold}`,
    color: C.accentDark,
    textAlign: "center",
    fontWeight: 950,
  },

  heroInfo: { minWidth: 0 },

  heroTopLine: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 6,
  },

  eyebrow: {
    fontSize: 11,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: C.accent,
    fontWeight: 950,
  },

  statusChip: {
    padding: "4px 9px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  heroName: {
    fontSize: 36,
    fontWeight: 700,
    color: C.text,
    margin: "0 0 14px",
    letterSpacing: -0.95,
    lineHeight: 1.04,
  },

  pillRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 },

  pill: {
    fontSize: 12,
    background: C.accentLight,
    color: C.accentDark,
    padding: "6px 12px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    fontWeight: 850,
    display: "inline-flex",
    alignItems: "center",
  },

  heroAbout: {
    fontSize: 14,
    color: C.muted,
    lineHeight: 1.75,
    margin: "0 0 16px",
    maxWidth: 700,
  },

  tagRow: { display: "flex", flexWrap: "wrap", gap: 7 },

  tag: {
    fontSize: 11,
    background: C.text,
    color: "#fff",
    padding: "5px 11px",
    borderRadius: 999,
    fontWeight: 850,
  },

  actionRow: {
    marginTop: 18,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  phoneBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
    boxShadow: `0 14px 30px ${C.borderBold}`,
  },

  phoneRevealed: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    background: C.accentLight,
    border: `1.5px solid ${C.accent}`,
    borderRadius: 999,
    padding: "9px 16px",
    fontSize: 14,
    color: C.accentDark,
    fontWeight: 950,
  },

  phoneHideBtn: {
    marginLeft: 6,
    background: "none",
    border: "none",
    color: C.muted,
    fontSize: 11,
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
  },

  emailPill: {
    padding: "9px 14px",
    borderRadius: 999,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 12,
    fontWeight: 850,
  },

  policyNotice: {
    marginTop: 10,
    padding: "12px 14px",
    borderRadius: 18,
    background: C.accentLight,
    border: `1px solid ${C.borderStrong}`,
    color: C.muted,
    maxWidth: 640,
  },

  policyNoticeTitle: {
    display: "block",
    color: C.text,
    fontSize: 13,
    fontWeight: 950,
    marginBottom: 4,
  },

  policyNoticeBody: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    fontSize: 12,
    lineHeight: 1.5,
  },

  heroAlertStack: {
    marginTop: 14,
    display: "grid",
    gap: 10,
    maxWidth: 720,
  },

  heroAlertBanner: {
    padding: "12px 14px",
    borderRadius: 18,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
  },

  heroAlertBannerHigh: {
    background: C.accentLight,
    border: `1px solid ${C.borderBold}`,
  },

  heroAlertTitle: {
    color: C.text,
    fontSize: 13,
    fontWeight: 900,
    lineHeight: 1.45,
  },

  heroAlertMeta: {
    marginTop: 4,
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.45,
  },

  policyHint: {
    marginBottom: 14,
    padding: "12px 14px",
    borderRadius: 16,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
  },

  heroActions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
  },

  addPhotoBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#fff",
    border: `1.5px solid ${C.accent}`,
    color: C.accent,
    borderRadius: 999,
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
  },

  editBtn: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "11px 17px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
  },

  quickGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 18,
  },

  quickCard: {
    background: "rgba(255,255,255,0.86)",
    border: `1px solid ${C.border}`,
    borderRadius: 22,
    padding: 16,
    boxShadow: `0 12px 34px ${C.shadowLight}`,
  },

  completenessPanel: {
    background: "rgba(255,255,255,0.94)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: 22,
    marginBottom: 18,
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
  },

  completenessPanelTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  panelEyebrow: {
    margin: 0,
    color: C.accent,
    fontSize: 10,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontWeight: 950,
  },

  panelTitle: {
    margin: "5px 0 0",
    color: C.text,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.1,
  },

  panelSub: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },

  panelAction: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  progressTrack: {
    height: 10,
    borderRadius: 999,
    background: "#f4d9d6",
    overflow: "hidden",
    marginTop: 16,
  },

  progressFill: {
    height: "100%",
    borderRadius: 999,
    background: `linear-gradient(135deg, ${C.accent}, ${C.accentDark})`,
  },

  missingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginTop: 16,
  },

  missingItem: {
    display: "flex",
    gap: 10,
    padding: 12,
    borderRadius: 18,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
  },

  missingNumber: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 950,
    flexShrink: 0,
  },

  completeText: {
    margin: "14px 0 0",
    color: C.accent,
    fontSize: 13,
    fontWeight: 900,
  },

  profileSignalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 18,
  },

  signalCard: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 24,
    padding: 18,
    boxShadow: `0 14px 40px ${C.shadowLight}`,
    minHeight: 104,
  },

  signal_blue: { borderTop: `4px solid ${C.accent}` },
  signal_green: { borderTop: `4px solid ${C.accentDark}` },
  signal_orange: { borderTop: `4px solid ${C.amber}` },
  signal_purple: { borderTop: `4px solid ${C.purple}` },

  signalLabel: {
    display: "block",
    color: C.muted,
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    fontWeight: 950,
    marginBottom: 7,
  },

  signalTitle: {
    display: "block",
    color: C.text,
    fontSize: 16,
    lineHeight: 1.3,
  },

  signalDetail: {
    margin: "6px 0 0",
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  detailBox: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    overflow: "hidden",
    marginBottom: 18,
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
  },

  tabBar: {
    display: "flex",
    borderBottom: `1px solid ${C.border}`,
    background: C.card,
    padding: "0 10px",
    overflowX: "auto",
  },

  tab: {
    padding: "15px 18px",
    background: "none",
    border: "none",
    borderBottom: "3px solid transparent",
    color: C.muted,
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    outline: "none",
    boxShadow: "none",
  },

  tabActive: {
    color: C.accent,
    borderBottomColor: C.accent,
    outline: "none",
    boxShadow: "none",
  },

  tabContent: { padding: "28px 32px", animation: "fadeUp 0.25s ease both" },

  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 32px" },
  cardGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },

  infoItem: {
    padding: "14px 0",
    borderBottom: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  infoLabel: {
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },

  infoValue: { fontSize: 15, color: C.text, fontWeight: 800 },

  sectionHead: {
    fontSize: 11,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    color: C.accentDark,
    margin: "0 0 14px",
    fontWeight: 950,
  },

  subCard: {
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "15px 18px",
    marginBottom: 10,
  },

  subCardTitle: {
    fontSize: 15,
    color: C.text,
    marginBottom: 5,
    fontWeight: 950,
  },

  subCardSub: {
    fontSize: 12,
    color: C.muted,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  metaLine: {
    marginTop: 8,
    fontSize: 11,
    color: C.muted,
    lineHeight: 1.45,
    fontWeight: 800,
  },

  alertCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
  },

  alertBadgeColumn: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-end",
    flexShrink: 0,
  },

  alertStatusBadge: {
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
  },

  alertStatusBadgeOpen: {
    background: C.accentLight,
    color: C.accent,
  },

  alertStatusBadgeResolved: {
    background: C.successBg,
    color: C.successText,
  },

  alertSeverityBadge: {
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 950,
    textTransform: "uppercase",
    background: C.neutralBg,
    color: C.muted,
  },

  alertSeverityBadgeHigh: {
    background: C.accentLight,
    color: C.accent,
  },

  copyHint: {
    marginTop: 8,
    color: C.accentDark,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.45,
  },

  miniMeta: {
    marginTop: 8,
    fontSize: 11,
    color: C.muted,
    fontWeight: 800,
  },

  contactCardTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
  },

  copyBtn: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "8px 13px",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 950,
    whiteSpace: "nowrap",
    minWidth: 92,
  },

  verifiedBadge: {
    marginLeft: 8,
    padding: "3px 7px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 950,
  },

  inlineLink: {
    display: "inline-flex",
    marginTop: 10,
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    textDecoration: "none",
  },

  documentCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  documentCardTitleWrap: {
    minWidth: 0,
    flex: 1,
  },
  documentHistoryButton: {
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.accent,
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 950,
    cursor: "pointer",
    flexShrink: 0,
  },
  documentActionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    marginTop: 10,
  },

  inlineLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    padding: 0,
    background: "none",
    border: "none",
    color: C.accent,
    fontSize: 12,
    fontWeight: 950,
    cursor: "pointer",
    textDecoration: "none",
  },

  inlineLinkButtonDisabled: {
    opacity: 0.7,
    cursor: "wait",
  },

  highestBadge: {
    marginLeft: 8,
    padding: "3px 7px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 950,
  },

  relativeTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
  },

  futureBadge: {
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    padding: "0 9px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  relativeEduList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 10,
  },

  relativeEduChip: {
    padding: "5px 8px",
    borderRadius: 999,
    background: "#fff",
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
  },

  emptyText: {
    color: C.muted,
    fontSize: 13,
    fontStyle: "italic",
    textAlign: "center",
    padding: "28px 0",
  },

  remarkBox: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    overflow: "hidden",
    marginBottom: 18,
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
  },

  remarkHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    padding: "24px 28px 0",
  },

  remarkEyebrow: {
    fontSize: 10,
    letterSpacing: 2.5,
    textTransform: "uppercase",
    color: C.accent,
    margin: "0 0 4px",
    fontWeight: 950,
  },

  remarkTitle: { fontSize: 20, fontWeight: 950, color: C.text, margin: 0 },
  remarkSubhead: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.55,
    fontWeight: 700,
    maxWidth: 560,
  },

  remarkCount: {
    fontSize: 11,
    color: C.muted,
    background: C.accentLight,
    padding: "5px 10px",
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    fontWeight: 900,
  },

  noteStatGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    padding: "18px 28px 0",
  },

  noteMetricCard: {
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  noteMetricCardAlert: {
    background: "#fff1ef",
    border: `1px solid ${C.borderBold}`,
  },

  noteMetricLabel: {
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },

  noteMetricValue: {
    fontSize: 22,
    lineHeight: 1.1,
    color: C.text,
    fontWeight: 950,
  },

  noteMetricHint: {
    fontSize: 11,
    color: C.muted,
    fontWeight: 700,
    lineHeight: 1.45,
  },

  remarkInputWrap: { padding: "20px 28px", borderBottom: `1px solid ${C.border}` },

  recommendationSearchModeRow: {
    display: "flex",
    gap: 18,
    alignItems: "center",
    marginBottom: 14,
    flexWrap: "wrap",
  },

  recommendationRadioLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },

  recommendationSearchPanel: {
    marginTop: 12,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: "#fff",
    overflow: "hidden",
  },

  recommendationSearchHint: {
    padding: "14px 16px",
    color: C.muted,
    fontSize: 12,
    fontWeight: 700,
  },

  recommendationSearchError: {
    padding: "14px 16px",
    color: C.accent,
    fontSize: 12,
    fontWeight: 800,
    background: "#fff6f4",
  },

  recommendationSearchList: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    padding: 12,
    background: C.inputBg,
  },

  recommendationSearchItem: {
    textAlign: "left",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "13px 14px",
    cursor: "pointer",
    boxShadow: `0 10px 24px ${C.shadowLight}`,
  },

  recommendationSearchItemInner: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },

  recommendationSearchPhotoWrap: {
    width: 54,
    height: 54,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: 16,
  },

  recommendationSearchName: {
    color: C.text,
    fontSize: 13,
    fontWeight: 900,
  },

  recommendationAddPill: {
    marginLeft: "auto",
    flexShrink: 0,
    borderRadius: 999,
    padding: "7px 11px",
    background: C.redLight,
    color: C.accent,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },

  recommendationSearchMeta: {
    marginTop: 6,
    color: C.muted,
    fontSize: 11,
    lineHeight: 1.45,
    fontWeight: 700,
  },

  recommendationSelectedWrap: {
    marginTop: 14,
    border: `1px solid ${C.border}`,
    borderRadius: 22,
    background: `linear-gradient(135deg, ${C.inputBg}, rgba(255,255,255,0.96))`,
    padding: 14,
  },

  recommendationSelectedHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "center",
    marginBottom: 12,
  },

  recommendationSelectedTitle: {
    color: C.text,
    fontSize: 14,
    fontWeight: 950,
  },

  recommendationSelectedHint: {
    marginTop: 4,
    color: C.muted,
    fontSize: 11,
    fontWeight: 700,
  },

  recommendationSelectedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  recommendationSelectedCard: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 10,
    alignItems: "center",
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    background: "#fff",
    padding: 10,
    boxShadow: `0 10px 26px ${C.shadowLight}`,
  },

  recommendationSelectedMain: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    gap: 12,
    alignItems: "center",
    border: "none",
    background: "transparent",
    textAlign: "left",
    padding: 0,
  },

  recommendationSelectedControls: {
    alignSelf: "start",
  },

  recommendationSelectedRemove: {
    border: `1px solid ${C.border}`,
    borderRadius: 999,
    background: C.inputBg,
    color: C.muted,
    padding: "7px 10px",
    cursor: "pointer",
    fontSize: 10,
    fontWeight: 900,
  },

  recommendationSelectedNote: {
    gridColumn: "1 / -1",
    width: "100%",
    minHeight: 58,
    resize: "vertical",
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    background: C.inputBg,
    color: C.text,
    padding: "10px 12px",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 700,
    outline: "none",
    boxSizing: "border-box",
  },

  recommendationTargetCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    marginTop: 12,
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "14px 16px",
  },

  recommendationTargetMain: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    minWidth: 0,
  },

  recommendationTargetPhotoWrap: {
    width: 58,
    height: 58,
    flexShrink: 0,
    overflow: "hidden",
    borderRadius: 18,
  },

  recommendationTargetTitle: {
    color: C.text,
    fontSize: 14,
    fontWeight: 900,
  },

  recommendationTargetMeta: {
    marginTop: 6,
    color: C.muted,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },

  followUpContextCard: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    background: C.blueLight,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    padding: "14px 16px",
    marginBottom: 14,
  },

  followUpContextTitle: {
    color: C.text,
    fontSize: 12,
    fontWeight: 900,
  },

  followUpContextMeta: {
    marginTop: 4,
    color: C.muted,
    fontSize: 11,
    fontWeight: 700,
  },

  followUpContextBody: {
    marginTop: 8,
    color: C.text,
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },

  followUpContextActions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
    flexShrink: 0,
  },

  followUpResolveLabel: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    color: C.muted,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  noteComposerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 14,
  },

  noteField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  noteFieldLabel: {
    fontSize: 10,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: C.muted,
    fontWeight: 900,
  },

  noteSelect: {
    width: "100%",
    height: 42,
    borderRadius: 14,
    border: `1.5px solid ${C.border}`,
    padding: "0 12px",
    fontSize: 13,
    color: C.text,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },

  noteInput: {
    width: "100%",
    height: 42,
    borderRadius: 14,
    border: `1.5px solid ${C.border}`,
    padding: "0 12px",
    fontSize: 13,
    color: C.text,
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },

  remarkInput: {
    width: "100%",
    minHeight: 82,
    borderRadius: 16,
    border: `1.5px solid ${C.border}`,
    padding: "12px 14px",
    fontSize: 14,
    color: C.text,
    background: C.inputBg,
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
    lineHeight: 1.6,
  },

  remarkActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
    gap: 12,
  },

  remarkCharCount: { fontSize: 11, color: C.muted, fontWeight: 800 },

  noteActionRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },

  noteGhostBtn: {
    background: "#fff",
    color: C.muted,
    border: `1px solid ${C.border}`,
    padding: "10px 16px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },

  remarkBtn: {
    background: C.text,
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  remarkBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
  remarkList: { padding: "8px 0" },

  noteFiltersBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    padding: "18px 28px 6px",
  },

  noteFilterSelect: {
    minWidth: 160,
    height: 40,
    borderRadius: 999,
    border: `1px solid ${C.border}`,
    padding: "0 12px",
    fontSize: 12,
    color: C.text,
    background: "#fff",
    outline: "none",
  },
  notePagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "16px 28px 0",
    borderTop: `1px solid ${C.border}`,
    marginTop: 12,
    flexWrap: "wrap",
  },
  notePaginationInfo: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 700,
  },
  notePaginationControls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  notePaginationButton: {
    minWidth: 38,
    height: 36,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    padding: "0 12px",
  },
  notePaginationButtonActive: {
    background: C.accent,
    color: "#fff",
    borderColor: C.accent,
  },
  notePaginationButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },

  remarkItem: {
    display: "flex",
    gap: 14,
    padding: "16px 28px",
    borderBottom: `1px solid ${C.border}`,
  },

  remarkItemFirst: {},
  remarkItemLeft: { flexShrink: 0 },

  remarkAvatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    overflow: "hidden",
    background: C.accentLight,
    border: `1.5px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    color: C.accentDark,
    fontWeight: 950,
  },

  remarkItemRight: { flex: 1 },

  remarkItemTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },

  remarkAuthor: { fontSize: 13, fontWeight: 950, color: C.text },
  remarkTime: { fontSize: 11, color: C.muted, fontWeight: 800 },
  remarkText: { fontSize: 14, color: C.text, lineHeight: 1.6, margin: 0 },
  noteRecommendationContext: {
    margin: "10px 0 8px",
    padding: "9px 11px",
    borderRadius: 14,
    background: "linear-gradient(135deg, rgba(255,245,242,0.96), rgba(255,255,255,0.98))",
    border: `1px solid ${C.border}`,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  noteRecommendationBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  noteRecommendationText: {
    color: C.text,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  noteRecommendationMeta: {
    color: C.muted,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.4,
  },

  noteCardActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 10,
  },

  recommendationCardMeta: {
    marginTop: 8,
    color: C.muted,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5,
  },

  recommendationTimeline: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  recommendationTimelineItem: {
    background: C.accentLight,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "11px 12px",
  },

  recommendationTimelineTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
  },

  recommendationTimelineBody: {
    marginTop: 8,
    color: C.text,
    fontSize: 13,
    lineHeight: 1.55,
    fontWeight: 700,
  },

  recommendationDownloadRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
  },

  recommendationOutcomeComposer: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: `1px solid ${C.border}`,
  },

  recommendationOutcomeActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 10,
  },

  noteFollowUpBtn: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    color: C.accent,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 900,
    cursor: "pointer",
  },

  noteSecondaryBtn: {
    background: C.neutralBg,
    border: `1px solid ${C.border}`,
    color: C.text,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 11,
    fontWeight: 850,
    cursor: "pointer",
  },

  noteInlineActionGroup: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
  },

  noteInlineInput: {
    height: 36,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    padding: "0 10px",
    fontSize: 12,
    color: C.text,
    background: "#fff",
    outline: "none",
  },

  noteMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
  },

  noteTypeBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.accentLight,
    color: C.accentDark,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  noteOutcomeBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.neutralBg,
    color: C.muted,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  noteStatusBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.text,
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
  },

  noteStatusBadgeCompleted: {
    background: C.successBg,
    color: C.successText,
  },

  noteStatusBadgeCancelled: {
    background: C.neutralBg,
    color: C.muted,
  },

  noteFollowUpBadge: {
    padding: "4px 8px",
    borderRadius: 999,
    background: C.blueLight,
    color: C.text,
    fontSize: 10,
    fontWeight: 900,
  },

  noteFollowUpBadgeDue: {
    background: "#fff1ef",
    color: C.accent,
  },

  remarkEmpty: { padding: "40px 28px", textAlign: "center" },
  remarkEmptyIcon: { fontSize: 28, display: "block", marginBottom: 10, opacity: 0.55 },
  remarkEmptyText: { fontSize: 13, color: C.muted, fontStyle: "italic", margin: 0 },

  suggestionBox: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    boxShadow: `0 18px 44px ${C.shadowStrong}`,
    zIndex: 50,
    maxHeight: 240,
    overflowY: "auto",
  },

  suggestionHeader: {
    padding: "9px 14px 7px",
    fontSize: 10,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: C.muted,
    borderBottom: `1px solid ${C.border}`,
    fontWeight: 950,
  },

  suggestionItem: {
    padding: "10px 14px",
    fontSize: 13,
    color: C.text,
    cursor: "pointer",
    borderBottom: `1px solid ${C.soft}`,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  suggestionItemHovered: { background: C.accentLight, color: C.accentDark },
  suggestionDot: { color: C.accent, fontSize: 16, lineHeight: 1 },

  gallery: {
    background: "rgba(255,255,255,0.92)",
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: "24px 28px",
    boxShadow: `0 18px 54px ${C.shadowSoft}`,
    marginBottom: 24,
  },

  galleryHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },

  galleryTitle: {
    fontSize: 12,
    letterSpacing: 1.8,
    textTransform: "uppercase",
    color: C.accentDark,
    margin: 0,
    fontWeight: 950,
  },

  galleryAddBtn: {
    background: "#fff",
    border: `1px dashed ${C.accent}`,
    color: C.accent,
    borderRadius: 999,
    padding: "7px 14px",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
  },

  galleryHelp: { fontSize: 12, color: C.muted, marginBottom: 14, fontWeight: 700 },
  galleryGrid: { display: "flex", flexWrap: "wrap", gap: 12 },
  galleryGridInTab: { display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 4 },

  galleryItem: {
    position: "relative",
    borderRadius: 18,
    overflow: "hidden",
    border: `2px solid ${C.border}`,
    transition: "border-color .15s, transform .15s, box-shadow .15s, opacity .15s",
    background: "transparent",
    padding: 0,
  },

  galleryItemClickable: { cursor: "pointer" },
  galleryItemLoading: { opacity: 0.7 },
  galleryItemPrimary: { border: `2px solid ${C.accent}` },

  galleryImg: { width: 146, height: 146, objectFit: "cover", display: "block" },

  galleryImgFallback: {
    width: 146,
    height: 146,
    background: C.accentLight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    color: C.muted,
  },

  galleryBadge: {
    position: "absolute",
    bottom: 8,
    left: 8,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  },

  gallerySetBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    background: "rgba(17,24,39,0.82)",
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  },

  galleryUpdatingBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "4px 8px",
    borderRadius: 999,
    textTransform: "uppercase",
  },
  photoPreviewBackdrop: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(10, 12, 18, 0.82)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1600,
    backdropFilter: "blur(10px)",
    animation: "fadeIn 0.2s ease both",
    padding: 16,
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
  },
  photoPreviewModal: {
    background: "rgba(255,255,255,0.98)",
    borderRadius: 28,
    width: "calc(100vw - 32px)",
    maxWidth: 1480,
    height: "calc(100vh - 32px)",
    maxHeight: 980,
    overflow: "hidden",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
    margin: "auto",
    boxSizing: "border-box",
  },
  photoPreviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "24px 28px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  photoPreviewToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  photoPreviewTitle: {
    fontSize: 24,
    fontWeight: 950,
    color: C.text,
    margin: 0,
  },
  photoPreviewBody: {
    padding: 16,
    background: "rgba(37,41,53,0.04)",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "stretch",
    minHeight: 0,
    flex: 1,
  },
  photoPreviewViewport: {
    flex: 1,
    overflow: "auto",
    borderRadius: 20,
    background: "rgba(255,255,255,0.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  photoPreviewImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    objectFit: "contain",
    borderRadius: 20,
    display: "block",
    boxShadow: "0 18px 44px rgba(37,41,53,0.16)",
    transformOrigin: "center center",
    transition: "transform 0.18s ease",
  },
  photoPreviewFallback: {
    width: "100%",
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 20,
    background: C.soft,
    color: C.muted,
    fontWeight: 800,
    fontSize: 15,
  },
  photoPreviewFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    padding: "18px 28px 24px",
    borderTop: `1px solid ${C.border}`,
  },
  photoPreviewMeta: {
    fontSize: 13,
    color: C.muted,
    fontWeight: 800,
  },
  photoPreviewActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  photoZoomButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1,
  },
  photoZoomResetButton: {
    height: 42,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 900,
    padding: "0 14px",
  },
  photoZoomButtonDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  photoPreviewCloseButton: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.muted,
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1,
  },

  noPhotoNudge: {
    background: "rgba(255,255,255,0.84)",
    border: `1px dashed ${C.border}`,
    borderRadius: 28,
    padding: 40,
    textAlign: "center",
    boxShadow: `0 14px 38px ${C.shadowLight}`,
  },

  noPhotoIcon: { fontSize: 34, color: C.accent, display: "block", marginBottom: 10 },
  noPhotoText: { color: C.muted, fontSize: 13, margin: "0 0 16px", fontWeight: 800 },

  noPhotoBtn: {
    background: C.accent,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1400,
    backdropFilter: "blur(7px)",
    animation: "fadeIn 0.2s ease both",
    padding: 20,
    overscrollBehavior: "contain",
  },

  overlayCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },

  overlayHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "28px 32px 20px",
    borderBottom: `1px solid ${C.border}`,
  },

  overlayEyebrow: {
    fontSize: 10,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: C.accent,
    margin: "0 0 4px",
    fontWeight: 950,
  },

  overlayTitle: { fontSize: 24, fontWeight: 950, color: C.text, margin: 0 },

  overlayClose: {
    background: "none",
    border: "none",
    fontSize: 30,
    color: C.muted,
    cursor: "pointer",
    lineHeight: 1,
    padding: 0,
  },

  dropZone: {
    margin: "20px 32px 0",
    border: `2px dashed ${C.border}`,
    borderRadius: 20,
    padding: "26px 0",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    background: C.accentLight,
  },

  dropIcon: { fontSize: 30, color: C.accent, fontWeight: 950 },
  dropText: { fontSize: 14, color: C.accent, fontWeight: 950 },
  dropSub: { fontSize: 11, color: C.muted, fontWeight: 700 },
  processingDropZone: {
    opacity: 0.78,
    cursor: "progress",
  },
  processingInlineNote: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    marginTop: 4,
    fontSize: 12,
    color: C.accent,
    fontWeight: 900,
  },
  processingSpinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: `2px solid ${C.border}`,
    borderTopColor: C.accent,
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },

  overlayHint: {
    fontSize: 12,
    color: C.muted,
    margin: "14px 32px 10px",
    textAlign: "center",
    fontWeight: 800,
  },

  previewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(124px, 1fr))",
    gap: 10,
    padding: "0 32px 20px",
  },

  previewCard: {
    position: "relative",
    borderRadius: 16,
    overflow: "hidden",
    border: `2px solid ${C.border}`,
    cursor: "pointer",
    transition: "border-color .15s",
    background: C.soft,
  },

  previewCardPrimary: { border: `2px solid ${C.accent}`, boxShadow: `0 0 0 3px ${C.accentLight}` },
  previewImg: { width: "100%", height: 116, objectFit: "cover", display: "block" },

  primaryBadge: {
    position: "absolute",
    top: 7,
    left: 7,
    background: C.accent,
    color: "#fff",
    fontSize: 10,
    fontWeight: 950,
    padding: "3px 7px",
    borderRadius: 999,
  },

  previewRemove: {
    position: "absolute",
    top: 7,
    right: 7,
    background: "rgba(37,41,53,0.7)",
    border: "none",
    color: "#fff",
    borderRadius: "50%",
    width: 23,
    height: 23,
    fontSize: 16,
    lineHeight: "23px",
    textAlign: "center",
    cursor: "pointer",
    padding: 0,
  },

  previewName: {
    fontSize: 10,
    color: C.muted,
    padding: "5px 7px",
    background: C.accentLight,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 700,
  },

  overlayFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 32px 24px",
    borderTop: `1px solid ${C.border}`,
    marginTop: "auto",
  },

  uploadStatus: { fontSize: 12, color: C.accent, fontWeight: 950 },
  uploadHint: { fontSize: 12, color: C.muted, fontWeight: 800 },

  overlayCancel: {
    background: "none",
    border: `1px solid ${C.border}`,
    color: C.muted,
    borderRadius: 999,
    padding: "10px 18px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 900,
  },

  overlaySubmit: {
    background: C.text,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "10px 22px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 950,
  },

  watermarkModalBackdrop: {
    ...{
      position: "fixed",
      inset: 0,
      background: "rgba(37,41,53,0.62)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(7px)",
      animation: "fadeIn 0.2s ease both",
      padding: 20,
    },
  },
  biodataModalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1400,
    backdropFilter: "blur(7px)",
    animation: "fadeIn 0.2s ease both",
    padding: 20,
    overscrollBehavior: "contain",
  },
  biodataModalCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },
  documentHistoryModalCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    maxHeight: "90vh",
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  biodataModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    padding: "28px 32px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  biodataOptionList: {
    display: "grid",
    gap: 10,
    padding: "20px 32px",
  },
  biodataOptionRowDisabled: {
    opacity: 0.5,
  },
  biodataOptionError: {
    padding: "12px 14px",
    borderRadius: 14,
    background: "#fff3f0",
    border: "1px solid rgba(210,58,43,0.22)",
    color: C.accent,
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 800,
  },
  documentHistoryBody: {
    display: "grid",
    gap: 14,
    padding: "20px 32px",
    overflowY: "auto",
  },
  documentHistoryMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
  },
  documentHistoryList: {
    display: "grid",
    gap: 12,
  },
  documentHistoryItem: {
    display: "grid",
    gap: 6,
    padding: "14px 16px",
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    background: C.soft,
  },
  documentHistoryItemTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  documentHistoryActor: {
    fontSize: 14,
    color: C.text,
    fontWeight: 950,
  },
  documentHistoryActionBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "5px 10px",
    background: C.accentLight,
    color: C.accent,
    fontSize: 11,
    fontWeight: 950,
  },
  documentHistoryItemMeta: {
    fontSize: 12,
    color: C.muted,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  watermarkModalCard: {
    background: C.card,
    borderRadius: 28,
    width: "100%",
    maxWidth: 620,
    boxShadow: "0 28px 90px rgba(37,41,53,0.28)",
    animation: "slideUp 0.25s ease both",
    display: "flex",
    flexDirection: "column",
  },
  watermarkModalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 14,
    padding: "28px 32px 18px",
    borderBottom: `1px solid ${C.border}`,
  },
  watermarkModalText: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  watermarkOptionList: {
    display: "grid",
    gap: 10,
    padding: "20px 32px",
  },
  watermarkOptionRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 14px",
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.text,
    fontSize: 14,
    fontWeight: 800,
  },
  watermarkEmptyState: {
    padding: "14px 16px",
    borderRadius: 16,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  watermarkModalFooter: {
    display: "grid",
    gap: 12,
    padding: "0 32px 24px",
  },
  watermarkFileHint: {
    fontSize: 12,
    color: C.muted,
    fontWeight: 800,
    wordBreak: "break-word",
  },
  watermarkActionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  overlaySubmitDisabled: { opacity: 0.45, cursor: "not-allowed" },
};
